<script setup lang="ts">
    import { ref, onMounted, computed, watch } from 'vue'
    import { Button } from '@/components/ui/button'
    import { Input } from '@/components/ui/input'
    import { Combobox } from '@/components/ui/combobox'
    import { MultiSelect } from '@/components/ui/multi-select'
    import AsyncMultiSelect from '@/components/ui/multi-select/AsyncMultiSelect.vue'
    import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
    import { Label } from '@/components/ui/label'
    import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
    import axios from 'axios'
    import { useToast } from '@/components/ui/toast'
    import { useI18n } from 'vue-i18n'
    import { useViewStore } from '@/stores/viewStore'
    import { ArrowLeft, Info, Upload, Loader2, Search, FileSpreadsheet } from 'lucide-vue-next'
    import DangeroursMaterialsLogo from '@/components/images/biohazard.svg'
    import { get_data_attr } from '@/utils/globals'
    import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
    import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
    
    const { t } = useI18n()
    const { toast } = useToast()
    const viewStore = useViewStore()
    
    const userInfo = get_data_attr("user")
    const isHoeppner = userInfo?.is_hoeppner || false
    
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    if (token) axios.defaults.headers.common["X-CSRF-Token"] = token
    
    const props = defineProps<{
      id?: number
    }>()
    
    const isLoading = ref(false)
    const isSaving = ref(false)
    const isUploadingSDB = ref(false)
    const showSDBDialog = ref(false)
    const sdbFile = ref<File | null>(null)
    /** Error message from last SDB upload (shown in dialog) */
    const sdbUploadError = ref('')
    /** 'add_only' = nur leere Felder füllen; 'overwrite' = alle Felder überschreiben */
    const sdbMergeMode = ref<'add_only' | 'overwrite'>('add_only')
    const showSearchDialog = ref(false)
    const showImportCsvDialog = ref(false)
    const csvImportFile = ref<File | null>(null)
    const csvImportError = ref('')
    const isImportingCsv = ref(false)
    const csvImportSuccess = ref<{ created: number; updated: number; errors?: string[] } | null>(null)
    const showImportSuccessDialog = ref(false)
    const searchQuery = ref('')
    const searchResults = ref<Array<{ id: number; name: string; cas_numbers: Array<{ id: number; cas_number: string; name?: string }> }>>([])
    const selectedSearchId = ref<number | null>(null)
    const isSearching = ref(false)
    let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null
    const gefahrstoffNames = ref<string[]>([])
    const hPhraseOptions = ref<Array<{ id: number; label: string; value: string }>>([])
    const eurhPhraseOptions = ref<Array<{ id: number; label: string; value: string }>>([])
    const pPhraseOptions = ref<Array<{ id: number; label: string; value: string }>>([])
    const casNumberOptions = ref<Array<{ id: number; label: string; value: string }>>([])
    const selectedCasNumberOptions = ref<Array<{ id: number; label: string; value: string }>>([])
    
    // Field options for autocomplete
    const workspaceOptions = ref<string[]>([])
    const inventorySpaceTypeOptions = ref<string[]>([])
    const useOptions = ref<string[]>([])
    const inventorySpaceOptions = ref<string[]>([])
    const inventorySpaceAreaOptions = ref<string[]>([])
    const locationOptions = ref<string[]>([])
    const nameResponsibleOptions = ref<string[]>([])
    const emailResponsibleOptions = ref<string[]>([])
    const emailDepartmentOptions = ref<string[]>([])
    
    // Form data
    const formData = ref({
      name: '',
      substitutiontest: {
        next_check: '',
        name_responsible: '',
        email_responsible: '',
        email_department: '',
        version_date: '',
        result: '',
      },
      h_phrase_ids: [] as number[],
      eurh_phrase_ids: [] as number[],
      p_phrase_ids: [] as number[],
      cas_number_ids: [] as number[],
      // Boolean fields
      hazardous_material: true,
      gas: false,
      spray: false,
      carcinogenic_material: false,
      // Additional fields
      un_number: '',
      eak_number: '',
      flammpunkt: '',
      water_hazard_class: '',
      inventory_class: '',
      use: '',
      number_of_workers: null as number | null,
      workspace: '',
      yearly_usage: '',
      yearly_usage_unit: 'kg',
      inventory_size: '',
      inventory_size_unit: 'kg',
      inventory_space_name: '' as string,
      inventory_space_area_name: '' as string,
      location: '',
      inventory_space_type: '',
      comment: '',
      active: true,
      use_start: '',
      use_end: '',
      use_duration_periodicity: '',
      use_location_or_machinery: '',
      extinguishing_agent_suitable: '',
      extinguishing_agent_unsuitable: '',
      inventory_conditions: '',
      gebinde_number: '',
      physical_state_at_room_temperature: '',
      form: '',
      color: '',
      odor: '',
      solubility_in_water: '',
      melting_point: '',
      boiling_point: '',
      ignition_temperature: '',
      autoignition_temperature: '',
      decomposition_point: '',
      density: '',
      viscosity: '',
      vapor_pressure: '',
      relative_vapor_density: '',
     ph_value: '',
     lower_explosion_limit_vol_percent: '',
     upper_explosion_limit_vol_percent: '',
     reactivity: '',
     stability: '',
      datasheet: {
        next_check: '',
        name_responsible: '',
        email_responsible: '',
        email_department: '',
        manufacturer: '',
        version_date: '',
        last_check: new Date().toISOString().split('T')[0], // Default to today's date
        last_check_hmc: '',
        last_check_hmc_comment: '',
        datasheet_file: null as File | null,
        existing_datasheet_url: null as string | null
      },
      gbu: {
        next_check: '',
        name_responsible: '',
        email_responsible: '',
        email_department: '',
        version_date: '',
        identifier: '',
        comment: '',
        gbu_file: null as File | null,
        existing_gbu_url: null as string | null
      },
      betriebsanweisung: {
        next_check: '',
        name_responsible: '',
        email_responsible: '',
        email_department: '',
        version_date: '',
        identifier: '',
        comment: '',
        betriebsanweisung_file: null as File | null,
        existing_betriebsanweisung_url: null as string | null
      }
    })
    
    // Water hazard class options
    const waterHazardClassOptions = [
      'WGK 0',
      'WGK 1',
      'WGK 2',
      'WGK 3',
      'Klasse A',
      'Klasse B',
    ]
    
    // Unit options
    const yearlyUsageUnitOptions = ['m³', 'l', 'g', 'mg', 'kg', 't']
    const inventorySizeUnitOptions = ['m³', 'l', 'g', 'mg', 'kg', 't']
    
    // UN number options
    const unNumberOptions = [
      '0004 - Ammoniumpikrat, trocken oder angefeuchtet mit weniger als 10 Masse-% Wasser',
      '0005 - Patronen für Waffen, mit Sprengladung',
      '0006 - Patronen für Waffen, mit Sprengladung',
      '0007 - Patronen für Waffen, mit Sprengladung',
      '0009 - Munition, Brand, mit oder ohne Zerleger, Ausstoß- oder Treibladung',
      '0010 - Munition, Brand, mit oder ohne Zerleger, Ausstoß- oder Treibladung',
      '0012 - Patronen für Waffen mit inertem Geschoss oder Patronen für Handfeuerwaffen',
      '0014 - Patronen für Waffen, Manöver oder Patronen für Handfeuerwaffen, Manöver oder Patronen für Werkzeuge, ohne Geschoss',
      '0015 - Munition, Nebel, mit oder ohne Zerleger, Ausstoß- oder Treibladung',
      '0016 - Munition, Nebel, mit oder ohne Zerleger, Ausstoß- oder Treibladung',
      '0018 - Munition, Augenreizstoff, mit Zerleger, Ausstoß- oder Treibladung',
      '0019 - Munition, Augenreizstoff, mit Zerleger, Ausstoß- oder Treibladung',
      '0020 - Munition, giftig, mit Zerleger, Ausstoß- oder Treibladung',
      '0021 - Munition, giftig, mit Zerleger, Ausstoß- oder Treibladung',
      '0027 - Schwarzpulver, gekörnt oder in Mehlform',
      '0028 - Schwarzpulver, gepresst oder als Pellets',
      '0029 - Sprengkapseln, nicht elektrisch',
      '0030 - Sprengkapseln, elektrisch',
      '0033 - Bomben, mit Sprengladung',
      '0034 - Bomben, mit Sprengladung',
      '0035 - Bomben, mit Sprengladung',
      '0037 - Bomben, Blitzlicht',
      '0038 - Bomben, Blitzlicht',
      '0039 - Bomben, Blitzlicht',
      '0042 - Zündverstärker, ohne Detonator',
      '0043 - Zerleger, mit Explosivstoff',
      '0044 - Anzündhütchen',
      '0048 - Sprengkörper',
      '0049 - Patronen, Blitzlicht',
      '0050 - Patronen, Blitzlicht',
      '0054 - Patronen, Signal',
      '0055 - Treibladungshülsen, leer, mit Treibladungsanzünder336',
      '0056 - Wasserbomben',
      '0059 - Hohlladungen, ohne Zündmittel',
      '0060 - Füllsprengkörper',
      '0065 - Sprengschnur, biegsam',
      '0066 - Anzündlitze',
      '0070 - Schneidvorrichtungen, Kabel, mit Explosivstoff',
      '0072 - Cyclotrimethylentrinitramin, Cyclonit, Hexogen, RDX, angefeuchtet mit mindestens 15 Masse-% Wasser',
      '0073 - Detonatoren für Munition',
      '0074 - Diazodinitrophenol, angefeuchtet mit mindestens 40 Masse-% Wasser oder einer Alkohol-/Wasser-Mischung',
      '0075 - Diethylenglycoldinitrat, desensibilisiert, mit mindestens 25 Masse-% nicht flüchtigem, wasserunlöslichem Phlegmatisierungsmittel',
      '0076 - Dinitrophenol, trocken oder angefeuchtet mit weniger als 15 Masse-% Wasser',
      '0077 - Dinitrophenolate der Alkalimetalle, trocken oder angefeuchtet mit weniger als 15 Masse-% Wasser',
      '0078 - Dinitroresorcinol, trocken oder angefeuchtet mit weniger als 15 Masse-% Wasser',
      '0079 - Hexanitrodiphenylamin (Dipikrylamin), (Hexyl)',
      '0081 - Sprengstoff, Typ A',
      '0082 - Sprengstoff, Typ B',
      '0083 - Sprengstoff, Typ C',
      '0084 - Sprengstoff, Typ D',
      '0092 - Leuchtkörper, Boden',
      '0093 - Leuchtkörper, Luftfahrzeug',
      '0094 - Blitzlichtpulver',
      '0099 - Lockerungssprenggeräte mit Explosivstoff, für Erdölbohrungen, ohne Zündmittel',
      '0101 - Stoppinen, nicht sprengkräftig',
      '0102 - Sprengschnur, mit Metallmantel',
      '0103 - Anzündschnur, rohrförmig, mit Metallmantel',
      '0104 - Sprengschnur mit geringer Wirkung, mit Metallmantel',
      '0105 - Anzündschnur (Sicherheitszündschnur)',
      '0106 - Zünder, sprengkräftig',
      '0107 - Zünder, sprengkräftig',
      '0110 - Granaten, Übung, Hand oder Gewehr',
      '0113 - Guanylnitrosaminoguanylidenhydrazin, angefeuchtet mit mindestens 30 Masse-% Wasser',
      '0114 - Guanylnitrosaminoguanyltetrazen (Tetrazene), angefeuchtet mit mindestens 30 Masse-% Wasser oder einer Alkohol/Wasser-Mischung',
      '0118 - Hexolit (Hexotol) trocken oder angefeuchtet mit weniger als 15 Masse-% Wasser',
      '0121 - Anzünder',
      '0124 - Perforationshohlladungsträger, geladen, für Erdölbohrlöcher, ohne Zündmittel',
      '0129 - Bleiazid, angefeuchtet, mit mindestens 20 Masse-% Wasser oder einer Alkohol/Wasser-Mischung',
      '0130 - Bleistyphnat (Bleitrinitroresorcinat) angefeuchtet mit mindestens 20 Masse-% Wasser oder einer Alkohol/Wasser-Mischung',
      '0131 - Anzünder, Anzündschnur',
      '0132 - Deflagrierende Metallsalze aromatischer Nitroverbindungen',
      '0133 - Mannitolhexanitrat (Nitromannitol), angefeuchtet mit mindestens 40 Masse-% Wasser oder einer Alkohol/Wasser-Mischung',
      '0135 - Quecksilberfulminat, angefeuchtet mit mindestens 20 Masse-% Wasser oder einer Alkohol/Wasser-Mischung',
      '0136 - Minen, mit Sprengladung',
      '0137 - Minen, mit Sprengladung',
      '0138 - Minen, mit Sprengladung',
      '0143 - Nitroglycerol (Nitroglycerin), desensibilisiert mit mindestens 40 Masse-% nicht flüchtigem, wasserunlöslichem 336Phlegmatisierungsmittel',
      '0144 - Nitroglycerol (Nitroglycerin) in alkoholischer Lösung, mit mehr als 1 %, aber nicht mehr als 10 % Nitroglycerol',
      '0146 - Nitrostärke, trocken oder angefeuchtet mit weniger als 20 Masse-% Wasser',
      '0147 - Nitroharnstoff',
      '0150 - Nitropenta (PETN), angefeuchtet mit mindestens 25 Masse-% Wasser oder desensibilisiert mit mindestens 15 Masse-% Phlegmatisierungsmittel336',
      '0151 - Pentolit, trocken oder angefeuchtet mit weniger als 15 Masse-% Wasser',
      '0153 - Trinitroanilin (Pikramid)',
      '0154 - Trinitrophenol (Pikrinsäure), angefeuchtet mit weniger als 30 Masse-% Wasser',
      '0155 - Trinitrochlorbenzen (Pikrylchlorid)',
      '0159 - Pulverrohmasse, angefeuchtet, mit mindestens 25 Masse-% Wasser',
      '0160 - Treibladungspulver',
      '0161 - Treibladungspulver',
      '0167 - Geschosse, mit Sprengladung',
      '0168 - Geschosse, mit Sprengladung',
      '0169 - Geschosse, mit Sprengladung',
      '0171 - Munition, Leucht, mit oder ohne Zerleger, Ausstoß- oder Treibladung',
      '0173 - Auslösevorrichtungen mit Explosivstoff',
      '0174 - Sprengnieten',
      '0180 - Raketen, mit Sprengladung',
      '0181 - Raketen, mit Sprengladung',
      '0182 - Raketen, mit Sprengladung',
      '0183 - Raketen, mit inertem Kopf',
      '0186 - Raketenmotoren',
      '0190 - Explosivstoff, Muster, außer Initialsprengstoff',
      '0191 - Signalkörper, Hand',
      '0192 - Knallkapseln, Eisenbahn',
      '0193 - Knallkapseln, Eisenbahn',
      '0194 - Signalkörper, Seenot',
      '0195 - Signalkörper, Seenot',
      '0196 - Signalkörper, Rauch',
      '0197 - Signalkörper, Rauch',
      '0204 - Falllote mit Explosivstoff',
      '0207 - Tetranitroanilin',
      '0208 - Trinitrophenylmethylnitramin (Tetryl)',
      '0209 - Trinitrotoluol (TNT), trocken oder angefeuchtet mit weniger als 30 Masse-% Wasser',
      '0212 - Leuchtspurkörper für Munition',
      '0213 - Trinitroanisol',
      '0214 - Trinitrobenzen, trocken oder angefeuchtet mit weniger als 30 Masse-% Wasser',
      '0215 - Trinitrobenzoesäure, trocken oder angefeuchtet mit weniger als 30 Masse-% Wasser',
      '0216 - Trinitrometakresol',
      '0217 - Trinitronaphthalin',
      '0218 - Trinitrophenethol',
      '0219 - Trinitroresorcinol (Styphninsäure), trocken oder angefeuchtet mit mindestens 20 Masse-% Wasser oder einer Alkohol-/Wasser-Mischung',
      '0220 - Harnstoffnitrat, trocken oder angefeuchtet mit weniger als 20 Masse-% Wasser',
      '0221 - Gefechtsköpfe, Torpedo, mit Sprengladung',
      '0222 - Ammoniumnitrat, mit mehr als 0,2 % brennbaren Stoffen, einschließlich jedes als Kohlenstoff berechneten organischen Stoffes, unter Ausschluss jedes anderen zugesetzten Stoffes',
      '0223 - Ammoniumnitrat-Düngemittel mit einer größeren Sensibilität als Ammoniumnitrat mit 0,2 % brennbaren Stoffen, einschließlich jedes als Kohlenstoff berechneten organischen Stoffes, unter Ausschluss jedes anderen zugesetzten Stoffes',
      '0224 - Bariumazid, trocken oder angefeuchtet mit weniger als 50 Masse-% Wasser',
      '0225 - Zündverstärker, mit Detonator',
      '0226 - Oktogen (HMX), angefeuchtet mit mindestens 15 Masse-% Wasser',
      '0234 - Natriumdinitroorthokresolat, trocken oder angefeuchtet mit weniger als 15 Masse-% Wasser',
      '0235 - Natriumpikramat, trocken oder angefeuchtet mit weniger als 20 Masse-% Wasser',
      '0236 - Zirconiumpikramat, trocken oder angefeuchtet mit weniger als 20 Masse-% Wasser',
      '0237 - Schneidladung, biegsam, gestreckt',
      '0238 - Raketen, Leinenwurf',
      '0240 - Raketen, Leinenwurf',
      '0241 - Sprengstoff, Typ E',
      '0242 - Treibladungen für Geschütze',
      '0243 - Munition, Brand, weißer Phosphor, mit Zerleger, Ausstoß- oder Treibladung',
      '0244 - Munition, Brand, weißer Phosphor, mit Zerleger, Ausstoß- oder Treibladung',
      '0245 - Munition, Nebel, weißer Phosphor, mit Zerleger, Ausstoß- oder Treibladung',
      '0246 - Munition, Nebel, weißer Phosphor, mit Zerleger, Ausstoß- oder Treibladung',
      '0247 - Munition, Brand, mit flüssigem oder geliertem Brandstoff, mit Zerleger, Ausstoß- oder Treibladung',
      '0248 - Vorrichtungen, durch Wasser aktivierbar, mit Zerleger, Ausstoß- oder Treibladung',
      '0249 - Vorrichtungen, durch Wasser aktivierbar, mit Zerleger, Ausstoß- oder Treibladung',
      '0250 - Raketentriebwerke mit Hypergolen, mit oder ohne Ausstoßladung',
      '0254 - Munition, Leucht, mit oder ohne Zerleger, Ausstoß- oder Treibladung',
      '0255 - Sprengkapseln, elektrisch',
      '0257 - Zünder, sprengkräftig',
      '0266 - Oktolit (Octol), trocken oder angefeuchtet mit weniger als 15 Masse-% Wasser',
      '0267 - Sprengkapseln, nicht elektrisch',
      '0268 - Zündverstärker, mit Detonator',
      '0271 - Treibladungen für Geschütze',
      '0272 - Treibladungen für Geschütze',
      '0275 - Kartusche für technische Zwecke',
      '0276 - Kartusche für technische Zwecke',
      '0277 - Kartuschen, Erdölbohrloch',
      '0278 - Kartuschen, Erdölbohrloch',
      '0279 - Treibladungen für Geschütze',
      '0280 - Raketenmotoren',
      '0281 - Raketenmotoren',
      '0282 - Nitroguanidin (Picrit), trocken oder angefeuchtet mit weniger als 20 Masse-% Wasser',
      '0283 - Zündverstärker, ohne Detonator',
      '0284 - Granaten, Hand oder Gewehr, mit Sprengladung',
      '0285 - Granaten, Hand oder Gewehr, mit Sprengladung',
      '0286 - Gefechtsköpfe, Rakete, mit Sprengladung',
      '0287 - Gefechtsköpfe, Rakete, mit Sprengladung',
      '0288 - Schneidladung, biegsam, gestreckt',
      '0289 - Sprengschnur, biegsam',
      '0290 - Sprengschnur, mit Metallmantel',
      '0291 - Bomben, mit Sprengladung',
      '0292 - Granaten, Hand oder Gewehr, mit Sprengladung',
      '0293 - Granaten, Hand oder Gewehr, mit Sprengladung',
      '0294 - Minen, mit Sprengladung',
      '0295 - Raketen, mit Sprengladung',
      '0296 - Falllote, mit Explosivstoff',
      '0297 - Munition, Leucht, mit oder ohne Zerleger, Ausstoß- oder Treibladung',
      '0299 - Bomben, Blitzlicht',
      '0300 - Munition, Brand, mit oder ohne Zerleger, Ausstoß- oder Treibladung',
      '0301 - Munition, Augenreizstoff, mit Zerleger, Ausstoß- oder Treibladung',
      '0303 - Munition, Nebel, mit oder ohne Zerleger, Ausstoß- oder Treibladung oder Munition, Nebel, mit oder ohne Zerleger, Ausstoß- oder Treibladung, mit ätzenden Stoffen',
      '0305 - Blitzlichtpulver',
      '0306 - Leuchtspurkörper für Munition',
      '0312 - Patronen, Signal',
      '0313 - Signalkörper, Rauch',
      '0314 - Anzünder',
      '0315 - Anzünder',
      '0316 - Zünder, nicht sprengkräftig',
      '0317 - Zünder, nicht sprengkräftig',
      '0318 - Granaten, Übung, Hand oder Gewehr',
      '0319 - Treibladungsanzünder',
      '0320 - Treibladungsanzünder',
      '0321 - Patronen für Waffen, mit Sprengladung',
      '0322 - Raketentriebwerke, mit Hypergolen, mit oder ohne Ausstoßladung',
      '0323 - Kartuschen für technische Zwecke',
      '0324 - Geschosse, mit Sprengladung',
      '0325 - Anzünder',
      '0326 - Patronen für Waffen, Manöver oder Patronen für Handfeuerwaffen, Manöver oder Patronen für Werkzeuge, ohne Geschoss',
      '0327 - Patronen für Waffen, Manöver oder Patronen für Handfeuerwaffen, Manöver oder Patronen für Werkzeuge, ohne Geschoss',
      '0328 - Patronen für Waffen, mit inertem Geschoss oder Patronen für Handfeuerwaffen',
      '0329 - Torpedos, mit Sprengladung',
      '0330 - Torpedos, mit Sprengladung',
      '0331 - Sprengstoff Typ B',
      '0332 - Sprengstoff Typ E',
      '0333 - Feuerwerkskörper',
      '0334 - Feuerwerkskörper',
      '0335 - Feuerwerkskörper',
      '0336 - Feuerwerkskörper',
      '0337 - Feuerwerkskörper',
      '0338 - Patronen für Waffen, Manöver oder Patronen für Handfeuerwaffen, Manöver oder Patronen für Werkzeuge, ohne Geschoss',
      '0339 - Patronen für Waffen mit inertem Geschoss oder Patronen für Handfeuerwaffen',
      '0340 - Nitrocellulose, trocken oder angefeuchtet mit weniger als 25 Masse-% Wasser (oder Alkohol)',
      '0341 - Nitrocellulose, nicht behandelt oder plastifiziert mit weniger als 18 Masse-% Plastifizierungsmittel',
      '0342 - Nitrocellulose, angefeuchtet mit mindestens 25 Masse-% Alkohol',
      '0343 - Nitrocellulose, plastifiziert, mit mindestens 18 Masse-% Plastifizierungsmittel',
      '0344 - Geschosse, mit Sprengladung',
      '0345 - Geschosse, inert, mit Leuchtspurmitteln',
      '0346 - Geschosse, mit Zerleger oder Ausstoßladung',
      '0347 - Geschosse, mit Zerleger oder Ausstoßladung',
      '0348 - Patronen für Waffen, mit Sprengladung',
      '0349 - Gegenstände mit Explosivstoff, N.A.G.',
      '0350 - Gegenstände mit Explosivstoff, N.A.G.',
      '0351 - Gegenstände mit Explosivstoff, N.A.G.',
      '0352 - Gegenstände mit Explosivstoff, N.A.G.',
      '0353 - Gegenstände mit Explosivstoff, N.A.G.',
      '0354 - Gegenstände mit Explosivstoff, N.A.G.',
      '0355 - Gegenstände mit Explosivstoff, N.A.G.',
      '0356 - Gegenstände mit Explosivstoff, N.A.G.',
      '0357 - Explosive Stoffe, N.A.G.',
      '0358 - Explosive Stoffe, N.A.G.',
      '0359 - Explosive Stoffe, N.A.G.',
      '0360 - Zündeinrichtungen für Sprengungen, nicht elektrisch',
      '0361 - Zündeinrichtungen für Sprengungen, nicht elektrisch',
      '0362 - Munition, Übung',
      '0363 - Munition, Prüfung',
      '0364 - Detonatoren für Munition',
      '0365 - Detonatoren für Munition',
      '0366 - Detonatoren für Munition',
      '0367 - Zünder, sprengfähig',
      '0368 - Zünder, nicht sprengfähig',
      '0369 - Gefechtsköpfe, Rakete, mit Sprengladung',
      '0370 - Gefechtsköpfe, Rakete, mit Zerleger oder Ausstoßladung',
      '0371 - Gefechtsköpfe, Rakete, mit Zerleger oder Ausstoßladung',
      '0372 - Granaten, Übung, Hand oder Gewehr',
      '0373 - Signalkörper, Hand',
      '0374 - Falllote, mit Explosivstoff',
      '0375 - Falllote, mit Explosivstoff',
      '0376 - Treibladungsanzünder',
      '0377 - Anzündhütchen',
      '0378 - Anzündhütchen',
      '0379 - Treibladungshülsen, leer, mit Treibladungsanzünder',
      '0380 - Gegenstände, pyrophor',
      '0381 - Kartuschen für technische Zwecke',
      '0382 - Bestandteile, Zündkette, N.A.G.',
      '0383 - Bestandteile, Zündkette, N.A.G.',
      '0384 - Bestandteile, Zündkette, N.A.G.',
      '0385 - 5-Nitrobenzotriazol',
      '0386 - Trinitrobenzensulfonsäure',
      '0387 - Trinitrofluorenon',
      '0388 - Trinitrotoluol (TNT) in Mischung mit Trinitrobenzen oder Trinitrotoluol (TNT) in Mischung mit Hexanitrostilben',
      '0389 - Trinitrotoluol (TNT) in Mischung mit Trinitrobenzen und Hexanitrostilben',
      '0390 - Tritonal',
      '0391 - Hexogen (RDX), in Mischung mit Oktogen (HMX), angefeuchtet mit mindestens 15 Masse-% Wasser oder desensibilisiert mit mindestens 10 Masse-% Phlegmatisierungsmittel',
      '0392 - Trinitrotoluen (TNT) in Mischung mit Trinitrobenzen oder Trinitrotoluen (TNT) in Mischung mit Hexanitrostilben',
      '0393 - Hexotonal',
      '0394 - Trinitroresorcinol (Styphninsäure), angefeuchtet mit mindestens 20 Masse-% Wasser oder einer Alkohol/Wasser-Mischung',
      '0395 - Raketenmotoren, Flüssigtreibstoff',
      '0396 - Raketenmotoren, Flüssigtreibstoff',
      '0397 - Raketenmotoren, Flüssigtreibstoff, mit Sprengladung',
      '0398 - Raketenmotoren, Flüssigtreibstoff, mit Sprengladung',
      '0399 - Bomben, die entzündbare Flüssigkeiten enthalten, mit Sprengladung',
      '0401 - Dipikrylsulfid, trocken oder angefeuchtet mit weniger als 10 Masse-% Wasser',
      '0402 - Ammoniumperchlorat',
      '0403 - Leuchtkörper, Luftfahrzeug',
      '0404 - Leuchtkörper, Luftfahrzeug',
      '0405 - Patronen, Signal',
      '0406 - Dinitrosobenzen',
      '0407 - Tetrazol-1-essigsäure',
      '0408 - Zünder, sprengkräftig, mit Sicherungsvorrichtungen',
      '0409 - Zünder, sprengkräftig, mit Sicherungsvorrichtungen',
      '0410 - Zünder, sprengkräftig, mit Sicherungsvorrichtungen',
      '0411 - Nitropenta (PETN), mit nicht weniger als 7 Masse-% Wachs',
      '0412 - Patronen für Waffen, mit Sprengladung',
      '0413 - Patronen für Waffen, Manöver oder Patronen für Handfeuerwaffen, Manöver oder Patronen für Werkzeuge, ohne Geschoss',
      '0414 - Treibladungen für Geschütze',
      '0415 - Treibladungen für Geschütze',
      '0417 - Patronen für Waffen, mit inertem Geschoss oder Patronen für Handfeuerwaffen',
      '0418 - Leuchtkörper, Boden',
      '0419 - Leuchtkörper, Boden',
      '0420 - Leuchtkörper, Luftfahrzeug',
      '0421 - Leuchtkörper, Luftfahrzeug',
      '0424 - Geschosse, inert, mit Leuchtspurmitteln',
      '0425 - Geschosse, inert, mit Leuchtspurmitteln',
      '0426 - Geschosse, mit Zerleger oder Ausstoßladung',
      '0427 - Geschosse, mit Zerleger oder Ausstoßladung',
      '0428 - Pyrotechnische Gegenstände für technische Zwecke',
      '0429 - Pyrotechnische Gegenstände für technische Zwecke',
      '0430 - Pyrotechnische Gegenstände für technische Zwecke',
      '0431 - Pyrotechnische Gegenstände für technische Zwecke',
      '0432 - Pyrotechnische Gegenstände für technische Zwecke',
      '0433 - Pulverrohmasse, angefeuchtet mit nicht weniger als 17 Masse-% Alkohol',
      '0434 - Geschosse, mit Zerleger oder Ausstoßladung',
      '0435 - Geschosse, mit Zerleger oder Ausstoßladung',
      '0436 - Raketen, mit Ausstoßladung',
      '0437 - Raketen, mit Ausstoßladung',
      '0438 - Raketen, mit Ausstoßladung',
      '0439 - Hohlladungen, ohne Zündmittel',
      '0440 - Hohlladungen, ohne Zündmittel',
      '0441 - Hohlladungen, ohne Zündmittel',
      '0442 - Sprengladungen, gewerbliche, ohne Zündmittel',
      '0443 - Sprengladungen, gewerbliche, ohne Zündmittel',
      '0444 - Sprengladungen, gewerbliche, ohne Zündmittel',
      '0445 - Sprengladungen, gewerbliche, ohne Zündmittel',
      '0446 - Treibladungshülsen, verbrennlich, leer, ohne Treibladungszünder',
      '0447 - Treibladungshülsen, verbrennlich, leer, ohne Treibladungszünder',
      '0448 - 5-Mercaptotetrazol-1-essigsäure',
      '0449 - Torpedos, mit Flüssigtreibstoff, mit oder ohne Sprengladung',
      '0450 - Torpedos, mit Flüssigtreibstoff, mit inertem Kopf',
      '0451 - Torpedos, mit Sprengladung',
      '0452 - Granaten, Übung, Hand oder Gewehr',
      '0453 - Raketen, Leinenwurf',
      '0454 - Anzünder',
      '0455 - Sprengkapseln, nicht elektrisch',
      '0456 - Sprengkapseln, elektrisch',
      '0457 - Sprengladungen, kunststoffgebunden',
      '0458 - Sprengladungen, kunststoffgebunden',
      '0459 - Sprengladungen, kunststoffgebunden',
      '0460 - Sprengladungen, kunststoffgebunden',
      '0461 - Bestandteile, Zündkette',
      '0462 - Gegenstände mit Explosivstoff, N.A.G.',
      '0463 - Gegenstände mit Explosivstoff, N.A.G.',
      '0464 - Gegenstände mit Explosivstoff, N.A.G.',
      '0465 - Gegenstände mit Explosivstoff, N.A.G.',
      '0466 - Gegenstände mit Explosivstoff, N.A.G.',
      '0467 - Gegenstände mit Explosivstoff, N.A.G.',
      '0468 - Gegenstände mit Explosivstoff, N.A.G.',
      '0469 - Gegenstände mit Explosivstoff, N.A.G.',
      '0470 - Gegenstände mit Explosivstoff, N.A.G.',
      '0471 - Gegenstände mit Explosivstoff, N.A.G.',
      '0472 - Gegenstände mit Explosivstoff, N.A.G.',
      '0473 - Explosive Stoffe, N.A.G.',
      '0474 - Explosive Stoffe, N.A.G.',
      '0475 - Explosive Stoffe, N.A.G.',
      '0476 - Explosive Stoffe, N.A.G.',
      '0477 - Explosive Stoffe, N.A.G.',
      '0478 - Explosive Stoffe, N.A.G.',
      '0479 - Explosive Stoffe, N.A.G.',
      '0480 - Explosive Stoffe, N.A.G.',
      '0481 - Explosive Stoffe, N.A.G.',
      '0482 - Explosive Stoffe, sehr unempfindlich N.A.G. (Stoffe, EVI, N.A.G.)',
      '0483 - Hexogen (RDX), desensibilisiert',
      '0484 - Oktogen (HMX), desensibilisiert',
      '0485 - Explosive Stoffe, N.A.G.',
      '0486 - Gegenstände mit Explosivstoff, extrem unempfindlich (Gegenstände, EEI)',
      '0487 - Signalkörper, Rauch',
      '0488 - Munition, Übung',
      '0489 - Dinitroglycoluril (DINGU)',
      '0490 - Oxynitrotriazol (ONTA)',
      '0491 - Treibladungen für Geschütze',
      '0492 - Knallkapseln, Eisenbahn',
      '0493 - Knallkapseln, Eisenbahn',
      '0494 - Perforationshohlladungsträger, geladen, für Erdölbohrlöcher, ohne Zündmittel',
      '0495 - Treibstoff, flüssig',
      '0496 - Octonal',
      '0497 - Treibstoff, flüssig',
      '0498 - Treibstoff, fest',
      '0499 - Treibstoff, fest',
      '0500 - Zündeinrichtungen für Sprengungen, nicht elektrisch',
      '0501 - Treibstoff, fest',
      '0502 - Raketen, mit inertem Kopf',
      '0503 - Airbag-Gasgeneratoren, pyrotechnisch oder Airbag-Module, pyrotechnisch oder Gurtstraffer, pyrotechnisch oder Sicherheitseinrichtungen, pyrotechnisch',
      '0504 - 1H-Tetrazol',
      '0505 - Signalkörper, Seenot, für Schiffe',
      '0506 - Signalkörper, Seenot, für Schiffe',
      '0507 - Signalkörper, Rauch',
      '0508 - 1-Hydroxybenzotriazol, wasserfrei, trocken oder angefeuchtet, mit weniger als 20 Masse-% Wasser',
      '0509 - Treibladungspulver',
      '0510 - Raketenmotoren',
      '0511 - Sprengkapseln, elektronisch, programmierbar',
      '0512 - Sprengkapseln, elektronisch, programmierbar',
      '0513 - Sprengkapseln, elektronisch, programmierbar',
      '0514 - Feuerlöschmittel-Dispergiervorrichtungen',
      '1001 - Acetylen, gelöst',
      '1002 - Luft, verdichtet (Druckluft)',
      '1003 - Luft, tiefgekühlt, flüssig',
      '1005 - Ammoniak, wasserfrei',
      '1006 - Argon, verdichtet',
      '1008 - Bortrifluorid, verdichtet',
      '1009 - Bromtrifluormethan (Gas als Kältemittel R 13B1)',
      '1010 - Butadiene, stabilisiert, oder Butadiene, und Kohlenwasserstoff, Gemisch, stabilisiert, mit mehr als 20 % Butadienen',
      '1011 - Butan',
      '1012 - 1-Buten oder cis-2-Buten oder trans-2-Buten oder Butene, Gemisch',
      '1013 - Kohlenstoffdioxid',
      '1014 - Kohlenstoffdioxid und Sauerstoff, Gemisch, verdichtet',
      '1015 - Kohlenstoffdioxid und Distickstoffmonoxid, Gemisch',
      '1016 - Kohlenstoffmonoxid, verdichtet',
      '1017 - Chlor',
      '1018 - Chlordifluormethan (Gas als Kältemittel R 22)',
      '1020 - Chlorpentafluorethan (Gas als Kältemittel R 115)',
      '1021 - 1-Chlor-1,2,2,2-tetrafluorethan (Gas als Kältemittel R 124)',
      '1022 - Chlortrifluormethan (Gas als Kältemittel R 13)',
      '1023 - Stadtgas, verdichtet',
      '1026 - Dicyan',
      '1027 - Cyclopropan',
      '1028 - Dichlordifluormethan (Gas als Kältemittel R 12)',
      '1029 - Dichlormonofluormethan (Gas als Kältemittel R 21)',
      '1030 - 1,1-Difluorethan (Gas als Kältemittel R 152a)',
      '1032 - Dimethylamin, wasserfrei',
      '1033 - Dimethylether',
      '1035 - Ethan',
      '1036 - Ethylamin, wasserfrei',
      '1037 - Ethylchlorid',
      '1038 - Ethen, tiefgekühlt, flüssig',
      '1039 - Ethylmethylether',
      '1040 - Ethylenoxid mit Stickstoff bis zu einem höchstzulässigen Gesamtdruck von 1 MPa (10 bar) bei 50 °C',
      '1041 - Ethylenoxid und Kohlendioxid, Gemisch mit mehr als 9 %, aber höchstens 87 % Ethylenoxid',
      '1043 - Düngemittel, Lösung, mit freiem Ammoniak',
      '1044 - Feuerlöscher mit verdichtetem oder verflüssigtem Gas',
      '1045 - Fluor, verdichtet',
      '1046 - Helium, verdichtet',
      '1048 - Bromwasserstoff, wasserfrei',
      '1049 - Wasserstoff, verdichtet',
      '1050 - Chlorwasserstoff, wasserfrei',
      '1051 - Cyanwasserstoff, stabilisiert, mit weniger als 3 % Wasser',
      '1052 - Fluorwasserstoff, wasserfrei',
      '1053 - Schwefelwasserstoff',
      '1055 - Isobuten',
      '1056 - Krypton, verdichtet',
      '1057 - Feuerzeuge mit entzündbarem Gas oder Nachfüllpatronen für Feuerzeuge mit entzündbarem Gas',
      '1058 - Verflüssigte Gase, nicht entzündbar, überlagert mit Stickstoff, Kohlenstoffdioxid oder Luft',
      '1060 - Methylacetylen und Propadien, Gemisch, stabilisiert (Gemisch P 1) (Gemisch P 2)',
      '1061 - Methylamin, wasserfrei',
      '1062 - Methylbromid, mit höchstens 2 % Chlorpikrin',
      '1063 - Methylchlorid (Gas als Kältemittel R 40)',
      '1064 - Methylmercaptan',
      '1065 - Neon, verdichtet',
      '1066 - Stickstoff, verdichtet',
      '1067 - Distickstofftetroxid (Stickstoffdioxid)',
      '1069 - Nitrosylchlorid',
      '1070 - Distickstoffmonoxid',
      '1071 - Ölgas, verdichtet',
      '1072 - Sauerstoff, verdichtet',
      '1073 - Sauerstoff, tiefgekühlt, flüssig',
      '1075 - Petroleumgase, verflüssigt',
      '1076 - Phosgen',
      '1077 - Propen',
      '1078 - Gas als Kältemittel, N.A.G. (Gemisch F 1) (Gemisch F 2) (Gemisch F 3)',
      '1079 - Schwefeldioxid',
      '1080 - Schwefelhexafluorid',
      '1081 - Tetrafluorethylen, stabilisiert',
      '1082 - Chlortrifluorethylen, stabilisiert (Gas als Kältemittel R 1113)',
      '1083 - Trimethylamin, wasserfrei',
      '1085 - Vinylbromid, stabilisiert',
      '1086 - Vinylchlorid, stabilisiert',
      '1087 - Vinylmethylether, stabilisiert',
      '1088 - Acetal',
      '1089 - Ethanal (Acetaldehyd)',
      '1090 - Aceton',
      '1091 - Acetonöle',
      '1092 - Propenal (Acrolein), stabilisiert',
      '1093 - Acrylnitril, stabilisiert',
      '1098 - Allylalkohol',
      '1099 - Allylbromid',
      '1100 - Allylchlorid',
      '1104 - Amylacetate',
      '1105 - Pentanole',
      '1106 - Amylamine',
      '1107 - Amylchloride',
      '1108 - Penten (n-Amylen)',
      '1109 - Amylformiate',
      '1110 - n-Amylmethylketon',
      '1111 - Amylmercaptane',
      '1112 - Amylnitrate',
      '1113 - Amylnitrite',
      '1114 - Benzol',
      '1120 - Butanole',
      '1123 - Butylacetate',
      '1125 - n-Butylamin',
      '1126 - 1-Brombutan',
      '1127 - Chlorbutane',
      '1128 - n-Butylformiat',
      '1129 - Butyraldehyd',
      '1130 - Campheröl (Kampferöl)',
      '1131 - Kohlenstoffdisulfid',
      '1133 - Klebstoffe, die eine entzündbare Flüssigkeit enthalten',
      '1134 - Chlorbenzol',
      '1135 - Chlorethanol (Ethylenchlorhydrin)',
      '1136 - Steinkohlenteerdestillate, entzündbar',
      '1139 - Schutzanstrichlösung (einschließlich zu Industrie- oder anderen Zwecken verwendete Oberflächenbehandlungen oder Beschichtungen, wie Zwischenbeschichtung für Fahrzeugkarosserien, Auskleidung für Fässer)',
      '1143 - Crotonaldehyd oder Crotonaldehyd, stabilisiert',
      '1144 - Crotonylen',
      '1145 - Cyclohexan',
      '1146 - Cyclopentan',
      '1147 - Decalin (Decahydronaphthalen)',
      '1148 - Diacetonalkohol, technisch',
      '1149 - Dibutylether',
      '1150 - 1,2-Dichlorethylen',
      '1152 - Dichlorpentane',
      '1153 - Ethylenglycoldiethylether',
      '1154 - Diethylamin',
      '1155 - Diethylether (Ethylether)',
      '1156 - Diethylketon',
      '1157 - Diisobutylketon',
      '1158 - Diisopropylamin',
      '1159 - Diisopropylether',
      '1160 - Dimethylamin, wässrige Lösung',
      '1161 - Dimethylcarbonat',
      '1162 - Dimethyldichlorsilan',
      '1163 - Dimethylhydrazin, asymmetrisch',
      '1164 - Dimethylsulfid',
      '1165 - Dioxan',
      '1166 - Dioxolan',
      '1167 - Divinylether, stabilisiert',
      '1169 - Extrakte, aromatisch, flüssig',
      '1170 - Ethanol (Ethylalkohol) oder Ethanol-Lösung',
      '1171 - Ethylenglycolmonoethylether',
      '1172 - Ethylenglycolmonoethyletheracetat',
      '1173 - Ethylacetat',
      '1175 - Ethylbenzol (Ethylbenzen)',
      '1176 - Ethylborat (Triethylborat)',
      '1177 - 2-Ethylbutylacetat',
      '1178 - 2-Ethylbutyraldehyd',
      '1179 - Ethylbutylether',
      '1180 - Ethylbutyrat',
      '1181 - Ethylchloracetat',
      '1182 - Ethylchlorformiat',
      '1183 - Dimethyldichlorsilan',
      '1184 - Ethylendichlorid',
      '1185 - Ethylenimin, stabilisiert',
      '1188 - Ethylenglycolmonomethylether',
      '1189 - Ethylenglycolmonomethyletheracetat',
      '1190 - Ethylformiat',
      '1191 - Octylaldehyde',
      '1192 - Ethyllactat',
      '1193 - Methylethylketon (Ethylmethylketon)',
      '1194 - Ethylnitril, Lösung',
      '1195 - Ethylpropionat',
      '1196 - Ethyltrichlorsilan',
      '1197 - Extrakte, Geschmackstoffe, flüssig',
      '1198 - Formaldehyd, Lösung, entzündbar',
      '1199 - Furfurale (Furaldehyde)',
      '1201 - Fuselöl',
      '1202 - Dieselkraftstoff oder Gasöl oder Heizöl (leicht)',
      '1203 - Benzin oder Ottokraftstoffe',
      '1204 - Nitroglycerol (Nitroglycerin), Lösung in Alkohol mit höchstens 1 % Nitroglycerol',
      '1206 - Heptane',
      '1207 - Hexaldehyd',
      '1208 - Hexane',
      '1210 - Druckfarbe, entzündbar oder Druckfarbzubehörstoffe (einschließlich Druckfarbverdünnung und -lösemittel), entzündbar',
      '1212 - Isobutanol (Isobutylalkohol)',
      '1213 - Isobutylacetat',
      '1214 - Isobutylamin',
      '1216 - Isooctene',
      '1218 - Isopren, stabilisiert',
      '1219 - 2-Propanol (Isopropylalkohol)',
      '1220 - Isopropylacetat',
      '1221 - Isopropylamin',
      '1222 - Isopropylnitrat',
      '1223 - Kerosin',
      '1224 - Ketone, flüssig, N.A.G.',
      '1228 - Alkanthiole (Mercaptane), flüssig, entzündbar, giftig oder Alkanthiole (Mercaptane), Mischung, flüssig, entzündbar, giftig',
      '1229 - Mesityloxid',
      '1230 - Methanol',
      '1231 - Methylacetat',
      '1233 - Methylamylacetat',
      '1234 - Methylal',
      '1235 - Dimethylamin, wässrige Lösung',
      '1237 - Methylbutyrat',
      '1238 - Methylchlorformiat',
      '1239 - Methylchlormethylether',
      '1242 - Methyldichlorsilan',
      '1243 - Ameisensäuremethylester (Methylformiat)',
      '1244 - Methylhydrazin',
      '1245 - Methylisobutylketon',
      '1246 - Methylisopropenylketon, stabilisiert',
      '1247 - Methylmethacrylat, Monomer, stabilisiert',
      '1248 - Methylpropionat',
      '1249 - Methylpropylketon',
      '1250 - Methyltrichlorsilan',
      '1251 - Methylvinylketon, stabilisiert',
      '1259 - Nickeltetracarbonyl',
      '1261 - Nitromethan',
      '1262 - Octane',
      '1263 - Farbe (einschließlich Farbe, Lack, Emaille, Beize, Schellack, Firnis, Politur, flüssiger Füllstoff und flüssige Lackgrundlage) oder Farbzubehörstoffe (inklusive Farbverdünnung und -lösemittel)',
      '1264 - Paraldehyd',
      '1265 - Pentane, flüssig',
      '1266 - Parfümerzeugnisse mit entzündbaren Lösungsmitteln',
      '1267 - Roherdöl',
      '1268 - Erdöldestillate, N.A.G. oder Erdölprodukte, N.A.G.',
      '1272 - Kiefernöl',
      '1274 - 1-Propanol (n-Propylalkohol)',
      '1275 - Propionaldehyd',
      '1276 - n-Propylacetat',
      '1277 - Propylamin',
      '1278 - 1-Chlorpropan',
      '1279 - 1,2-Dichlorpropan',
      '1280 - Propylenoxid',
      '1281 - Propylformiate',
      '1282 - Pyridin',
      '1286 - Harzöl',
      '1287 - Gummilösung',
      '1288 - Schieferöl',
      '1289 - Natriummethylat, Lösung in Alkohol',
      '1292 - Tetraethylsilicat (Tetraethylorthosilikat)',
      '1293 - Tinkturen, medizinische',
      '1294 - Toluol',
      '1295 - Trichlorsilan',
      '1296 - Triethylamin',
      '1297 - Trimethylamin, wässrige Lösung mit mindestens 50 Masse-% Trimethylamin',
      '1298 - Trimethylchlorsilan',
      '1299 - Terpentin',
      '1300 - Terpentinölersatz',
      '1301 - Vinylacetat, stabilisiert',
      '1302 - Vinylethylether, stabilisiert',
      '1303 - Vinylidenchlorid, stabilisiert',
      '1304 - Vinylisobutylether, stabilisiert',
      '1305 - Vinyltrichlorsilan, stabilisiert',
      '1306 - Holzschutzmittel, flüssig',
      '1307 - Xylene',
      '1308 - Zirconium, suspendiert in einem entzündbaren flüssigen Stoff',
      '1309 - Aluminiumpulver, überzogen',
      '1310 - Ammoniumpikrat, angefeuchtet mit mindestens 10 Masse-% Wasser',
      '1312 - Borneol',
      '1313 - Calciumresinat',
      '1314 - Calciumresinat, geschmolzen und erstarrt',
      '1318 - Cobaltresinat, gefällt',
      '1320 - Dinitrophenol, angefeuchtet mit mindestens 15 Masse-% Wasser',
      '1321 - Dinitrophenolate, angefeuchtet mit mindestens 15 Masse-% Wasser',
      '1322 - Dinitroresorcinol, angefeuchtet mit mindestens 15 Masse-% Wasser',
      '1323 - Cereisen',
      '1324 - Filme auf Nitrocellulosebasis, gelatiniert, ausgenommen Abfälle',
      '1325 - Entzündbarer organischer fester Stoff',
      '1326 - Hafniumpulver, angefeuchtet mit mindestens 25 % Wasser',
      '1327 - Heu oder Stroh oder Bhusa',
      '1328 - Hexamethylentetramin',
      '1330 - Manganresinat',
      '1331 - Zündhölzer, überall zündbar',
      '1332 - Metaldehyd',
      '1333 - Cer, Platten, Barren, Stangen',
      '1334 - Naphthalin, roh oder raffiniert',
      '1336 - Nitroguanidin (Picrit), angefeuchtet mit mindestens 20 Masse-% Wasser',
      '1337 - Nitrostärke, angefeuchtet mit mindestens 20 Masse-% Wasser',
      '1338 - Phosphor, amorph',
      '1339 - Phosphorheptasulfid (P4S7), ohne gelben oder weißen Phosphor',
      '1340 - Phosphorpentasulfid (P2S5), ohne gelben oder weißen Phosphor',
      '1341 - Phosphorsesquisulfid (P4S3), ohne gelben oder weißen Phosphor',
      '1343 - Phosphortrisulfid (P4S6), ohne gelben oder weißen Phosphor',
      '1344 - Trinitrophenol (Pikrinsäure), angefeuchtet mit mindestens 30 Masse-% Wasser',
      '1345 - Kautschuk- (Gummi-) abfälle, gemahlen oder Kautschuk- (Gummi) reste, pulverförmig oder granuliert, höchstens 840 µm und mehr als 45 % Kautschuk-Gehalt',
      '1346 - Siliciumpulver, amorph',
      '1347 - Silberpikrat, angefeuchtet mit mindestens 30 Masse-% Wasser',
      '1348 - Natriumdinitroorthokresolat, angefeuchtet mit mindestens 15 Masse-% Wasser',
      '1349 - Natriumpikramat, angefeuchtet mit mindestens 20 Masse-% Wasser',
      '1350 - Schwefel',
      '1352 - Titanpulver, angefeuchtet mit mindestens 25 % Wasser',
      '1353 - Fasern, imprägniert mit schwach nitrierter Cellulose oder Gewebe imprägniert mit schwach nitrierter Cellulose',
      '1354 - Trinitrobenzen, angefeuchtet mit mindestens 30 Masse-% Wasser',
      '1355 - Trinitrobenzoesäure, angefeuchtet mit mindestens 30 Masse-% Wasser',
      '1356 - Trinitrotoluol (TNT), angefeuchtet mit mindestens 30 Masse-% Wasser',
      '1357 - Harnstoffnitrat, angefeuchtet mit mindestens 20 Masse-% Wasser',
      '1358 - Zirconiumpulver, angefeuchtet mit mindestens 25 % Wasser',
      '1360 - Calciumphosphid',
      '1361 - Kohle oder Ruß, tierischen oder pflanzlichen Ursprungs',
      '1362 - Kohle, aktiviert',
      '1363 - Kopra',
      '1364 - Baumwollabfälle, ölhaltig',
      '1365 - Baumwolle, nass',
      '1366 - Diethylzink',
      '1369 - p-Nitrosodimethylanilin',
      '1370 - Dimethylzink',
      '1372 - Fasern, tierischen Ursprungs oder Fasern, pflanzlichen Ursprungs, gebrannt, nass oder feucht',
      '1373 - Fasern oder Gewebe, tierischen oder pflanzlichen oder synthetischen Ursprungs, N.A.G., imprägniert mit Öl',
      '1374 - Fischmehl (Fischabfall), nicht stabilisiert, sehr gefährlich, unbeschränkter Feuchtegehalt, unbeschränkter Fettgehalt über 12 Masse-%, unbeschränkter Fettgehalt über 15 Masse-% bei mit Antioxidantien behandeltem Fischmehl oder Fischabfall',
      '1376 - Eisenoxid, gebraucht oder Eisenschwamm, gebraucht, aus der Kokereigasreinigung',
      '1378 - Metallkatalysator, angefeuchtet mit einem sichtbaren Überschuss an Flüssigkeit',
      '1379 - Papier, mit ungesättigten Ölen behandelt, unvollständig getrocknet (auch Kohlepapier)',
      '1380 - Pentaboran',
      '1381 - Phosphor, weiß oder gelb, trocken, unter Wasser oder in Lösung',
      '1382 - Kaliumsulfid, wasserfrei oder Kaliumsulfid mit weniger als 30 % Kristallwasser',
      '1383 - Pyrophores Metall oder Legierung',
      '1384 - Natriumdithionit (Natriumhydrosulfit)',
      '1385 - Natriumsulfid, wasserfrei oder Natriumsulfid mit weniger als 30 % Kristallwasser',
      '1386 - Ölsaatkuchen mit mehr als 1,5 Masse-% Öl und höchstens 11 Masse-% Feuchtigkeit',
      '1387 - Wollabfälle, nass',
      '1389 - Alkalimetallamalgam',
      '1390 - Alkalimetallamide',
      '1391 - Alkalimetalldispersion oder Erdalkalimetalldispersion',
      '1392 - Erdalkalimetallamalgam, flüssig',
      '1393 - Erdalkalimetalllegierung',
      '1394 - Aluminiumcarbid',
      '1395 - Aluminiumferrosiliciumpulver',
      '1396 - Aluminiumpulver, nicht überzogen',
      '1397 - Aluminiumphosphid',
      '1398 - Aluminiumsiliciumpulver, nicht überzogen',
      '1400 - Bariumazid, trocken oder angefeuchtet mit weniger als 50 Masse-% Wasser',
      '1401 - Calciumresinat',
      '1402 - Calciumcarbid',
      '1403 - Calciumcyanamid mit mehr als 0,1 Masse-% Calciumcarbid',
      '1404 - Calciumhydrid',
      '1405 - Calciumsilicid',
      '1407 - Caesium',
      '1408 - Ferrosilicium mit mindestens 30 Masse-%, aber weniger als 90 Masse-% Silicium',
      '1409 - Metallhydride, mit Wasser reagierend',
      '1410 - Lithiumaluminiumhydrid',
      '1411 - Lithiumaluminiumhydrid in Ether',
      '1413 - Lithiumborhydrid',
      '1414 - Lithiumhydrid',
      '1415 - Lithium',
      '1417 - Lithiumsilicium',
      '1418 - Magnesiumpulver oder Magnesiumlegierungspulver',
      '1419 - Magnesiumaluminiumphosphid',
      '1420 - Kaliummetalllegierungen, flüssig',
      '1421 - Alkalimetalllegierung, flüssig',
      '1422 - Kalium-Natrium-Legierungen, flüssig',
      '1423 - Rubidium',
      '1426 - Natriumborhydrid',
      '1427 - Natriumhydrid',
      '1428 - Natrium',
      '1431 - Natriummethylat, Lösung in Alkohol',
      '1432 - Natriumphosphid',
      '1433 - Zinnphosphide',
      '1435 - Zinkaschen',
      '1436 - Zinkpulver oder Zinkstaub',
      '1437 - Zirconiumhydrid',
      '1438 - Aluminiumnitrat',
      '1439 - Ammoniumdichromat',
      '1442 - Ammoniumperchlorat',
      '1444 - Ammoniumpersulfat',
      '1445 - Bariumchlorat, fest',
      '1446 - Bariumnitrat',
      '1447 - Bariumperchlorat, fest',
      '1448 - Bariumpermanganat',
      '1449 - Bariumperoxid',
      '1450 - Bromate, anorganisch',
      '1451 - Caesiumnitrat',
      '1452 - Calciumchlorat',
      '1453 - Calciumchlorit',
      '1454 - Calciumnitrat',
      '1455 - Calciumperchlorat',
      '1456 - Calciumpermanganat',
      '1457 - Calciumperoxid',
      '1458 - Chlorat und Borat, Mischung',
      '1459 - Chlorat und Magnesiumchlorid, Mischung, fest',
      '1461 - Chlorate, anorganisch',
      '1462 - Chlorite, anorganisch',
      '1463 - Chromtrioxid, wasserfrei',
      '1465 - Didymiumnitrat',
      '1466 - Eisen(III)-nitrat',
      '1467 - Guanidinnitrat',
      '1469 - Bleinitrat',
      '1470 - Bleiperchlorat, fest',
      '1471 - Lithiumhypochlorit, trocken oder Mischung',
      '1472 - Lithiumperoxid',
      '1473 - Magnesiumbromat',
      '1474 - Magnesiumnitrat',
      '1475 - Magnesiumperchlorat',
      '1476 - Magnesiumperoxid',
      '1477 - Nitrate, anorganisch',
      '1479 - Entzündend (oxidierend) wirkender Stoff',
      '1481 - Perchlorate, anorganisch',
      '1482 - Permanganate, anorganisch',
      '1483 - Peroxide, anorganisch',
      '1484 - Kaliumbromat',
      '1485 - Kaliumchlorat',
      '1486 - Kaliumnitrat',
      '1487 - Kaliumnitrat und Natriumnitrit, Mischung',
      '1488 - Kaliumnitrit',
      '1489 - Kaliumperchlorat',
      '1490 - Kaliumpermanganat',
      '1491 - Kaliumperoxid',
      '1492 - Kaliumpersulfat',
      '1493 - Silbernitrat',
      '1494 - Natriumbromat',
      '1495 - Natriumchlorat',
      '1496 - Natriumchlorit',
      '1498 - Natriumnitrat',
      '1499 - Natriumnitrat und Kaliumnitrat, Mischung',
      '1500 - Kaliumnitrat und Natriumnitrit, Mischung',
      '1502 - Natriumperchlorat',
      '1503 - Natriumpermanganat',
      '1504 - Natriumperoxid',
      '1505 - Natriumpersulfat',
      '1506 - Strontiumchlorat',
      '1507 - Strontiumnitrat',
      '1508 - Strontiumperchlorat',
      '1509 - Strontiumperoxid',
      '1510 - Tetranitromethan',
      '1511 - Harnstoffwasserstoffperoxid',
      '1512 - Zinkammoniumnitrit',
      '1513 - Zinkchlorat',
      '1514 - Zinknitrat',
      '1515 - Zinkpermanganat',
      '1516 - Zinkperoxid',
      '1517 - Zirconiumpikramat, angefeuchtet mit mindestens 20 Masse-% Wasser',
      '1541 - Acetoncyanhydrin, stabilisiert',
      '1544 - Alkaloide, fest oder Alkaloidsalze, fest',
      '1545 - Allylisothiocyanat, stabilisiert',
      '1546 - Ammoniumarsenat',
      '1547 - Anilin',
      '1548 - Anilinhydrochlorid',
      '1549 - Anorganische Antimonverbindung, fest, N.A.G.',
      '1550 - Antimonlaktat',
      '1551 - Antimonylkaliumtartrat',
      '1553 - Arsensäure, flüssig',
      '1554 - Arsensäure, fest',
      '1555 - Arsen(III)-bromid',
      '1556 - Arsenverbindung, flüssig, anorganisch einschließlich Arsenate, Arsenite und Arsensulfide',
      '1557 - Arsenverbindung, fest, anorganisch (einschließlich Arsenate, Arsenite und Arsensulfide)',
      '1558 - Arsensäure, flüssig',
      '1559 - Arsenpentoxid',
      '1560 - Arsen(III)-chlorid',
      '1561 - Arsen(III)-oxid',
      '1562 - Arsenstaub',
      '1564 - Bariumverbindungen, N.A.G.',
      '1565 - Bariumcyanid',
      '1566 - Berylliumverbindungen, N.A.G.',
      '1567 - Beryllium, Pulver',
      '1569 - Bromaceton',
      '1570 - Brucin',
      '1571 - Bariumazid, angefeuchtet mit mindestens 50 Masse-% Wasser',
      '1572 - Kakodylsäure',
      '1573 - Calciumarsenat',
      '1574 - Calciumarsenat und Calciumarsenit, Mischung, fest',
      '1575 - Calciumcyanid',
      '1577 - Chlordinitrobenzole, flüssig',
      '1578 - Chlornitrobenzole, fest',
      '1579 - 4-Chlor-o-toluidinhydrochlorid, fest',
      '1580 - Chlorpikrin',
      '1581 - Chlorpikrin und Brommethan, Gemisch, mit mehr als 2 % Chlorpikrin',
      '1582 - Chlorpikrin und Methylchlorid, Gemisch',
      '1583 - Chlorpikrin, Mischung',
      '1585 - Kupferacetoarsenit',
      '1586 - Kupferarsenit',
      '1587 - Kupfercyanid',
      '1588 - Cyanide, anorganisch, fest',
      '1589 - Chlorcyan, stabilisiert',
      '1590 - Dichloraniline, flüssig',
      '1591 - o-Dichlorbenzen',
      '1593 - Dichlormethan',
      '1594 - Diethylsulfat',
      '1595 - Dimethylsulfat',
      '1596 - Dinitroaniline',
      '1597 - Dinitrobenzole, flüssig',
      '1598 - Dinitro-o-kresol',
      '1599 - Dinitrophenol-Lösung',
      '1600 - Dinitrotoluole, geschmolzen',
      '1601 - Desinfektionsmittel, fest, giftig, N.A.G.',
      '1602 - Farbe, flüssig, giftig, N.A.G. oder Farbstoffzwischenprodukt, flüssig, giftig, N.A.G.',
      '1603 - Ethylbromacetat',
      '1604 - Ethylendiamin',
      '1605 - Ethylendibromid',
      '1606 - Eisen(III)-arsenat',
      '1607 - Eisen(III)-arsenit',
      '1608 - Eisen(II)-arsenat',
      '1611 - Hexaethyltetraphosphat',
      '1612 - Hexaethyltetraphosphat und verdichtetes Gas, Gemisch',
      '1613 - Cyanwasserstoff, wässrige Lösung, mit höchstens 20 % Cyanwasserstoff',
      '1614 - Cyanwasserstoff, stabilisiert, mit weniger als 3 % Wasser und aufgesaugt durch eine inerte, poröse Masse',
      '1616 - Blei(II)-acetat (Bleiacetat)',
      '1617 - Bleiarsenate',
      '1618 - Bleiarsenite',
      '1620 - Bleicyanid',
      '1621 - London purple',
      '1622 - Magnesiumarsenat',
      '1623 - Quecksilber(II)-arsenat',
      '1624 - Quecksilber(II)-chlorid',
      '1625 - Quecksilber(II)-nitrat',
      '1626 - Kaliumquecksilber(II)-cyanid',
      '1627 - Quecksilber(I)-nitrat',
      '1629 - Quecksilberacetat',
      '1630 - Quecksilber(II)-ammoniumchlorid',
      '1631 - Quecksilber(II)-benzoat',
      '1634 - Quecksilberbromide',
      '1636 - Quecksilbercyanid',
      '1637 - Quecksilberglukonat',
      '1638 - Quecksilberiodid (Quecksilber(I)-iodid und Quecksilber(II)-iodid)',
      '1639 - Quecksilbernucleat',
      '1640 - Quecksilberoleat',
      '1641 - Quecksilber(II)-oxid',
      '1642 - Quecksilberoxycyanid, desensibilisiert',
      '1643 - Kaliumquecksilber(II)-iodid',
      '1644 - Quecksilbersalicylat',
      '1645 - Quecksilbersulfat',
      '1646 - Quecksilberthiocyanat',
      '1647 - Brommethan und Ethylendibromid, Mischung, flüssig',
      '1648 - Acetonitril',
      '1649 - Klopfschutzmittel für Motorkraftstoff',
      '1650 - beta-Naphthylamin, fest',
      '1651 - Naphthylthioharnstoff',
      '1652 - Naphthylharnstoff',
      '1653 - Nickelcyanid',
      '1654 - Nikotin',
      '1655 - Nikotinverbindung, fest oder Nikotinzubereitung, fest',
      '1656 - Nikotinhydrochlorid, flüssig oder Lösung',
      '1657 - Nikotinsalicylat',
      '1658 - Nikotinsulfat, Lösung',
      '1659 - Nikotintartrat',
      '1660 - Stickstoffmonoxid, verdichtet (Stickoxide, verdichtet)',
      '1661 - Nitroaniline (o-, m-, p-)',
      '1662 - Trinitrobenzen, trocken oder angefeuchtet mit weniger als 30 Masse-% Wasser',
      '1663 - Nitrophenole (o-, m-, p-)',
      '1664 - Nitrotoluole, flüssig',
      '1665 - Nitroxylene, flüssig',
      '1669 - Pentachlorethan',
      '1670 - Perchlormethylmercaptan',
      '1671 - Phenol, fest',
      '1672 - Phenylcarbylaminchlorid',
      '1673 - Phenylendiamine (o-, m-, p-)',
      '1674 - Phenylquecksilber(II)-acetat',
      '1677 - Kaliumarsenat',
      '1678 - Kaliumarsenit',
      '1679 - Kaliumkupfer(I)-cyanid',
      '1680 - Kaliumcyanid, fest',
      '1683 - Silberarsenit',
      '1684 - Quecksilbercyanid',
      '1685 - Natriumarsenat',
      '1686 - Natriumarsenit, wässrige Lösung',
      '1687 - Natriumazid',
      '1688 - Natriumkakodylat',
      '1689 - Natriumcyanid, fest',
      '1690 - Natriumfluorid, fest',
      '1691 - Strontiumarsenit',
      '1692 - Strychnin oder Strychninsalze',
      '1693 - Stoff zur Herstellung von Tränengasen, flüssig, N.A.G.',
      '1694 - Brombenzylcyanide, flüssig oder fest',
      '1695 - Chloraceton, stabilisiert',
      '1697 - Chloracetophenon, fest',
      '1698 - Diphenylaminochlorarsin',
      '1699 - Diphenylchlorarsin, flüssig',
      '1700 - Tränengas-Kerzen',
      '1701 - Xylylbromid, flüssig',
      '1702 - 1,1,2,2-Tetrachlorethan',
      '1704 - Tetraethyldithiopyrophosphat',
      '1707 - Thalliumverbindung, N.A.G.',
      '1708 - Toluidine, flüssig',
      '1709 - 2,4-Toluylendiamin, fest',
      '1710 - Trichlorethylen',
      '1711 - Xylidine, flüssig',
      '1712 - Zinkarsenat oder Zinkarsenit oder Zinkarsenat und Zinkarsenit, Mischung',
      '1713 - Zinkcyanid',
      '1714 - Zinkphosphid',
      '1715 - Essigsäureanhydrid',
      '1716 - Acetylbromid',
      '1717 - Acetylchlorid',
      '1718 - Butylphosphat',
      '1719 - Ätzender, alkalischer, flüssiger Stoff, N.A.G.',
      '1722 - Allylchlorformiat',
      '1723 - Allyliodid',
      '1724 - Allyltrichlorsilan, stabilisiert',
      '1725 - Aluminiumbromid, wasserfrei',
      '1726 - Aluminiumchlorid, wasserfrei',
      '1727 - Ammoniumhydrogendifluorid, fest',
      '1728 - Amyltrichlorsilan',
      '1729 - Anisoylchlorid',
      '1730 - Antimonpentachlorid, flüssig',
      '1731 - Antimonpentachlorid, Lösung',
      '1732 - Antimon(V)-fluorid',
      '1733 - Antimontrichlorid',
      '1736 - Benzoylchlorid',
      '1737 - Benzylbromid',
      '1738 - Benzylchlorid',
      '1739 - Benzylchlorformiat',
      '1740 - Hydrogendifluoride, fest, N.A.G.',
      '1741 - Bortrichlorid',
      '1742 - Bortrifluorid-Essigsäure-Komplex, flüssig',
      '1743 - Bortrifluorid-Propionsäure-Komplex, flüssig',
      '1744 - Brom oder Brom, Lösung',
      '1745 - Brompentafluorid',
      '1746 - Bromtrifluorid',
      '1747 - Butyltrichlorsilan',
      '1748 - Calciumhypochlorit, trocken oder Calciumhypochlorit, Mischung, trocken, mit mehr als 39 % aktivem Chlor sowie 8,8 % aktivem Sauerstoff',
      '1749 - Chlortrifluorid',
      '1750 - Chloressigsäure, Lösung',
      '1751 - Chloressigsäure, fest',
      '1752 - Chloracetylchlorid',
      '1753 - Chlorphenyltrichlorsilan',
      '1754 - Chlorsulfonsäure mit oder ohne Schwefeltrioxid',
      '1755 - Chromsäure, Lösung',
      '1756 - Chromfluorid, fest (CrF2, CrF3, CrF4 und CrF5)',
      '1757 - Chromfluorid, Lösung (CrF2, CrF3, CrF4 und CrF5)',
      '1758 - Chromoxychlorid',
      '1759 - Ätzender, fester Stoff, N.A.G.',
      '1760 - Ätzender, flüssiger Stoff, N.A.G.',
      '1761 - Kupferethylendiamin, Lösung',
      '1762 - Cyclohexenyltrichlorsilan',
      '1763 - Cyclohexyltrichlorsilan',
      '1764 - Dichloressigsäure',
      '1765 - Dichloracetylchlorid',
      '1766 - Dichlorphenyltrichlorsilan',
      '1767 - Diethyldichlorsilan',
      '1768 - Difluorphosphorsäure, wasserfrei',
      '1769 - Diphenyldichlorsilan',
      '1770 - Diphenylbrommethan',
      '1771 - Dodecyltrichlorsilan',
      '1773 - Eisenchlorid, wasserfrei (Eisen(II)-chlorid und Eisen(III)-chlorid)',
      '1774 - Feuerlöscherladungen, ätzender flüssiger Stoff',
      '1775 - Fluorborsäure',
      '1776 - Fluorphosphorsäure, wasserfrei',
      '1777 - Fluorsulfonsäure',
      '1778 - Fluorkieselsäure',
      '1779 - Ameisensäure, mit mehr als 85 Masse-% Säure',
      '1780 - Fumarylchlorid',
      '1781 - Hexadecyltrichlorsilan',
      '1782 - Hexafluorphosphorsäure',
      '1783 - Hexamethylendiamin, Lösung',
      '1784 - Cyclohexyltrichlorsilan',
      '1786 - Fluorwasserstoffsäure und Schwefelsäure, Mischung',
      '1787 - Iodwasserstoffsäure',
      '1788 - Bromwasserstoffsäure',
      '1789 - Salzsäure',
      '1790 - Fluorwasserstoffsäure mit mehr als 85 % Fluorwasserstoff',
      '1791 - Hypochloritlösung',
      '1792 - Iodmonochlorid, fest',
      '1793 - Isopropylphosphat',
      '1794 - Blei(II)-sulfat mit mehr als 3 % freier Säure',
      '1796 - Nitriersäuremischung mit mehr als 50 % Salpetersäure',
      '1798 - Gemische aus Salpetersäure und Salzsäure, Beförderung verboten',
      '1799 - Nonyltrichlorsilan',
      '1800 - Octadecyltrichlorsilan',
      '1801 - Octyltrichlorsilan',
      '1802 - Perchlorsäure mit höchstens 50 Masse-% Säure',
      '1803 - Phenolsulfonsäure, flüssig',
      '1804 - Chlorphenyltrichlorsilan',
      '1805 - Phosphorsäure, Lösung',
      '1806 - Phosphorpentachlorid',
      '1807 - Phosphorpentoxid',
      '1808 - Phosphortribromid',
      '1809 - Phosphortrichlorid',
      '1810 - Phosphoroxychlorid',
      '1811 - Kaliumhydrogendifluorid, fest',
      '1812 - Kaliumfluorid, fest',
      '1813 - Kaliumhydroxid, fest',
      '1814 - Kaliumhydroxidlösungen (Kalilauge)',
      '1815 - Propionylchlorid',
      '1816 - Propyltrichlorsilan',
      '1817 - Pyrosulfurylchlorid',
      '1818 - Siliciumtetrachlorid',
      '1819 - Natriumaluminatlösung',
      '1823 - Natriumhydroxid, fest',
      '1824 - Natriumhydroxidlösungen (Natronlauge)',
      '1825 - Natriummonoxid',
      '1826 - Abfall-Nitriersäuremischung mit mehr als 50 % Salpetersäure',
      '1827 - Zinntetrachlorid, wasserfrei',
      '1828 - Schwefelchloride (Schwefel(I)-chlorid, Schwefeldichlorid und Schwefeltetrachlorid)',
      '1829 - Schwefeltrioxid, stabilisiert',
      '1830 - Schwefelsäure mit mehr als 51 % Säure',
      '1831 - Schwefelsäure, rauchend (Oleum)',
      '1832 - Schwefelsäure, gebraucht',
      '1833 - Schweflige Säure',
      '1834 - Pyrosulfurylchlorid',
      '1835 - Tetramethylammoniumhydroxid, wässrige Lösung mit mehr als 2,5 %, aber weniger als 25 % Tetramethylammoniumhydroxid',
      '1836 - Thionylchlorid',
      '1837 - Thiophosphorylchlorid',
      '1838 - Titantetrachlorid',
      '1839 - Trichloressigsäure',
      '1840 - Zinkchlorid, Lösung',
      '1841 - Acetaldehydammoniak',
      '1843 - Ammoniumdinitro-o-kresolat',
      '1845 - Kohlenstoffdioxid, fest (Trockeneis)',
      '1846 - Tetrachlormethan (Tetrachlorkohlenstoff)',
      '1847 - Kaliumsulfid, hydratisiert, mit mindestens 30 % Kristallwasser',
      '1848 - Propionsäure, mit mindestens 10 % und weniger als 90 Masse-% Säure',
      '1849 - Natriumsulfid mit mindestens 30 % Kristallwasser',
      '1851 - Medikament, flüssig, giftig, N.A.G.',
      '1854 - Bariumlegierungen, pyrophor',
      '1855 - Calcium, pyrophor oder Calciumlegierungen, pyrophor',
      '1856 - Lappen, ölhaltig',
      '1857 - Textilabfälle, nass',
      '1858 - Hexafluorpropylen (Gas als Kältemittel R 1216)',
      '1859 - Siliciumtetrafluorid, verdichtet',
      '1860 - Vinylfluorid, stabilisiert',
      '1862 - Ethylcrotonat',
      '1863 - Düsenkraftstoff, Dampfdruck bei 50 °C größer als 110 kPa z. B. Jet A1',
      '1865 - n-Propylnitrat',
      '1866 - Harzlösung, entzündbar',
      '1868 - Decaboran',
      '1869 - Magnesium oder Magnesiumlegierungen, mit mehr als 50 % Magnesium, in Pellets, Spänen, Bändern',
      '1870 - Kaliumborhydrid',
      '1871 - Titanhydrid',
      '1872 - Bleidioxid',
      '1873 - Perchlorsäure, mit mehr als 50 Masse-%, aber höchstens 72 Masse-% Säure',
      '1884 - Bariumoxid',
      '1885 - Benzidin',
      '1886 - Benzylidenchlorid',
      '1887 - Bromchlormethan',
      '1888 - Trichlormethan (Chloroform)',
      '1889 - Bromcyan',
      '1891 - Ethylbromid',
      '1892 - Ethyldichlorarsin',
      '1894 - Phenylquecksilber(II)-hydroxid',
      '1895 - Phenylquecksilber(II)-nitrat',
      '1897 - Tetrachlorethen (Perchlorethylen)',
      '1898 - Acetyliodid',
      '1902 - Diisooctylphosphat',
      '1903 - Desinfektionsmittel, flüssig, ätzend, N.A.G.',
      '1905 - Selensäure',
      '1906 - Abfallschwefelsäure',
      '1907 - Natronkalk mit mehr als 4 % Natriumhydroxid',
      '1908 - Chloritlösungen',
      '1910 - Calciumoxid',
      '1911 - Diboran, verdichtet',
      '1912 - Methylchlorid und Dichlormethan, Gemisch',
      '1913 - Neon, tiefgekühlt, flüssig',
      '1914 - Butylpropionate',
      '1915 - Cyclohexanon',
      '1916 - 2,2′-Dichlordiethylether',
      '1917 - Ethylacrylat, stabilisiert',
      '1918 - Isopropylbenzen',
      '1919 - Methylacrylat, stabilisiert',
      '1920 - Nonane',
      '1921 - Propylenimin, stabilisiert',
      '1922 - Pyrrolidin',
      '1923 - Calciumdithionit (Calciumhydrosulfit)',
      '1928 - Methylmagnesiumbromid in Diethylether',
      '1929 - Kaliumdithionit (Kaliumhydrosulfit)',
      '1931 - Zinkdithionit',
      '1932 - Zirconiumabfall',
      '1935 - Cyanid, Lösung, N.A.G.',
      '1938 - Bromessigsäure, Lösung',
      '1939 - Phosphoroxybromid',
      '1940 - Thioglycolsäure',
      '1941 - Dibromdifluormethan',
      '1942 - Ammoniumnitrat, mit höchstens 0,2 % brennbaren Stoffen (einschließlich organischer Stoffe als Kohlenstoff-Äquivalent) und frei von sonstigen zugesetzten Stoffen',
      '1944 - Sicherheitszündhölzer (Heftchen, Briefchen oder Schachteln)',
      '1945 - Wachszündhölzer',
      '1950 - Druckgaspackungen; ätzend',
      '1951 - Argon, tiefgekühlt, flüssig',
      '1952 - Ethylenoxid und Kohlenstoffdioxid, Gemisch mit höchstens 9 % Ethylenoxid',
      '1953 - Verdichtetes Gas, giftig, entzündbar, N.A.G.',
      '1954 - Verdichtetes Gas, entzündbar, N.A.G.',
      '1955 - Verdichtetes Gas, giftig, N.A.G.',
      '1956 - Verdichtetes Gas, N.A.G.',
      '1957 - Deuterium, verdichtet',
      '1958 - 1,2-Dichlor-1,1,2,2-tetrafluorethan (Gas als Kältemittel R 114)',
      '1959 - 1,1-Difluorethylen (Gas als Kältemittel R 1132a)',
      '1961 - Ethan, tiefgekühlt, flüssig',
      '1962 - Ethen (Ethylen)',
      '1963 - Helium, tiefgekühlt, flüssig',
      '1964 - Kohlenwasserstoffgas, Gemisch, verdichtet, N.A.G.',
      '1965 - Kohlenwasserstoffgas, Gemische, verflüssigt, N.A.G. (Gemisch A, A 01, A 02, A 0, A 1, B 1, B 2, B oder C)',
      '1966 - Wasserstoff, tiefgekühlt, flüssig',
      '1967 - Insektenbekämpfungsmittel, gasförmig, giftig, N.A.G.',
      '1968 - Insektenbekämpfungsmittel, gasförmig, N.A.G.',
      '1969 - Isobutan',
      '1970 - Krypton, tiefgekühlt, flüssig',
      '1971 - Methan, verdichtet oder Erdgas, verdichtet, mit hohem Methangehalt',
      '1972 - Methan, tiefgekühlt, flüssig oder Erdgas, tiefgekühlt, flüssig, mit hohem Methangehalt, LNG (Liquefied Natural Gas)',
      '1973 - Chlordifluormethan und Chlorpentafluorethan, Gemisch mit einem konstanten Siedepunkt mit rund 49 % Chlordifluormethan (Gas als Kältemittel R 502)',
      '1974 - Bromchlordifluormethan (Gas als Kältemittel R 12B1)',
      '1975 - Stickstoffmonoxid und Distickstofftetroxid Gemisch (Stickstoffmonoxid und Stickstoffdioxid Gemisch)',
      '1976 - Octafluorcyclobutan (Gas als Kältemittel RC 318)',
      '1977 - Stickstoff, tiefgekühlt, flüssig',
      '1978 - Propan',
      '1979 - Edelgase, Gemisch, verdichtet',
      '1980 - Edelgase und Sauerstoff, Gemisch, verdichtet',
      '1981 - Edelgase und Stickstoff, Gemisch, verdichtet',
      '1982 - Tetrafluormethan, verdichtet (Gas als Kältemittel R 14, verdichtet)',
      '1983 - 1-Chlor-2,2,2-trifluorethan (Gas als Kältemittel R 133a)',
      '1984 - Trifluormethan (Gas als Kältemittel R 23)',
      '1986 - Alkohole, entzündbar, giftig, N.A.G.',
      '1987 - Alkohole, N.A.G.',
      '1988 - Aldehyde, entzündbar, giftig, N.A.G.',
      '1989 - Aldehyde, N.A.G.',
      '1990 - Benzaldehyd',
      '1991 - Chloropren, stabilisiert',
      '1992 - Entzündbarer, flüssiger Stoff, giftig, N.A.G.',
      '1993 - Entzündbarer, flüssiger Stoff, N.A.G.',
      '1994 - Eisenpentacarbonyl',
      '1999 - Teere, flüssig, einschließlich Straßenasphalt und Öle, Bitumen, und Cutback (Verschnittbitumen)',
      '2000 - Zelluloid, in Blöcken, Stangen, Platten, Rohren usw. (ausgenommen Abfälle)',
      '2001 - Cobaltnaphtenatpulver',
      '2002 - Zelluloid, Abfall',
      '2003 - Metallalkyle, mit Wasser reagierend oder Metallaryle, mit Wasser reagierend',
      '2004 - Magnesiumdiamid',
      '2005 - Diphenylmagnesium',
      '2006 - Kunststoffe auf Nitrocellulosebasis, selbsterhitzungsfähig, N.A.G.',
      '2008 - Zirconiumpulver, trocken',
      '2009 - Zirconium, trocken, Bleche, Streifen oder gerollter Draht (dünner als 18 µm)',
      '2010 - Magnesiumhydrid',
      '2011 - Magnesiumphosphid',
      '2012 - Kaliumphosphid',
      '2013 - Strontiumphosphid',
      '2014 - Wasserstoffperoxid, wässrige Lösung mit mindestens 20 %, aber höchstens 60 % Wasserstoffperoxid (Stabilisierung nach Bedarf)',
      '2015 - Wasserstoffperoxid, wässrige Lösung, stabilisiert mit mehr als 60 % Wasserstoffperoxid',
      '2016 - Munition, giftig, nicht explosiv, ohne Zerleger oder Ausstoßladung, nicht scharf',
      '2017 - Munition, tränenerzeugend, nicht explosiv, ohne Zerleger oder Ausstoßladung, nicht scharf',
      '2018 - Chloraniline, fest',
      '2019 - Chloraniline, flüssig',
      '2020 - Chlorphenole, fest',
      '2021 - Chlorphenole, flüssig',
      '2022 - Cresylsäure',
      '2023 - Epichlorhydrin',
      '2024 - Quecksilberverbindung, flüssig, N.A.G.',
      '2025 - Quecksilberverbindung, fest, N.A.G.',
      '2026 - Phenylquecksilberverbindungen, N.A.G.',
      '2027 - Natriumarsenit, fest',
      '2028 - Rauchbomben, Nebelbomben, nicht explosiv, ätzenden flüssigen Stoff enthaltend, ohne Zünder',
      '2029 - Hydrazin, wasserfrei',
      '2030 - Hydrazinhydrat oder Hydrazin, wässrige Lösung mit mindestens 37 Masse-%, aber höchstens 64 Masse-% Hydrazin',
      '2031 - Salpetersäure, andere als rotrauchend, mit mehr als 70 % Säure',
      '2032 - Salpetersäure, rotrauchend',
      '2033 - Kaliummonoxid',
      '2034 - Wasserstoff und Methan, Gemisch, verdichtet',
      '2035 - 1,1,1-Trifluorethan (Gas als Kältemittel R 143a)',
      '2036 - Xenon, verdichtet',
      '2037 - Gefäße, klein, mit Gas (Gaspatronen), ohne Entnahmeeinrichtung, nicht nachfüllbar',
      '2038 - Dinitrotoluole, flüssig',
      '2044 - 2,2-Dimethylpropan',
      '2045 - Isobutyraldehyd (Isobutanal)',
      '2046 - Cymole (Cymene)',
      '2047 - Dichlorpropene',
      '2048 - Dicyclopentadien',
      '2049 - Diethylbenzen',
      '2050 - Diisobutylen, isomere Verbindungen',
      '2051 - 2-Dimethylaminoethanol',
      '2052 - Limonen (Dipenten)',
      '2053 - Methylisobutylcarbinol',
      '2054 - Morpholin',
      '2055 - Styrol, monomer, stabilisiert',
      '2056 - Tetrahydrofuran',
      '2057 - Tripropylen',
      '2058 - Valeraldehyd',
      '2059 - Zellulosenitrat (Nitrocellulose), Lösung, entzündbar',
      '2067 - Ammoniumnitrathaltige Düngemittel, Typ A1',
      '2068 - Ammoniumnitrathaltige Düngemittel, Typ A2',
      '2069 - Ammoniumnitrathaltige Düngemittel, Typ A3',
      '2070 - Ammoniumnitrathaltige Düngemittel, Typ A4',
      '2071 - Ammoniumnitrathaltige Düngemittel',
      '2072 - Ammoniumnitrathaltige Düngemittel',
      '2073 - Ammoniaklösung in Wasser, relative Dichte kleiner als 0,880 kg/m−3 bei 15 °C, mit mehr als 35 %, aber höchstens 50 % Ammoniak',
      '2074 - Acrylamid, fest',
      '2075 - Chloral, wasserfrei, stabilisiert',
      '2076 - Kresole, flüssig oder fest',
      '2077 - alpha-Naphthylamin',
      '2078 - Toluol-2,4-diisocyanat',
      '2079 - Diethylentriamin',
      '2186 - Chlorwasserstoff, tiefgekühlt, flüssig',
      '2187 - Kohlenstoffdioxid, tiefgekühlt, flüssig',
      '2188 - Diphenylaminochlorarsin',
      '2189 - Dimethyldichlorsilan',
      '2190 - Sauerstoffdifluorid, verdichtet',
      '2191 - Sulfurylfluorid',
      '2192 - Germane (Germaniumwasserstoff)',
      '2193 - Hexafluorethan, verdichtet (Gas als Kältemittel R 116, verdichtet)',
      '2194 - Selenhexafluorid',
      '2195 - Tellurhexafluorid',
      '2196 - Wolframhexafluorid',
      '2197 - Iodwasserstoff, wasserfrei',
      '2198 - Phosphorpentafluorid, verdichtet',
      '2199 - Phosphorwasserstoff (Phosphin)',
      '2200 - Propadien, stabilisiert',
      '2201 - Lachgas (Distickstoffmonoxid), tiefgekühlt, flüssig',
      '2202 - Selenwasserstoff, wasserfrei',
      '2203 - Dimethyldichlorsilane, verdichtet',
      '2204 - Carbonylsulfid',
      '2205 - Adiponitril',
      '2206 - Isocyanate, giftig, N.A.G. oder Isocyanat, Lösung, giftig, N.A.G.',
      '2208 - Calciumhypochlorit, Mischung, trocken, mit mehr als 10 %, aber höchstens 39 % aktivem Chlor',
      '2209 - Formaldehydlösung mit mindestens 25 % Formaldehyd',
      '2210 - Maneb oder Manebzubereitungen mit mindestens 60 Masse-% Maneb',
      '2211 - Schäumbare Polymerkügelchen, entzündbare Dämpfe abgebend',
      '2212 - Asbest, Amphibol (Amosit, Tremolit, Aktinolith, Anthophyllit, Krokydolith Mysorit)',
      '2213 - Paraformaldehyd',
      '2214 - Phthalsäureanhydrid mit mehr als 0,05 % Maleinsäureanhydrid',
      '2215 - Maleinsäureanhydrid, geschmolzen',
      '2216 - Fischmehl (Fischabfälle), stabilisiert',
      '2217 - Ölsaatkuchen mit höchstens 1,5 Masse-% Öl und höchstens 11 Masse-% Feuchtigkeit',
      '2218 - Acrylsäure, stabilisiert',
      '2219 - Allylglycidylether',
      '2222 - Anisol',
      '2224 - Benzonitril',
      '2225 - Benzensulfonylchlorid',
      '2226 - Benzotrichlorid',
      '2227 - n-Butylmethacrylat, stabilisiert',
      '2232 - 2-Chlorethanal',
      '2233 - Chloranisidine',
      '2234 - Chlorbenzotrifluoride',
      '2235 - Chlorbenzylchloride, flüssig',
      '2236 - 3-Chlor-4-methylphenylisocyanat, flüssig',
      '2237 - Chlornitroaniline',
      '2238 - Chlortoluole',
      '2239 - Chlortoluidine, fest',
      '2240 - Chromschwefelsäure',
      '2241 - Cycloheptan',
      '2242 - Cyclohepten',
      '2243 - Cyclohexylacetat',
      '2244 - Cyclopentanol',
      '2245 - Cyclopentanon',
      '2246 - Cyclopenten',
      '2247 - n-Decan',
      '2248 - Di-n-Butylamin',
      '2249 - Dichlordimethylether, symmetrisch',
      '2250 - Dichlorphenylisocyanate',
      '2251 - Bicyclo-(2,2,1)-hepta-2,5-dien, stabilisiert (Norbornan-2,5-dien, stabilisiert)',
      '2252 - 1,2-Dimethoxyethan',
      '2253 - N,N-Dimethylanilin',
      '2254 - Sturmzündhölzer',
      '2256 - Cyclohexen',
      '2257 - Kalium',
      '2258 - 1,2-Propylendiamin',
      '2259 - Triethylentetramin',
      '2260 - Tripropylamin',
      '2261 - Xylenole, fest',
      '2262 - N,N-Dimethylcarbamoylchlorid',
      '2263 - Dimethylcyclohexane',
      '2264 - N,N-Dimethylcyclohexylamin',
      '2265 - N,N-Dimethylformamid',
      '2266 - Dimethyl-n-propylamin',
      '2267 - Dimethylthiophosphorylchlorid',
      '2269 - 3,3′-Iminobispropylamin',
      '2270 - Ethylamin, wässrige Lösung mit mindestens 50 Masse-% und höchstens 70 Masse-% Ethylamin',
      '2271 - Ethylamylketon',
      '2272 - N-Ethylanilin',
      '2273 - 2-Ethylanilin',
      '2274 - N-Ethyl-N-benzylanilin',
      '2275 - 2-Ethylbutanol',
      '2276 - 2-Ethylhexylamin',
      '2277 - Ethylmethacrylat',
      '2278 - n-Hepten',
      '2279 - Hexachlorbutadien',
      '2280 - Hexamethylendiamin, fest',
      '2281 - Hexamethylendiisocyanat',
      '2282 - Hexanole',
      '2283 - Isobutylmethacrylat, stabilisiert',
      '2284 - Isobutyronitril',
      '2285 - Isocyanatobenzotrifluoride',
      '2286 - Pentamethylheptan',
      '2287 - Isoheptene',
      '2288 - Isohexene',
      '2289 - Isophorondiamin',
      '2290 - Isophorondiisocyanat',
      '2291 - Bleiverbindung, löslich, N.A.G.',
      '2293 - 4-Methoxy-4-methylpentan-2-on',
      '2294 - N-Methylanilin',
      '2295 - Methylchloracetat',
      '2296 - Methylcyclohexan',
      '2297 - 2-Methylcyclohexanon',
      '2298 - Methylcyclopentan',
      '2299 - Methyldichloracetat',
      '2300 - 2-Methyl-5-ethylpyridin',
      '2301 - 2-Methylfuran',
      '2302 - 5-Methylhexan-2-on',
      '2303 - Isopropenylbenzen',
      '2304 - Naphthalin, geschmolzen',
      '2305 - Trinitrobenzensulfonsäure',
      '2306 - Nitrobenzotrifluoride, flüssig',
      '2307 - 3-Nitro-4-chlorbenzotrifluorid',
      '2308 - Nitrosylschwefelsäure, flüssig',
      '2309 - Octadiene',
      '2310 - Pentan-2,4-dion',
      '2311 - Phenetidine',
      '2312 - Phenol, geschmolzen',
      '2313 - Picoline',
      '2315 - Polychlorierte Biphenyle, flüssig',
      '2316 - Natriumkupfer(I)-cyanid, fest',
      '2317 - Natriumkupfer(I)-cyanid, Lösung',
      '2318 - Natriumhydrogensulfid mit weniger als 25 % Kristallwasser',
      '2319 - Terpenkohlenwasserstoffe, N.A.G.',
      '2320 - Tetraethylenpentamin',
      '2321 - Trichlorbenzole, flüssig',
      '2322 - Trichlorbuten',
      '2323 - Triethylphosphit',
      '2324 - Triisobutylen',
      '2325 - 1,3,5-Trimethylbenzen',
      '2326 - Trimethylcyclohexylamin',
      '2327 - Trimethylhexamethylendiamine',
      '2328 - Trimethylhexamethylendiisocyanat (und isomere Gemische)',
      '2329 - Trimethylphosphit',
      '2330 - Undecan',
      '2331 - Zinkchlorid, wasserfrei',
      '2332 - Acetaldehydoxim',
      '2333 - Allylacetat',
      '2334 - Allylamin',
      '2335 - Allylethylether',
      '2336 - Allylformiat',
      '2337 - Phenylmercaptan',
      '2338 - Chlorbenzotrifluoride',
      '2339 - 2-Brombutan',
      '2340 - 2-Bromethylethylether',
      '2341 - 1-Brom-3-methylbutan',
      '2342 - Brommethylpropan',
      '2343 - 2-Brompentan',
      '2344 - Brompropan',
      '2345 - 3-Brompropin',
      '2346 - Butandion',
      '2347 - Butylmercaptan',
      '2348 - Acrylsäurebutylester (Butylacrylat), stabilisiert',
      '2350 - Butylmethylether',
      '2351 - Butylnitrit',
      '2352 - Vinyisobutylether, stabilisiert',
      '2353 - Butyrylchlorid',
      '2354 - Chlormethylethylether',
      '2356 - 2-Chlorpropan',
      '2357 - Cyclohexylamin',
      '2358 - Cyclooctatetraen',
      '2359 - Diallylamin',
      '2360 - Diallylether',
      '2361 - Diisobutylamin',
      '2362 - 1,1-Dichlorethan',
      '2363 - Methanthiol (Methylmercaptan)',
      '2364 - n-Propylbenzen',
      '2366 - Diethylcarbonat',
      '2367 - alpha-Methylvaleraldehyd',
      '2368 - α-Pinen',
      '2370 - Hex-1-en',
      '2371 - Isopentene',
      '2372 - 1,2-Di(dimethylamino)ethan',
      '2373 - Diethoxymethan',
      '2374 - 3,3-Diethoxypropen',
      '2375 - Diethylsulfid',
      '2376 - 2,3-Dihydropyran',
      '2377 - 1,1-Dimethoxyethan',
      '2378 - 2-Dimethylaminoacetonitril',
      '2379 - 1,3-Dimethylbutylamin',
      '2380 - Dimethyldiethoxysilan',
      '2381 - Dimethyldisulfid',
      '2382 - Dimethylhydrazin, symmetrisch',
      '2383 - 3,3\'-Iminobispropylamin',
      '2384 - Di-n-propylether',
      '2385 - Ethylisobutyrat',
      '2386 - 1-Ethylpiperidin',
      '2387 - Fluorbenzol',
      '2388 - Fluortoluole',
      '2389 - Furan',
      '2390 - 2-Iodbutan',
      '2391 - Iodmethylpropane',
      '2392 - Iodpropane',
      '2393 - Isobutylformiat',
      '2394 - Isobutylpropionat',
      '2395 - Isobutyrylchlorid',
      '2396 - Methacrylaldehyd, stabilisiert',
      '2397 - 3-Methylbutan-2-on',
      '2398 - Methyl-tert-butylether',
      '2399 - 1-Methylpiperidin',
      '2400 - Methylisovalerat',
      '2401 - Piperidin',
      '2402 - Propanthiole',
      '2403 - Isopropenylacetat',
      '2404 - Propionitril',
      '2405 - Isopropylbutyrat',
      '2406 - Isopropylisobutyrat',
      '2407 - Isopropylchlorformiat',
      '2409 - Isopropylpropionat',
      '2410 - 1,2,3,6-Tetrahydropyridin',
      '2411 - Butyronitril',
      '2412 - Tetrahydrothiophen',
      '2413 - Tetrapropylorthotitanat',
      '2414 - Tetrahydrothiophen',
      '2416 - Trimethylborat',
      '2417 - Carbonylfluorid, verdichtet',
      '2418 - Schwefeltetrafluorid',
      '2419 - Bromtrifluorethylen',
      '2420 - Hexafluoraceton',
      '2421 - Distickstofftrioxid',
      '2422 - Octafluorbut-2-en (Gas als Kältemittel R 1318)',
      '2424 - Octafluorpropan (Gas als Kältemittel R 218)',
      '2426 - Ammoniumnitrat, flüssig, heiße konzentrierte Lösung mit einer Konzentration von mehr als 80 %, aber höchstens 93 %',
      '2427 - Kaliumchlorat, wässrige Lösung',
      '2428 - Natriumchlorat, wässrige Lösung',
      '2429 - Calciumchlorat, wässrige Lösung',
      '2430 - Alkylphenole, fest, N.A.G. (einschließlich C2-C12-Homologe)',
      '2431 - Chloranisidine',
      '2432 - N,N-Diethylanilin',
      '2433 - Chlornitrotoluole, flüssig',
      '2434 - Dibenzyldichlorsilan',
      '2435 - Ethylphenyldichlorsilan',
      '2436 - Thioessigsäure',
      '2437 - Methylphenyldichlorsilan',
      '2438 - Trimethylacetylchlorid',
      '2439 - Natriumhydrogendifluorid',
      '2440 - Zinntetrachloridpentahydrat',
      '2441 - Titantrichlorid, pyrophor oder Titaniumtrichlorid-Mischungen, pyrophor',
      '2442 - Trichloracetylchlorid',
      '2443 - Vanadiumoxytrichlorid',
      '2444 - Vanadiumtetrachlorid',
      '2445 - Lithiumalkyle',
      '2446 - Nitrokresole, fest',
      '2447 - Phosphor, weiß, geschmolzen',
      '2448 - Schwefel, geschmolzen',
      '2451 - Stickstofftrifluorid, verdichtet',
      '2452 - Ethylacetylen, stabilisiert',
      '2453 - Ethylfluorid (Gas als Kältemittel R 161)',
      '2454 - Methylfluorid (Gas als Kältemittel R 41)',
      '2455 - Methylnitrit',
      '2456 - 2-Chlorpropen',
      '2457 - 2,3-Dimethylbutan',
      '2458 - Hexadiene',
      '2459 - 2-Methylbut-1-en',
      '2460 - 2-Methylbut-2-en',
      '2461 - Methylpentadiene',
      '2463 - Lithiumaluminiumhydrid',
      '2464 - Berylliumnitrat',
      '2465 - Dichlorisocyanursäure, trocken oder Dichlorisocyanursäuresalze',
      '2466 - Kaliumsuperoxid',
      '2468 - Trichlorisocyanursäure, trocken',
      '2469 - Zinkbromat',
      '2470 - Phenylacetonitril, flüssig',
      '2471 - Osmiumtetroxid',
      '2473 - Natriumarsanilat',
      '2474 - Thiophosgen',
      '2475 - Vanadiumtrichlorid',
      '2477 - Methylisothiocyanat',
      '2478 - Isocyanate, entzündbar, giftig, N.A.G. oder Isocyanat, Lösung, entzündbar, giftig, N.A.G.',
      '2480 - Methylisocyanat',
      '2481 - Ethylisocyanat',
      '2482 - n-Propylisocyanat',
      '2483 - Isopropylisocyanat',
      '2484 - tert-Butylisocyanat',
      '2485 - n-Butylisocyanat',
      '2486 - Isobutylisocyanat',
      '2487 - 3-Chlor-4-methylphenylisocyanat, flüssig',
      '2488 - Cyclohexylisocyanat',
      '2490 - Dichlorisopropylether',
      '2491 - Ethanolamin oder Ethanolamin, Lösung',
      '2493 - Hexamethylenimin',
      '2495 - Iodpentafluorid',
      '2496 - Propionsäureanhydrid',
      '2498 - 1,2,3,6-Tetrahydrobenzaldehyd',
      '2501 - Tris(1-aziridinyl)phosphinoxid, Lösung',
      '2502 - Valerylchlorid',
      '2503 - Zirconiumtetrachlorid',
      '2504 - Tetrabromethan',
      '2505 - Ammoniumfluorid',
      '2506 - Ammoniumhydrogensulfat',
      '2507 - Hexachloroplatinsäure, fest',
      '2508 - Molybdänpentachlorid',
      '2509 - Kaliumhydrogensulfat',
      '2511 - alpha-Chlorpropionsäure, Lösung oder fest',
      '2512 - Aminophenole (o-, m-, p-)',
      '2513 - Bromacetylbromid',
      '2514 - Brombenzen',
      '2515 - Bromoform',
      '2516 - Tetrabromkohlenstoff',
      '2517 - 1-Chlor-1,1-difluorethan (Gas als Kältemittel R 142b)',
      '2518 - 1,5,9-Cyclododecatrien',
      '2520 - Cyclooctadiene',
      '2521 - Diketen, stabilisiert',
      '2522 - 2-Dimethylaminoethylmethacrylat, stabilisiert',
      '2524 - Ethylorthoformiat',
      '2525 - Ethyloxalat',
      '2526 - Furfurylamin',
      '2527 - Isobutylacrylat, stabilisiert',
      '2528 - Isobutylisobutyrat',
      '2529 - Isobuttersäure',
      '2531 - Methacrylsäure, stabilisiert',
      '2533 - Methyltrichloracetat',
      '2534 - Methylchlorsilan',
      '2535 - 4-Methylmorpholin (N-Methylmorpholin)',
      '2536 - Methyltetrahydrofuran',
      '2538 - Trinitronaphthalin',
      '2541 - Terpinolen',
      '2542 - Tributylamin',
      '2545 - Hafniumpulver, trocken',
      '2546 - Titanpulver, trocken',
      '2547 - Natriumsuperoxid',
      '2548 - Chlorpentafluorid',
      '2552 - Hexafluoracetonhydrat, flüssig',
      '2554 - Methylallylchlorid',
      '2555 - Zellulosenitrat (Nitrocellulose), mit mindestens 25 Masse-% Wasser',
      '2556 - Zellulosenitrat (Nitrocellulose), mit mindestens 25 Masse-% Alkohol und höchstens 12,6 % Stickstoff in der Trockenmasse',
      '2557 - Zellulosenitrat (Nitrocellulose), Mischung mit höchstens 12,6 % Stickstoff in der Trockenmasse, mit oder ohne Plastifizierungsmittel, mit oder ohne Pigment',
      '2558 - Epibromhydrin',
      '2560 - 2-Methylpentan-2-ol',
      '2561 - 3-Methylbut-1-en',
      '2564 - Trichloressigsäure, Lösung',
      '2565 - Dicyclohexylamin',
      '2567 - Natriumpentachlorphenolat',
      '2570 - Cadmiumverbindung',
      '2571 - Alkylschwefelsäuren',
      '2572 - Phenylhydrazin',
      '2573 - Thalliumchlorat',
      '2574 - Tricresylphosphat mit mehr als 3 % ortho-Isomer',
      '2576 - Phosphoroxybromid, geschmolzen',
      '2577 - Phenylacetylchlorid',
      '2578 - Phosphortrioxid',
      '2579 - Piperazin',
      '2580 - Aluminiumbromid, Lösung',
      '2581 - Aluminiumchlorid, Lösung',
      '2582 - Eisenchlorid, Lösung (Eisen(II)-chlorid und Eisen(III)-chlorid)',
      '2583 - Alkylsulfonsäuren, fest oder Arylsulfonsäuren, fest, mit mehr als 5 % freier Schwefelsäure',
      '2584 - Alkylsulfonsäuren, flüssig oder Arylsulfonsäuren, flüssig, mit mehr als 5 % freier Schwefelsäure',
      '2585 - Alkylsulfonsäure, fest oder Arylsulfonsäuren, fest, mit höchstens 5 % freier Schwefelsäure',
      '2586 - Alkylsulfonsäuren, flüssig oder Arylsulfonsäuren, flüssig, mit höchstens 5 % freier Schwefelsäure',
      '2587 - Benzochinon',
      '2588 - Pestizid, fest, giftig',
      '2589 - Vinylchloracetat, N.A.G.',
      '2590 - Asbest, Chrysotil',
      '2591 - Xenon, tiefgekühlt, flüssig',
      '2599 - Chlortrifluormethan und Trifluormethan, azeotropes Gemisch mit rund 60 % Chlortrifluormethan (Gas als Kältemittel R 503)',
      '2600 - Kohlenstoffmonoxid und Wasserstoff, Gemisch, verdichtet',
      '2601 - Octafluorcyclobutan (Gas als Kältemittel RC 318)',
      '2602 - Dichlordifluormethan und 1,1-Difluorethan, azeotropes Gemisch mit rund 74 % Dichlordifluormethan (Gas als Kältemittel R 500)',
      '2603 - Cycloheptatrien',
      '2604 - Bortrifluoriddiethyletherat',
      '2605 - Methoxymethylisocyanat',
      '2606 - Methylorthosilicat',
      '2607 - Propenal (Acrolein), dimer, stabilisiert',
      '2608 - Nitropropane',
      '2609 - Triallylborat',
      '2610 - Triallylamin',
      '2611 - 1-Chlorpropan-2-ol',
      '2612 - Methylpropylether',
      '2614 - Methylallylalkohol',
      '2615 - Ethylpropylether',
      '2616 - Triisopropylborat',
      '2617 - Methylcyclohexanole, entzündbar',
      '2618 - Vinyltoluole, stabilisiert',
      '2619 - Benzyldimethylamin',
      '2620 - Amylbutyrate',
      '2621 - Acetylmethylcarbinol',
      '2622 - Glycidaldehyd',
      '2623 - Feueranzünder (fest), mit entzündbarem, flüssigem Stoff getränkt',
      '2624 - Magnesiumsilicid',
      '2626 - Chlorsäure, wässrige Lösung mit höchstens 10 % Säure',
      '2627 - Nitrite, anorganische, N.A.G.',
      '2628 - Kaliumfluoracetat',
      '2629 - Natriumfluoracetat',
      '2630 - Selenate oder Selenite',
      '2642 - Fluoressigsäure',
      '2643 - Methylbromacetat',
      '2644 - Methyliodid',
      '2645 - Phenacylbromid',
      '2646 - Hexachlorcyclopentadien',
      '2647 - Malonitril',
      '2648 - 1,2-Dibrombutan-3-on',
      '2649 - 1,3-Dichloraceton',
      '2650 - 1,1-Dichlor-1-nitroethan',
      '2651 - 4,4′-Diaminodiphenylmethan',
      '2653 - Benzyliodid',
      '2655 - Kaliumfluorsilicat',
      '2656 - Chinolin',
      '2657 - Selendisulfid',
      '2659 - Natriumchloracetat',
      '2660 - Nitrotoluidine (mono)',
      '2661 - Hexachloraceton',
      '2662 - Hydrochinon',
      '2664 - Dibrommethan',
      '2667 - Butyltoluole',
      '2668 - Chloracetonitril',
      '2669 - Chlorkresole, Lösung',
      '2670 - Cyanurchlorid',
      '2671 - Aminopyridine (o-, m-, p-)',
      '2672 - Ammoniaklösung in Wasser, relative Dichte zwischen 0,880 und 0,957 bei 15 °C, mit mehr als 10 %, aber höchstens 35 % Ammoniak',
      '2673 - 2-Amino-4-chlorphenol',
      '2674 - Natriumfluorosilicat',
      '2676 - Stiban (Antimonwasserstoff)',
      '2677 - Rubidiumhydroxidlösung',
      '2678 - Rubidiumhydroxid',
      '2679 - Lithiumhydroxidlösung',
      '2680 - Lithiumhydroxidmonohydrat',
      '2681 - Caesiumhydroxidlösung',
      '2682 - Caesiumhydroxid',
      '2683 - Ammoniumsulfid, Lösung',
      '2684 - 3-(Diethylamino)-propylamin',
      '2685 - N,N-Diethylethylendiamin',
      '2686 - 2-Diethylaminoethanol',
      '2687 - Dicyclohexylammoniumnitrit',
      '2688 - 1-Brom-3-chlorpropan',
      '2689 - Glycerol-alpha-monochlorhydrin',
      '2690 - N-(n-Butyl)imidazol',
      '2691 - Phosphorpentabromid',
      '2692 - Bortribromid',
      '2693 - Hydrogensulfite, wässrige Lösung',
      '2698 - Tetrahydrophthalsäureanhydride mit mehr als 0,05 % Maleinsäureanhydrid',
      '2699 - Trifluoressigsäure',
      '2705 - 1-Pentol',
      '2707 - Dimethyldioxane',
      '2709 - Butylbenzole',
      '2710 - Dipropylketon',
      '2713 - Acridin',
      '2714 - Zinkresinat',
      '2715 - Aluminiumresinat',
      '2716 - Butin-1,4-diol',
      '2717 - Campher, synthetisch',
      '2719 - Bariumbromat',
      '2720 - Chromnitrat',
      '2721 - Kupferchlorat',
      '2722 - Lithiumnitrat',
      '2723 - Magnesiumchlorat',
      '2724 - Mangannitrat',
      '2725 - Nickelnitrat',
      '2726 - Nickelnitrit',
      '2727 - Thalliumnitrat',
      '2728 - Zirconiumnitrat',
      '2729 - Hexachlorbenzen',
      '2730 - Nitroanisole, flüssig',
      '2732 - Nitrobrombenzole, flüssig',
      '2733 - Amine, entzündbar, ätzend, N.A.G. oder Polyamine, entzündbar, ätzend, N.A.G.',
      '2734 - Amine, flüssig, ätzend, entzündbar, N.A.G. oder Polyamine, flüssig, ätzend, entzündbar, N.A.G.',
      '2735 - Amine, flüssig, ätzend, N.A.G. oder Polyamine, flüssig, ätzend, N.A.G.',
      '2738 - N-Butylanilin',
      '2739 - Buttersäureanhydrid',
      '2740 - n-Propylchlorformiat',
      '2741 - Bariumhypochlorit mit mehr als 22 % aktivem Chlor',
      '2742 - Chlorformiate, giftig, ätzend, entzündbar, N.A.G.',
      '2743 - n-Butylchlorformiat',
      '2744 - Cyclobutylchlorformiat',
      '2745 - Chlormethylchlorformiat',
      '2746 - Phenylchlorformiat',
      '2747 - tert-Butylcyclohexylchlorformiat',
      '2748 - 2-Ethylhexylchlorformiat',
      '2749 - Tetramethylsilan',
      '2750 - 1,3-Dichlorpropan-2-ol',
      '2751 - Diethylthiophosphorylchlorid',
      '2752 - 1,2-Epoxy-3-ethoxypropan',
      '2753 - N-Ethyl-N-benzyltoluidine, flüssig',
      '2754 - N-Ethyltoluidine',
      '2757 - Carbamat-Pestizid, fest, giftig',
      '2758 - Carbamat-Pestizid, flüssig, entzündbar, giftig, Flammpunkt unter 23 °C',
      '2759 - Arsenhaltiges Pestizid, fest, giftig',
      '2760 - Arsenhaltiges Pestizid, flüssig, entzündbar, giftig, Flammpunkt unter 23 °C',
      '2761 - Organochlor-Pestizid, fest, giftig',
      '2762 - Organochlor-Pestizid, flüssig, entzündbar, giftig, Flammpunkt unter 23 °C',
      '2763 - Triazin-Pestizid, fest, giftig',
      '2764 - Triazin-Pestizid, flüssig, entzündbar, giftig, Flammpunkt unter 23 °C',
      '2771 - Thiocarbamat-Pestizid, fest, giftig',
      '2772 - Thiocarbamat-Pestizid, flüssig, entzündbar, giftig, Flammpunkt unter 23 °C',
      '2775 - Kupferhaltiges Pestizid, fest, giftig',
      '2776 - Kupferhaltiges Pestizid, flüssig, entzündbar, giftig, Flammpunkt unter 23 °C',
      '2777 - Quecksilberhaltiges Pestizid, fest, giftig',
      '2778 - Quecksilberhaltiges Pestizid, flüssig, entzündbar, giftig, Flammpunkt unter 23 °C',
      '2779 - Substituiertes Nitrophenol-Pestizid, fest, giftig',
      '2780 - Substituiertes Nitrophenol-Pestizid, flüssig, entzündbar, giftig, Flammpunkt unter 23 °C',
      '2781 - Bipyridilium-Pestizid, fest, giftig',
      '2782 - Bipyridilium-Pestizid, flüssig, entzündbar, giftig, Flammpunkt unter 23 °C',
      '2783 - Organophosphor-Pestizid, fest, giftig',
      '2784 - Organophosphor-Pestizid, flüssig, entzündbar, giftig, Flammpunkt unter 23 °C',
      '2785 - Thiapentan-4-al',
      '2786 - Organozinn-Pestizid, fest, giftig',
      '2787 - Organozinn-Pestizid, flüssig, entzündbar, giftig, Flammpunkt unter 23 °C',
      '2788 - Organische Zinnverbindung, flüssig, N.A.G.',
      '2789 - Eisessig oder Essigsäure, Lösung mit mehr als 80 Masse-% Säure',
      '2790 - Essigsäure, Lösung mit mindestens 50 Masse-% und höchstens 80 Masse-% Säure',
      '2793 - Metallisches Eisen als Bohrspäne, Frässpäne, Drehspäne, Abfälle in selbsterhitzungsfähiger Form',
      '2794 - Batterien (Akkumulatoren), nass, gefüllt mit Säure, elektrische Sammler',
      '2795 - Batterien (Akkumulatoren), nass, gefüllt mit Alkalien, elektrische Sammler',
      '2796 - Schwefelsäure mit höchstens 51 % Säure oder Batterieflüssigkeit, sauer',
      '2797 - Batterieflüssigkeit, alkalisch',
      '2798 - Phenylphosphordichlorid',
      '2799 - Phenylphosphorthiodichlorid',
      '2800 - Batterien (Akkumulatoren), nass, auslaufsicher, elektrische Sammler',
      '2801 - Farbstoff, flüssig, ätzend, N.A.G. oder Farbstoffzwischenprodukt, flüssig, ätzend, N.A.G.',
      '2802 - Kupferchlorid (Kupfer(I)-chlorid und Kupfer(II)-chlorid)',
      '2803 - Gallium',
      '2805 - Lithiumhydrid geschmolzen und erstarrt',
      '2806 - Lithiumnitrid',
      '2807 - Magnetisierte Stoffe',
      '2809 - Quecksilber',
      '2810 - Giftiger, organischer, flüssiger Stoff, N.A.G.',
      '2811 - Giftiger, organischer, fester Stoff, N.A.G.',
      '2812 - Natriumaluminat, fest',
      '2813 - Mit Wasser reagierender, fester Stoff, N.A.G.',
      '2814 - Ansteckungsgefährlicher Stoff, gefährlich für Menschen',
      '2815 - N-Aminoethylpiperazin',
      '2817 - Ammoniumhydrogendifluorid, Lösung',
      '2818 - Ammoniumpolysulfid, Lösung',
      '2819 - Amylphosphat',
      '2820 - Isobuttersäure',
      '2821 - Dinitrophenol, Lösung',
      '2822 - 2-Chlorpyridin',
      '2823 - Crotonsäure',
      '2826 - Ethylchlorthioformiat',
      '2829 - Capronsäure',
      '2830 - Lithiumferrosilicid',
      '2831 - 1,1,1-Trichlorethan',
      '2834 - Phosphorige Säure',
      '2835 - Natriumaluminiumhydrid',
      '2837 - Hydrogensulfate, wässrige Lösung',
      '2838 - Vinylbutyrat, stabilisiert',
      '2839 - Aldol (3-Hydroxybutyraldehyd)',
      '2840 - Butyraldoxim',
      '2841 - Di-n-amylamin',
      '2842 - 1,1-Dichlor-1-nitroethan',
      '2844 - Calciummangansilicium',
      '2845 - Pyrophorer organischer flüssiger Stoff',
      '2846 - Pyrophorer organischer fester Stoff',
      '2849 - 3-Chlorpropan-1-ol',
      '2850 - Tetrapropylen (Propylentetramer)',
      '2851 - Bortrifluoriddihydrat',
      '2852 - Dipikrylsulfid, angefeuchtet mit mindestens 10 Masse-% Wasser',
      '2853 - Magnesiumfluorosilicat',
      '2854 - Ammoniumfluorosilicat',
      '2855 - Zinkfluorosilicat',
      '2856 - Fluorosilicate, N.A.G.',
      '2857 - Kältemaschinen mit nicht entzündbarem und nicht giftigem verflüssigten Gas oder Ammoniaklösung',
      '2858 - Zirconium, trocken, gerollter Draht, fertige Bleche, Streifen (dünner als 254 µm, aber nicht dünner als 18 µm)',
      '2859 - Ammoniummetavanadat',
      '2861 - Ammoniumpolyvanadat',
      '2862 - Vanadiumpentoxid, nicht geschmolzen',
      '2863 - Natriumammoniumvanadat',
      '2864 - Kaliummetavanadat',
      '2865 - Hydroxylaminsulfat',
      '2869 - Titantrichlorid, pyrophor oder Titantrichlorid-Mischungen, pyrophor',
      '2870 - Aluminiumborhydrid',
      '2871 - Antimonpulver',
      '2872 - Dibromchlorpropane',
      '2873 - Dibutylaminoethanol',
      '2874 - Furfurylalkohol',
      '2875 - Hexachlorophen',
      '2876 - Dinitroresorcinol, trocken oder angefeuchtet mit weniger als 15 Masse-% Wasser',
      '2878 - Titanschwammgranulate oder Titanschwammpulver',
      '2879 - Selenoxychlorid',
      '2880 - Calciumhypochlorit, hydratisiert oder Calciumhypochlorit, hydratisierte Mischung mit mindestens 5,5 %, aber höchstens 16 % Wasser',
      '2881 - Metallkatalysator, trocken',
      '2900 - Ansteckungsgefährlicher Stoff, nur gefährlich für Tiere',
      '2901 - Bromchlorid',
      '2902 - Pestizid, flüssig, giftig, N.A.G.',
      '2903 - Pestizid, flüssig, giftig, entzündbar, N.A.G., mit einem Flammpunkt von 23 °C oder darüber',
      '2904 - Chlorphenolate, flüssig oder Phenolate, flüssig',
      '2905 - Chlorphenolate, fest oder Phenolate, fest',
      '2907 - Isosorbiddinitrat, Mischung mit mindestens 60 % Lactose, Mannose, Stärke oder Calciumhydrogenphosphat',
      '2908 - Radioaktive Stoffe, freigestelltes Versandstück – leere Verpackung',
      '2909 - Radioaktive Stoffe, freigestelltes Versandstück – Fabrikate aus natürlichem Uran oder aus abgereichertem Uran oder aus natürlichem Thorium',
      '2910 - Radioaktive Stoffe, freigestelltes Versandstück – begrenzte Stoffmenge',
      '2911 - Radioaktive Stoffe, freigestelltes Versandstück – Instrumente oder Fabrikate',
      '2912 - Radioaktive Stoffe mit geringer spezifischer Aktivität (LSA-I), nicht spaltbar oder spaltbar, freigestellt',
      '2913 - Radioaktive Stoffe, oberflächenkontaminierte Gegenstände (SCO-I, SCO-II oder SCO-III), nicht spaltbar oder spaltbar, freigestellt',
      '2915 - Radioaktive Stoffe, Typ A-Versandstück, nicht in besonderer Form, nicht spaltbar oder spaltbar, freigestellt',
      '2916 - Radioaktive Stoffe, Typ B(U)-Versandstück, nicht spaltbar oder spaltbar, freigestellt',
      '2917 - Radioaktive Stoffe, Typ B(M)-Versandstück, nicht spaltbar oder spaltbar, freigestellt',
      '2919 - Radioaktive Stoffe, unter Sondervereinbarung befördert, nicht spaltbar oder spaltbar, freigestellt',
      '2920 - Ätzender, flüssiger Stoff, entzündbar, N.A.G.',
      '2921 - Ätzender, fester Stoff, entzündbar, N.A.G.',
      '2922 - Ätzender, flüssiger Stoff, giftig, N.A.G.',
      '2923 - Ätzender, fester Stoff, giftig, N.A.G.',
      '2924 - Entzündbarer, flüssiger Stoff, ätzend, N.A.G.',
      '2925 - Entzündbarer, organischer, fester Stoff, ätzend, N.A.G.',
      '2926 - Entzündbarer, organischer, fester Stoff, giftig, N.A.G.',
      '2927 - Giftiger, organischer, flüssiger Stoff, ätzend, N.A.G.',
      '2928 - Giftiger, organischer, fester Stoff, ätzend, N.A.G.',
      '2929 - Giftiger, organischer, flüssiger Stoff, entzündbar, N.A.G.',
      '2930 - Giftiger, organischer, fester Stoff, entzündbar, N.A.G.',
      '2931 - Vanadylsulfat',
      '2933 - Methyl-2-chlorpropionat',
      '2934 - Isopropyl-2-chlorpropionat',
      '2935 - Methyl-2-chlorpropionat',
      '2936 - Thiomilchsäure',
      '2937 - alpha-Methylbenzylalkohol, flüssig',
      '2940 - 9-Phosphabicyclononane (Cyclooctadienphosphine)',
      '2941 - Fluoraniline',
      '2942 - 2-Trifluormethylanilin',
      '2943 - Tetrahydrofurfurylamin',
      '2945 - N-Methylbutylamin',
      '2946 - 2-Amino-5-diethylaminopentan',
      '2947 - Isopropylchloracetat',
      '2948 - 3-Trifluormethylanilin',
      '2949 - Natriumhydrogensulfid, hydratisiert, mit mindestens 25 % Kristallwasser',
      '2950 - Magnesiumgranulate, überzogen, mit einer Teilchengröße von mindestens 149 µm',
      '2956 - 5-tert-Buytl-2,4,6-trinitro-m-xylen (Xylenmoschus)',
      '2965 - Bortrifluoriddimethyletherat',
      '2966 - Thioglycolsäure',
      '2967 - Sulfaminsäure',
      '2968 - Maneb, stabilisiert oder Manebzubereitungen, stabilisiert gegen Selbsterhitzung',
      '2969 - Rizinussaat oder Rizinusmehl oder Rizinussaatkuchen oder Rizinusflocken',
      '2977 - Radioaktive Stoffe, Uranhexafluorid, spaltbar',
      '2978 - Radioaktive Stoffe, Uranhexafluorid, nicht spaltbar oder spaltbar, freigestellt',
      '2983 - Ethylenoxid und Propylenoxid, Mischung mit höchstens 30 % Ethylenoxid',
      '2984 - Wasserstoffperoxid, wässrige Lösung mit mindestens 8 %, aber weniger als 20 % Wasserstoffperoxid (Stabilisierung nach Bedarf)',
      '2985 - Chlorsilane, entzündbar, ätzend, N.A.G.',
      '2986 - Chlorsilane, ätzend, entzündbar, N.A.G.',
      '2987 - Chlorsilane, ätzend, N.A.G.',
      '2988 - Chlorsilane, mit Wasser reagierend, entzündbar, ätzend, N.A.G.',
      '2989 - Bleiphosphit, zweibasig',
      '2990 - Rettungsmittel, selbstaufblasend',
      '2991 - Carbamat-Pestizid, flüssig, giftig, entzündbar, mit einem Flammpunkt von 23 °C oder darüber',
      '2992 - Carbamat-Pestizid, flüssig, giftig, entzündbar',
      '2993 - Arsenhaltiges Pestizid, flüssig, giftig, entzündbar, mit einem Flammpunkt von 23 °C oder darüber',
      '2994 - Arsenhaltiges Pestizid, flüssig, giftig, entzündbar',
      '2995 - Organochlor-Pestizid, flüssig, giftig, entzündbar, mit einem Flammpunkt von 23 °C oder darüber',
      '2996 - Organochlor-Pestizid, flüssig, giftig, entzündbar',
      '2997 - Triazin-Pestizid, flüssig, giftig, entzündbar, mit einem Flammpunkt von 23 °C oder darüber',
      '2998 - Triazin-Pestizid, flüssig, giftig, entzündbar',
      '3005 - Thiocarbamit-Pestizid, flüssig, giftig, entzündbar, mit einem Flammpunkt von 23 °C oder darüber',
      '3006 - Thiocarbamit-Pestizid, flüssig, giftig, entzündbar',
      '3009 - Kupferhaltiges Pestizid, flüssig, giftig, entzündbar, mit einem Flammpunkt von 23 °C oder darüber',
      '3010 - Kupferhaltiges Pestizid, flüssig, giftig, entzündbar',
      '3011 - Quecksilberhaltiges Pestizid, flüssig, giftig, entzündbar, mit einem Flammpunkt von 23 °C oder darüber',
      '3012 - Quecksilberhaltiges Pestizid, flüssig, giftig, entzündbar',
      '3013 - Substituiertes Nitrophenol-Pestizid, flüssig, giftig, entzündbar, mit einem Flammpunkt von 23 °C oder darüber',
      '3014 - Substituiertes Nitrophenol-Pestizid, flüssig, giftig, entzündbar',
      '3015 - Bipyridilium-Pestizid, flüssig, giftig, entzündbar, mit einem Flammpunkt von 23 °C oder darüber',
      '3016 - Bipyridilium-Pestizid, flüssig, giftig, entzündbar',
      '3017 - Organophosphor-Pestizid, flüssig, giftig, entzündbar, mit einem Flammpunkt von 23 °C oder darüber',
      '3018 - Organophosphor-Pestizid, flüssig, giftig, entzündbar',
      '3019 - Organozinn-Pestizid, flüssig, giftig, entzündbar, mit einem Flammpunkt von 23 °C oder darüber',
      '3020 - Organozinn-Pestizid, flüssig, giftig, entzündbar',
      '3021 - Pestizid, flüssig, entzündbar, giftig, Flammpunkt unter 23 °C',
      '3022 - 1,2-Butylenoxid, stabilisiert',
      '3023 - 2-Methyl-2-Heptanthiol',
      '3024 - Cumarin-Pestizid, flüssig, entzündbar, giftig, Flammpunkt unter 23 °C',
      '3025 - Cumarin-Pestizid, flüssig, giftig, entzündbar, mit einem Flammpunkt von 23 °C oder darüber',
      '3026 - Cumarin-Pestizid, flüssig, giftig, entzündbar',
      '3027 - Cumarin-Pestizid, fest, giftig',
      '3028 - Batterien (Akkulumatoren), trocken, Kaliumhydroxid, fest, enthaltend, elektrische Sammler',
      '3048 - Aluminiumphosphid-Pestizid',
      '3054 - Cyclohexylmercaptan',
      '3055 - 2-(2-Aminoethoxy)-ethanol',
      '3056 - n-Heptaldehyd',
      '3057 - Trifluoracetylchlorid',
      '3064 - Nitroglycerol, Lösung in Alkohol, mit mehr als 1 %, aber höchstens 5 % Nitroglycerol',
      '3065 - Alkoholische Getränke mit mehr als 70 Vol.-% Alkohol',
      '3066 - Farbe (einschließlich Farbe, Lack, Emaille, Beize, Schellack, Firnis, Politur, flüssiger Füllstoff und flüssige Lackgrundlage) oder Farbzubehörstoffe (inklusive Farbverdünnung und -lösemittel)',
      '3070 - Ethylenoxid und Dichlordifluormethan, Gemisch, mit höchstens 12,5 % Ethylenoxid',
      '3071 - Mercaptane, flüssig, giftig, entzündbar, N.A.G. oder Mercaptane-Mischung, flüssig, giftig, entzündbar, N.A.G.',
      '3072 - Rettungsmittel, nicht selbstaufblasend, gefährliche Güter als Ausrüstung enthaltend',
      '3073 - Vinylpyridine, stabilisiert',
      '3076 - Aluminiumalkylhydride',
      '3077 - Umweltgefährdender Stoff, fest, N.A.G.',
      '3078 - Cer, Späne oder Grieß',
      '3079 - Methacrylnitril, stabilisiert',
      '3080 - Isocyanate, giftig, entzündbar, N.A.G. oder Isocyanate, Lösung, giftig, entzündbar, N.A.G.',
      '3082 - Umweltgefährdender Stoff, flüssig, N.A.G.',
      '3083 - Perchlorylfluorid',
      '3084 - Ätzender, fester Stoff, entzündend (oxidierend) wirkend, N.A.G.',
      '3085 - Entzündend (oxidierend) wirkender, fester Stoff, ätzend, N.A.G.',
      '3086 - Giftiger, fester Stoff, entzündend (oxidierend) wirkend, N.A.G.',
      '3087 - Entzündend (oxidierend) wirkender, fester Stoff, giftig, N.A.G.',
      '3088 - Selbsterhitzungsfähiger organischer fester Stoff, N.A.G.',
      '3089 - Entzündbares Metallpulver, N.A.G.',
      '3090 - Lithium-Metall-Batterien (einschließlich Batterien aus Lithiumlegierungen)',
      '3091 - Lithium-Metall-Batterien in Ausrüstungen oder Lithium-Metall-Batterien, mit Ausrüstungen verpackt (einschließlich Batterien aus Lithiumlegierungen)',
      '3092 - 1-Methoxy-2-propanol',
      '3093 - Ätzender, flüssiger Stoff, entzündend (oxidierend) wirkend, N.A.G.',
      '3094 - Ätzender, flüssiger Stoff, mit Wasser reagierend, N.A.G.',
      '3095 - Ätzender, fester Stoff, selbsterhitzungsfähig, N.A.G.',
      '3096 - Ätzender, fester Stoff, mit Wasser reagierend, N.A.G.',
      '3097 - Entzündbarer, fester Stoff, entzündend (oxidierend) wirkend, N.A.G.',
      '3098 - Entzündend (oxidierend) wirkender, flüssiger Stoff, ätzend, N.A.G.',
      '3099 - Entzündend (oxidierend) wirkender, flüssiger Stoff, giftig, N.A.G.',
      '3100 - Entzündend (oxidierend) wirkender, fester Stoff, selbserhitzungsfähig, N.A.G.',
      '3101 - Organisches Peroxid, Typ B, flüssig',
      '3102 - Organisches Peroxid, Typ B, fest',
      '3103 - Organisches Peroxid, Typ C, flüssig',
      '3104 - Organisches Peroxid, Typ C, fest',
      '3105 - Organisches Peroxid, Typ D, flüssig',
      '3106 - Organisches Peroxid, Typ D, fest',
      '3107 - Organisches Peroxid, Typ E, flüssig',
      '3108 - Organisches Peroxid, Typ E, fest',
      '3109 - Organisches Peroxid, Typ F, flüssig',
      '3110 - Organisches Peroxid, Typ F, fest',
      '3111 - Organisches Peroxid, Typ B, flüssig, temperaturkontrolliert',
      '3112 - Organisches Peroxid, Typ B, fest, temperaturkontrolliert',
      '3113 - Organisches Peroxid, Typ C, flüssig, temperaturkontrolliert',
      '3114 - Organisches Peroxid, Typ C, fest, temperaturkontrolliert',
      '3115 - Organisches Peroxid, Typ D, flüssig, temperaturkontrolliert',
      '3116 - Organisches Peroxid, Typ D, fest, temperaturkontrolliert',
      '3117 - Organisches Peroxid, Typ E, flüssig, temperaturkontrolliert',
      '3118 - Organisches Peroxid, Typ E, fest, temperaturkontrolliert',
      '3119 - Organisches Peroxid, Typ F, flüssig, temperaturkontrolliert',
      '3120 - Organisches Peroxid, Typ F, fest, temperaturkontrolliert',
      '3121 - Entzündend (oxidierend) wirkender, fester Stoff, mit Wasser reagierend',
      '3122 - Giftiger, flüssiger Stoff, entzündend (oxidierend) wirkend, N.A.G.',
      '3123 - Giftiger, flüssiger Stoff, mit Wasser reagierend, N.A.G.',
      '3124 - Giftiger, fester Stoff, selbsterhitzungsfähig, N.A.G.',
      '3125 - Giftiger, fester Stoff, mit Wasser reagierend, N.A.G.',
      '3126 - Selbsterhitzungsfähiger, organischer, fester Stoff, ätzend, N.A.G.',
      '3127 - Selbsterhitzungsfähiger, fester Stoff, entzündend (oxidierend) wirkend',
      '3128 - Selbsterhitzungsfähiger, organischer, fester Stoff, giftig, N.A.G.',
      '3129 - Mit Wasser reagierender, flüssiger Stoff, ätzend, N.A.G.',
      '3130 - Mit Wasser reagierender, flüssiger Stoff, giftig, N.A.G.',
      '3131 - Mit Wasser reagierender, fester Stoff, ätzend, N.A.G.',
      '3132 - Mit Wasser reagierender, fester Stoff, entzündbar, N.A.G.',
      '3133 - Mit Wasser reagierender, fester Stoff, entzündend (oxidierend) wirkend, N.A.G.',
      '3134 - Mit Wasser reagierender, fester Stoff, giftig, N.A.G.',
      '3135 - Mit Wasser reagierender, fester Stoff, selbsterhitzungsfähig, N.A.G.',
      '3136 - Trifluormethan, tiefgekühlt, flüssig',
      '3137 - Entzündend (oxidierend) wirkender, fester Stoff, entzündbar, N.A.G.',
      '3138 - Ethylen, Acetylen und Propylen, Gemisch, tiefgekühlt, flüssig, mit mindestens 71,5 % Ethylen, höchstens 22,5 % Acetylen und höchstens 6 % Propylen',
      '3139 - Entzündend (oxidierend) wirkender flüssiger Stoff, N.A.G.',
      '3140 - Alkaloide, flüssig, Alkaloidsalze, flüssig, N.A.G.',
      '3141 - Anorganische Antimonverbindung, flüssig, N.A.G.',
      '3142 - Desinfektionsmittel, flüssig, giftig, N.A.G.',
      '3143 - Farbe, fest, giftig, N.A.G. oder Farbstoffzwischenprodukt, fest, giftig, N.A.G.',
      '3144 - Nicotinverbindung, flüssig, N.A.G. oder Nicotinzubereitung, flüssig, N.A.G.',
      '3145 - Alkylphenole, flüssig, N.A.G. einschließlich C2-C12-Homologe',
      '3146 - Organische Zinnverbindung, fest, N.A.G.',
      '3147 - Farbstoff, fest, ätzend, N.A.G. oder Farbstoffzwischenprodukt, fest, ätzend, N.A.G.',
      '3148 - Mit Wasser reagierender, flüssiger Stoff, N.A.G.',
      '3149 - Wasserstoffperoxid und Peressigsäure, Mischung, stabilisiert mit Säure(n), Wasser und höchstens 5 % Peressigsäure',
      '3150 - Geräte, klein, mit Kohlenwasserstoffgas, mit Entnahmeeinrichtung oder Kohlenwasserstoffgasnachfüllpatronen für kleine Geräte, mit Entnahmeeinrichtung',
      '3151 - Polyhalogenierte Biphenyle, flüssig oder polyhalogenierte Terphenyle, flüssig',
      '3152 - Polyhalogenierte Biphenyle, fest oder polyhalogenierte Terphenyle, fest',
      '3153 - Perfluor(methylvinyl)ether',
      '3154 - Perfluor(ethylvinyl)ether',
      '3155 - Natriumpentachlorphenolat',
      '3156 - Verdichtetes Gas, oxidierend, N.A.G.',
      '3157 - Verflüssigtes Gas, oxidierend, N.A.G.',
      '3158 - Gas, tiefgekühlt, flüssig, N.A.G.',
      '3159 - 1,1,1,2-Tetrafluorethan (Gas als Kältemittel R 134a)',
      '3160 - Verflüssigtes Gas, giftig, entzündbar, N.A.G.',
      '3161 - Verflüssigtes Gas, entzündbar, N.A.G.',
      '3162 - Verflüssigtes Gas, giftig, N.A.G.',
      '3163 - Verflüssigtes Gas, N.A.G.',
      '3164 - Gegenstände unter pneumatischem Druck oder Gegenstände unter hydraulischem Druck (mit nicht entzündbaren Gasen)',
      '3165 - Kraftstofftank für hydraulisches Aggregat für Flugzeuge (mit einer Mischung von wasserfreiem Hydrazin und Methylhydrazin) (Kraftstoff M86)',
      '3166 - Fahrzeug mit Antrieb durch entzündbares Gas oder Fahrzeug mit Antrieb durch entzündbare Flüssigkeit oder Brennstoffzellenfahrzeug mit Antrieb durch entzündbares Gas oder Brennstoffzellenfahrzeug mit Antrieb durch entzündbare Flüssigkeit',
      '3167 - Gasprobe, nicht unter Druck stehend, entzündbar, N.A.G., nicht tiefgekühlt flüssig',
      '3168 - Gasprobe, nicht unter Druck stehend, giftig, entzündbar, N.A.G., nicht tiefgekühlt flüssig',
      '3169 - Gasprobe, nicht unter Druck stehend, giftig, N.A.G., nicht tiefgekühlt flüssig',
      '3170 - Nebenprodukte der Aluminiumherstellung oder Nebenprodukte der Aluminiumschmelzung',
      '3171 - Batteriebetriebenes Fahrzeug oder Batteriebetriebenes Gerät',
      '3172 - Toxine, gewonnen aus lebenden Organismen, flüssig, N.A.G.',
      '3174 - Titaniumdisulfid',
      '3175 - Feste Stoffe oder Gemische aus festen Stoffen (wie Präparate, Zubereitungen und Abfälle), die entzündbare flüssige Stoffe mit einem Flammpunkt von höchstens 60 °C enthalten, N.A.G.',
      '3176 - Entzündbarer, organischer, fester Stoff, N.A.G., in geschmolzenem Zustand',
      '3178 - Entzündbarer, anorganischer, fester Stoff, N.A.G.',
      '3179 - Entzündbarer, anorganischer, fester Stoff, giftig, N.A.G.',
      '3180 - Entzündbarer, anorganischer, fester Stoff, ätzend, N.A.G.',
      '3181 - Entzündbare Metallsalze organischer Verbindungen, N.A.G.',
      '3182 - Entzündbare Metallhydride, N.A.G.',
      '3183 - Selbsterhitzungsfähiger, organischer, flüssiger Stoff, N.A.G.',
      '3184 - Selbsterhitzungsfähiger, organischer, flüssiger Stoff, giftig, N.A.G.',
      '3185 - Selbsterhitzungsfähiger, organischer, flüssiger Stoff, ätzend, N.A.G.',
      '3186 - Selbsterhitzungsfähiger, anorganischer, flüssiger Stoff, N.A.G.',
      '3187 - Selbsterhitzungsfähiger, anorganischer, flüssiger Stoff, giftig, N.A.G.',
      '3188 - Selbsterhitzungsfähiger, anorganischer, flüssiger Stoff, ätzend, N.A.G.',
      '3189 - Selbsterhitzungsfähiges Metallpulver, N.A.G.',
      '3190 - Selbsterhitzungsfähiger, anorganischer, fester Stoff, N.A.G.',
      '3191 - Selbsterhitzungsfähiger, anorganischer, fester Stoff, giftig, N.A.G.',
      '3192 - Selbsterhitzungsfähiger, anorganischer, fester Stoff, ätzend, N.A.G.',
      '3194 - Pyrophorer, anorganischer, flüssiger Stoff, N.A.G.',
      '3200 - Pyrophorer anorganischer fester Stoff, N.A.G.',
      '3203 - Pyrophore metallorganische Verbindung, mit Wasser reagierend, flüssig',
      '3205 - Erdalkalimetallalkoholate, N.A.G.',
      '3206 - Alkalimetallalkoholate, selbsterhitzungsfähig, ätzend, N.A.G.',
      '3207 - Metallorganische Verbindung oder metallorganische Verbindung, Lösung oder metallorganische Verbindung, Dispersion, mit Wasser reagierend, entzündbar',
      '3208 - Metallischer Stoff, mit Wasser reagierend, N.A.G.',
      '3209 - Metallischer Stoff, mit Wasser reagierend, selbsterhitzungsfähig, N.A.G.',
      '3210 - Chlorate, anorganische, wässrige Lösung, N.A.G.',
      '3211 - Perchlorate, anorganische, wässrige Lösung, N.A.G.',
      '3212 - Hypochlorite, anorganische, N.A.G.',
      '3213 - Bromate, anorganische, wässrige Lösung, N.A.G.',
      '3214 - Permanganate, anorganische, wässrige Lösung, N.A.G.',
      '3215 - Persulfate, anorganische, N.A.G.',
      '3216 - Persulfate, anorganische, wässrige Lösung, N.A.G.',
      '3218 - Nitrate, anorganische, wässrige Lösung, N.A.G.',
      '3219 - Nitrite, anorganische, wässrige Lösung, N.A.G.',
      '3220 - Pentafluorethan (Gas als Kältemittel R 125)',
      '3221 - Selbstzersetzlicher Stoff Typ B, flüssig',
      '3222 - Selbstzersetzlicher Stoff Typ B, fest',
      '3223 - Selbstzersetzlicher Stoff Typ C, flüssig',
      '3224 - Selbstzersetzlicher Stoff Typ C, fest',
      '3225 - Selbstzersetzlicher Stoff Typ D, flüssig',
      '3226 - Selbstzersetzlicher Stoff Typ D, fest',
      '3227 - Selbstzersetzlicher Stoff Typ E, flüssig',
      '3228 - Selbstzersetzlicher Stoff Typ E, fest',
      '3229 - Selbstzersetzlicher Stoff Typ F, flüssig',
      '3230 - Selbstzersetzlicher Stoff Typ F, fest',
      '3231 - Selbstzersetzlicher Stoff Typ B, flüssig, temperaturkontrolliert',
      '3232 - Selbstzersetzlicher Stoff Typ B, fest, temperaturkontrolliert',
      '3233 - Selbstzersetzlicher Stoff Typ C, flüssig, temperaturkontrolliert',
      '3234 - Selbstzersetzlicher Stoff Typ C, fest, temperaturkontrolliert',
      '3235 - Selbstzersetzlicher Stoff Typ D, flüssig, temperaturkontrolliert',
      '3236 - Selbstzersetzlicher Stoff Typ D, fest, temperaturkontrolliert',
      '3237 - Selbstzersetzlicher Stoff Typ E, flüssig, temperaturkontrolliert',
      '3238 - Selbstzersetzlicher Stoff Typ E, fest, temperaturkontrolliert',
      '3239 - Selbstzersetzlicher Stoff Typ F, flüssig, temperaturkontrolliert',
      '3240 - Selbstzersetzlicher Stoff Typ F, fest, temperaturkontrolliert',
      '3241 - 2-Brom-2-nitropropan-1,3-diol',
      '3242 - Azodicarbonamid',
      '3243 - Feste Stoffe mit giftigem, flüssigem Stoff, N.A.G.',
      '3244 - Feste Stoffe mit ätzendem, flüssigem Stoff, N.A.G.',
      '3245 - Genetisch veränderte Mikroorganismen oder Genetisch veränderte Organismen',
      '3246 - Methansulfonylchlorid',
      '3247 - Natriumperoxoborat, wasserfrei',
      '3248 - Medikament, flüssig, entzündbar, giftig, N.A.G.',
      '3249 - Medikament, fest, giftig, N.A.G.',
      '3250 - Chloressigsäure, geschmolzen',
      '3251 - Isosorbid-5-mononitrat',
      '3252 - Difluormethan (Gas als Kältemittel R 32)',
      '3253 - Dinatriumtrioxosilicat',
      '3254 - Tributylphosphan',
      '3255 - tert-Butylhypochlorit',
      '3256 - Erwärmter, flüssiger Stoff, entzündbar, N.A.G., mit einem Flammpunkt über 61 °C, bei oder über seinem Flammpunkt',
      '3257 - Erwärmter, flüssiger Stoff, N.A.G., bei oder über 100 °C und bei Stoffen mit einem Schmelzpunkt unter seinem Flammpunkt (einschließlich geschmolzenes Metall, geschmolzenes Salz usw.)',
      '3258 - Erwärmter, fester Stoff, N.A.G., bei oder über 240 °C',
      '3259 - Amine, fest, ätzend, N.A.G. oder Polyamine, fest, ätzend, N.A.G.',
      '3260 - Ätzender, saurer, anorganischer, fester Stoff, N.A.G.',
      '3261 - Ätzender, saurer, organischer, fester Stoff, N.A.G.',
      '3262 - Ätzender, basischer, anorganischer, fester Stoff, N.A.G.',
      '3263 - Ätzender, basischer, organischer, fester Stoff, N.A.G.',
      '3264 - Ätzender, saurer, anorganischer, flüssiger Stoff, N.A.G.',
      '3265 - Ätzender, saurer, organischer, flüssiger Stoff, N.A.G.',
      '3266 - Ätzender, basischer, anorganischer, flüssiger Stoff, N.A.G.',
      '3267 - Ätzender, basischer, organischer, flüssiger Stoff, N.A.G.',
      '3268 - Airbag-Gasgeneratoren, pyrotechnisch oder Airbag-Module, pyrotechnisch oder Gurtstraffer, pyrotechnisch oder Sicherheitseinrichtungen, elektrische Auslösung',
      '3269 - Polyesterharzmehrkomponentensysteme',
      '3270 - Membranfilter aus Nitrocellulose, mit höchstens 12,6 % Stickstoff in der Trockenmasse',
      '3271 - Ether, N.A.G.',
      '3272 - Ester, N.A.G.',
      '3273 - Nitrile, entzündbar, giftig, N.A.G.',
      '3274 - Alkoholate, Lösung in Alkohol, N.A.G.',
      '3275 - Nitrile, giftig, entzündbar, N.A.G.',
      '3276 - Nitrile, giftig, N.A.G.',
      '3277 - Chlorformiate, giftig, ätzend, N.A.G.',
      '3278 - Organische Phosphorverbindung, giftig, flüssig, N.A.G.',
      '3279 - Organische Phosphorverbindung, giftig, entzündbar, N.A.G.',
      '3280 - Organische Arsenverbindung, flüssig, N.A.G.',
      '3281 - Metallcarbonyle, flüssig, N.A.G.',
      '3282 - Metallorganische Verbindung, giftig, flüssig, N.A.G.',
      '3283 - Selenverbindung, fest, N.A.G.',
      '3284 - Tellurverbindung, N.A.G.',
      '3285 - Vanadiumverbindung, N.A.G.',
      '3286 - Entzündbarer, flüssiger Stoff, giftig, ätzend, N.A.G.',
      '3287 - Giftiger, anorganischer, flüssiger Stoff, N.A.G.',
      '3288 - Giftiger, anorganischer, fester Stoff, N.A.G.',
      '3289 - Giftiger, anorganischer, flüssiger Stoff, ätzend, N.A.G.',
      '3290 - Giftiger, anorganischer, fester Stoff, ätzend, N.A.G.',
      '3291 - Klinischer Abfall, unspezifiziert, N.A.G. oder (Bio)Medizinischer Abfall, N.A.G. oder Unter die vorschriften fallender Medizinischer Abfall, N.A.G.',
      '3292 - Batterien, die metallisches Natrium oder Natriumlegierungen enthalten oder Zellen, die metallisches Natrium oder Natriumlegierungen enthalten',
      '3293 - Hydrazin, wässrige Lösung mit höchstens 37 Masse-% Hydrazin',
      '3294 - Cyanwasserstoff, Lösung in Alkohol mit höchstens 45 % Cyanwasserstoff',
      '3295 - Kohlenwasserstoffe, flüssig, N.A.G.',
      '3296 - Heptafluorpropan (Gas als Kältemittel R 227)',
      '3297 - Ethylenoxid und Chlortetrafluorethan, Gemisch mit höchstens 8,8 % Ethylenoxid',
      '3298 - Ethylenoxid und Pentafluorethan, Gemisch mit höchstens 7,9 % Ethylenoxid',
      '3299 - Ethylenoxid und Tetrafluorethan, Gemisch mit höchstens 5,6 % Ethylenoxid',
      '3300 - Ethylenoxid und Kohlendioxid, Gemisch mit mehr als 87 % Ethylenoxid',
      '3301 - Ätzender, flüssiger Stoff, selbsterhitzungsfähig, N.A.G.',
      '3302 - 2-Dimethylaminoethylacrylat',
      '3303 - Verdichtetes Gas, giftig, oxidierend, N.A.G.',
      '3304 - Verdichtetes Gas, giftig, ätzend, N.A.G.',
      '3305 - Verdichtetes Gas, giftig, entzündbar, ätzend, N.A.G.',
      '3306 - Verdichtetes Gas, giftig, oxidierend, ätzend, N.A.G.',
      '3307 - Verflüssigtes Gas, giftig, oxidierend, N.A.G.',
      '3308 - Verflüssigtes Gas, giftig, ätzend, N.A.G.',
      '3309 - Verflüssigtes Gas, giftig, entzündbar, ätzend, N.A.G.',
      '3310 - Verflüssigtes Gas, giftig, oxidierend, ätzend, N.A.G.',
      '3311 - Gas, tiefgekühlt, flüssig, oxidierend, N.A.G.',
      '3312 - Gas, tiefgekühlt, flüssig, entzündbar, N.A.G.',
      '3313 - Selbsterhitzungsfähige organische Pigmente',
      '3314 - Kunststoffpressmischung, in Teig-, Platten- oder Strangpressform, entzündbare Dämpfe abgebend',
      '3315 - Chemische Probe, giftig',
      '3316 - Chemie-Testsatz oder Erste-Hilfe-Ausrüstung',
      '3317 - 2-Amino-4,6-dinitrophenol, angefeuchtet mit mindestens 20 Masse-% Wasser',
      '3318 - Ammoniaklösung in Wasser, Dichte kleiner als 0,880 kg/l bei 15 °C, mit mehr als 50 % Ammoniak',
      '3319 - Nitroglycerin, Gemisch, desensibilisiert, fest, N.A.G., mit mehr als 2 Masse-%, aber höchstens 10 Masse-% Nitroglycerin',
      '3320 - Natriumborhydrid und Natriumhydroxid, Lösung mit höchstens 12 Masse-% Natriumborhydrid und höchstens 40 Masse-% Natriumhydroxid',
      '3321 - Radioaktive Stoffe mit geringer spezifischer Aktivität (LSA-II), nicht spaltbar oder spaltbar, freigestellt',
      '3322 - Radioaktive Stoffe mit geringer spezifischer Aktivität (LSA-III), nicht spaltbar oder spaltbar, freigestellt',
      '3323 - Radioaktive Stoffe, Typ C-Versandstück, nicht spaltbar oder spaltbar, freigestellt',
      '3324 - Radioaktive Stoffe mit geringer spezifischer Aktivität (LSA-II), spaltbar',
      '3325 - Radioaktive Stoffe mit geringer spezifischer Aktivität (LSA-III), spaltbar',
      '3326 - Radioaktive Stoffe, oberflächenkontaminierte Gegenstände (SCO-I oder SCO-II), spaltbar',
      '3327 - Radioaktive Stoffe, Typ A-Versandstück, spaltbar, nicht in besonderer Form',
      '3328 - Radioaktive Stoffe, Typ B(U)-Versandstück, spaltbar',
      '3329 - Radioaktive Stoffe, Typ B(M)-Versandstück, spaltbar',
      '3330 - Radioaktive Stoffe, Typ C-Versandstück, spaltbar',
      '3331 - Radioaktive Stoffe, unter Sondervereinbarung befördert, spaltbar',
      '3332 - Radioaktive Stoffe, Typ A-Versandstück, in besonderer Form, nicht spaltbar oder spaltbar, freigestellt',
      '3333 - Radioaktive Stoffe, Typ A-Versandstück, in besonderer Form, spaltbar',
      '3334 - Flüssiger Stoff, den für die Luftfahrt geltenden Vorschriften unterliegend, N.A.G.',
      '3335 - Fester Stoff, den für die Luftfahrt geltenden Vorschriften unterliegend, N.A.G.',
      '3336 - Mercaptane, flüssig, entzündbar, N.A.G. oder Mercaptane, Mischung, flüssig, entzündbar, N.A.G.',
      '3337 - Gas als Kältemittel R 404A, Pentafluorethan, 1,1,1-Trifluorethan und 1,1,1,2-Tetrafluorethan, zeotropes Gemisch mit rund 44 % Pentafluorethan und 52 % 1,1,1-Trifluorethan',
      '3338 - Gas als Kältemittel R 407A, Difluormethan, Pentafluorethan und 1,1,1,2-Tetrafluorethan, zeotropes Gemisch mit rund 20 % Difluormethan und 40 % Pentafluorethan',
      '3339 - Gas als Kältemittel R 407B, Difluormethan, Pentafluorethan und 1,1,1,2-Tetrafluorethan, zeotropes Gemisch mit rund 10 % Difluormethan und 70 % Pentafluorethan',
      '3340 - Gas als Kältemittel R 407 C, Difluormethan, Pentafluorethan und 1,1,1,2-Tetrafluorethan, zeotropes Gemisch mit rund 23 % Difluormethan und 25 % Pentafluorethan',
      '3341 - Thioharnstoffdioxid',
      '3342 - Xanthate',
      '3343 - Nitroglycerin, Gemisch, desensibilisiert, flüssig, entzündbar, N.A.G., mit höchstens 30 Masse-% Nitroglycerin',
      '3344 - Pentaerythritoltetranitrat, Gemisch, desensibilisiert, fest, N.A.G., mit mehr als 10 Masse-%, aber höchstens 20 Masse-% PETN',
      '3345 - Phenoxyessigsäurederivat-Pestizid, fest, giftig',
      '3346 - Phenoxyessigsäurederivat-Pestizid, flüssig, entzündbar, giftig, Flammpunkt unter 23 °C',
      '3347 - Phenoxyessigsäurederivat-Pestizid, flüssig, giftig, entzündbar, mit einem Flammpunkt von 23 °C oder darüber',
      '3348 - Phenoxyessigsäurederivat-Pestizid, flüssig, giftig',
      '3349 - Pyrethroid-Pestizid, fest, giftig',
      '3350 - Pyrethroid-Pestizid, flüssig, entzündbar, giftig, Flammpunkt unter 23 °C',
      '3351 - Pyrethroid-Pestizid, flüssig, giftig, entzündbar, mit einem Flammpunkt von 23 °C oder darüber',
      '3352 - Pyrethroid-Pestizid, flüssig, giftig, entzündbar',
      '3354 - Insektenbekämpfungsmittel, gasförmig, entzündbar, N.A.G.',
      '3355 - Insektenbekämpfungsmittel, gasförmig, giftig, entzündbar, N.A.G.',
      '3356 - Sauerstoffgenerator, chemisch',
      '3357 - Nitroglycein, Gemisch, desensibilisiert, flüssig, N.A.G., mit höchstens 30 Masse-% Nitroglycerin',
      '3358 - Kältemaschinen mit entzündbarem, nicht giftigem verflüssigtem Gas',
      '3359 - Begaste Güterbeförderungseinheit (CTU)',
      '3360 - Fasern, pflanzlich, trocken',
      '3361 - Chlorsilane, giftig, ätzend, N.A.G.',
      '3362 - Chlorsilane, giftig, ätzend, entzündbar, N.A.G.',
      '3363 - Gefährliche Güter in Gegenständen oder Gefährliche Güter in Maschinen oder Gefährliche Güter in Geräten',
      '3364 - Trinitrophenol (Pikrinsäure), angefeuchtet mit mindestens 10 Masse-% Wasser, in Mengen von höchstens 500 g',
      '3365 - Trinitrochlorbenzen (Pikrylchlorid), angefeuchtet mit mindestens 10 Masse-% Wasser, in Mengen von höchstens 500 g',
      '3366 - Trinitrotoluol (TNT), angefeuchtet mit mindestens 10 Masse-% Wasser, in Mengen von höchstens 500 g',
      '3367 - Trinitrobenzen, angefeuchtet mit mindestens 10 Masse-% Wasser, in Mengen von höchstens 500 g',
      '3368 - Trinitrobenzoesäure, angefeuchtet mit mindestens 10 Masse-% Wasser, in Mengen von höchstens 500 g',
      '3369 - Natriumdinitroorthokresolat, angefeuchtet mit mindestens 10 Masse-% Wasser, in Mengen von höchstens 500 g',
      '3370 - Harnstoffnitrat, angefeuchtet mit mindestens 10 Masse-% Wasser, in Mengen von höchstens 11,5 kg',
      '3371 - 2-Methylbutanal',
      '3373 - Biologischer Stoff, Kategorie B oder Biologischer Stoff, Kategorie B (nur tierische Stoffe)',
      '3374 - Acetylen, frei von Lösungsmitteln',
      '3375 - Ammoniumnitrat-Emulsion, Zwischenprodukt für die Herstellung von Sprengstoffen',
      '3376 - 4-Nitrophenylhydrazin, mit mindestens 30 Masse-% Wasser',
      '3377 - Natriumperborat-Monohydrat',
      '3378 - Natriumcarbonat-Peroxyhydrat',
      '3379 - Desensibilisierter Explosivstoff, flüssig, N.A.G.',
      '3380 - Desensibilisierter Explosivstoff, fest, N.A.G.',
      '3381 - Beim Einatmen giftiger flüssiger Stoff, N.A.G., mit einem LC50-Wert von höchstens 200 ml/m3 und einer gesättigten Dampfkonzentration von mindestens 500 LC50',
      '3382 - Beim Einatmen giftiger flüssiger Stoff, N.A.G., mit einem LC50-Wert von höchstens 1000 ml/m3 und einer gesättigten Dampfkonzentration von mindestens 10 LC50',
      '3383 - Beim Einatmen giftiger flüssiger Stoff, entzündbar, N.A.G., mit einem LC50-Wert von höchstens 200 ml/m3 und einer gesättigten Dampfkonzentration von mindestens 500 LC50',
      '3384 - Beim Einatmen giftiger flüssiger Stoff, entzündbar, N.A.G., mit einem LC50-Wert von höchstens 1000 ml/m3 und einer gesättigten Dampfkonzentration von mindestens 10 LC50',
      '3385 - Beim Einatmen giftiger flüssiger Stoff, mit Wasser reagierend, N.A.G., mit einem LC50-Wert von höchstens 200 ml/m3 und einer gesättigten Dampfkonzentration von mindestens 500 LC50',
      '3386 - Beim Einatmen giftiger flüssiger Stoff, mit Wasser reagierend, N.A.G., mit einem LC50-Wert von höchstens 1000 ml/m3 und einer gesättigten Dampfkonzentration von mindestens 10 LC50',
      '3387 - Beim Einatmen giftiger flüssiger Stoff, entzündend (oxidierend) wirkend, N.A.G., mit einem LC50-Wert von höchstens 200 ml/m3 und einer gesättigten Dampfkonzentration von mindestens 500 LC50',
      '3388 - Beim Einatmen giftiger flüssiger Stoff, entzündend (oxidierend) wirkend, N.A.G., mit einem LC50-Wert von höchstens 1000 ml/m3 und einer gesättigten Dampfkonzentration von mindestens 10 LC50',
      '3389 - Beim Einatmen giftiger flüssiger Stoff, ätzend N.A.G., mit einem LC50-Wert von höchstens 200 ml/m3 und einer gesättigten Dampfkonzentration von mindestens 500 LC50',
      '3390 - Beim Einatmen giftiger flüssiger Stoff, ätzend N.A.G., mit einem LC50-Wert von höchstens 1000 ml/m3 und einer gesättigten Dampfkonzentration von mindestens 10 LC50',
      '3391 - Metallorganischer Stoff, fest, pyrophor',
      '3392 - Metallorganischer Stoff, flüssig, pyrophor',
      '3393 - Metallorganischer Stoff, mit Wasser reagierend, fest, pyrophor',
      '3394 - Metallorganischer Stoff, mit Wasser reagierend, flüssig, pyrophor',
      '3395 - Metallorganischer Stoff, mit Wasser reagierend, fest',
      '3396 - Metallorganischer Stoff, mit Wasser reagierend, entzündbar, fest',
      '3397 - Metallorganischer Stoff, mit Wasser reagierend, selbsterhitzungsfähig, fest',
      '3398 - Metallorganischer Stoff, mit Wasser reagierend, flüssig',
      '3399 - Metallorganischer Stoff, mit Wasser reagierend, entzündbar, flüssig',
      '3400 - Selbsterhitzungsfähiger metallorganischer fester Stoff',
      '3401 - Alkalimetallamalgam, fest',
      '3402 - Erdalkalimetallamalgam, fest',
      '3403 - Kaliummetalllegierungen, fest',
      '3404 - Kalium-Natrium-Legierungen, fest',
      '3405 - Bariumchlorat, Lösung',
      '3406 - Bariumperchlorat, Lösung',
      '3407 - Chlorat und Magnesiumchlorid, Mischung, Lösung',
      '3408 - Bleiperchlorat, Lösung',
      '3409 - Chlornitrobenzole, Isomerengemisch, flüssig',
      '3410 - 4-Chlor-o-toluidinhydrochlorid, Lösung',
      '3411 - beta-Naphthylamin, Lösung',
      '3413 - Kaliumcyanid, Lösung',
      '3414 - Natriumcyanid, Lösung',
      '3415 - Natriumfluorid, Lösung',
      '3416 - Chloracetophenon, flüssig',
      '3417 - Xylylbromid, fest',
      '3418 - 2,4-Toluylendiamin, Lösung',
      '3419 - Bortrifluorid-Essigsäure-Komplex, fest',
      '3420 - Bortrifluorid-Propionsäure-Komplex, fest',
      '3421 - Kaliumhydrogendifluorid, Lösung',
      '3422 - Kaliumfluorid, Lösung',
      '3423 - Tetramethylammoniumhydroxid, fest',
      '3424 - Ammoniumdinitro-o-cresolat, Lösung',
      '3425 - Bromessigsäure, fest',
      '3426 - Acrylamid, Lösung',
      '3427 - Chlorbenzylchlorid, fest',
      '3428 - 3-Chlor-4-methylphenylisocyanat, fest',
      '3429 - Chlortoluidine, flüssig',
      '3430 - Xylenole, flüssig',
      '3431 - Nitrobenzotrifluoride, fest',
      '3432 - Polychlorierte Biphenyle, fest',
      '3433 - Lithiumalkyle, fest',
      '3434 - Nitrocresole, flüssig',
      '3436 - Hexafluoracetonhydrat, fest',
      '3437 - Chlorcresole, fest',
      '3438 - alpha-Methylbenzylalkohol, fest',
      '3439 - Nitrile, fest, giftig, N.A.G.',
      '3440 - Selenverbindung, flüssig, N.A.G.',
      '3441 - Chlordinitrobenzene, fest',
      '3442 - Dichloraniline, fest',
      '3443 - Dinitrobenzol, fest',
      '3444 - Nicotinhydrochlorid, fest',
      '3445 - Nicotinsulfat, fest',
      '3446 - Nitrotoluene, fest',
      '3447 - Nitroxylene, fest',
      '3448 - Stoff zur Herstellung von Tränengasen, fest, N.A.G.',
      '3449 - Brombenzylcyanide, fest',
      '3450 - Diphenylchlorarsin, fest',
      '3451 - Toluidine, fest',
      '3452 - Xylidine, fest',
      '3453 - Phosphorsäure, fest',
      '3454 - Dinitrotoluene, fest',
      '3455 - Cresole, fest',
      '3456 - Nitrosylschwefelsäure, fest',
      '3457 - Chlornitrotoluene, fest',
      '3458 - Nitroanisol, Isomerengemisch, fest',
      '3459 - Nitrobrombenzene, fest',
      '3460 - N-Ethyl-N-benzyltoluidine, fest',
      '3462 - Toxine, gewonnen aus lebenden Organismen, fest, N.A.G.',
      '3463 - Propionsäure, mit mindestens 90 Masse-% Säure',
      '3464 - Organische Phosphorverbindung, fest, giftig, N.A.G.',
      '3465 - Organische Arsenverbindung, fest, N.A.G.',
      '3466 - Metallcarbonyle, fest, N.A.G.',
      '3467 - Metallorganische Verbindung, fest, giftig, N.A.G.',
      '3468 - Wasserstoff in einem Metallhydrid-Speichersystem oder Wasserstoff in einem Metallhydrid-Speichersystem in Ausrüstungen oder Wasserstoff in einem Metallhydrid-Speichersystem, mit Ausrüstungen verpackt',
      '3469 - Farbe, entzündbar, ätzend (einschließlich Farbe, Lack, Emaille, Beize, Schellack, Firnis, Politur, flüssiger Füllstoff und flüssige Lackgrundlage) oder Farbzubehörstoffe, entzündbar, ätzend (einschließlich Farbverdünnung und -lösemittel)',
      '3470 - Farbe, ätzend, entzündbar (einschließlich Farbe, Lack, Emaille, Beize, Schellack, Firnis, Politur, flüssiger Füllstoff und flüssige Lackgrundlage) oder Farbzubehörstoffe, ätzend, entzündbar (einschließlich Farbverdünnung und -lösemittel)',
      '3471 - Hydrogendifluoride, wässrige Lösung, N.A.G.',
      '3472 - Crotonsäure, flüssig',
      '3473 - Brennstoffzellen-Kartuschen oder Brennstoffzellen-Kartuschen in Ausrüstungen oder Brennstoffzellen-Kartuschen, mit Ausrüstungen verpackt, entzündbare flüssige Stoffe enthaltend',
      '3474 - 1-Hydroxybenzotriazol-Monohydrat',
      '3475 - Ethanol und Benzin, Gemisch oder Ethanol und Ottokraftstoff, Gemisch mit mehr als 10 % Ethanol',
      '3476 - Brennstoffzellenkartuschen oder Brennstoffzellenkartuschen in Ausrüstungen oder Brennstoffzellenkartuschen, mit Ausrüstungen verpackt, mit Wasser reagierende Stoffe enthaltend',
      '3477 - Brennstoffzellenkartuschen oder Brennstoffzellenkartuschen in Ausrüstungen oder Brennstoffzellenkartuschen, mit Ausrüstungen verpackt, ätzende Stoffe enthaltend',
      '3478 - Brennstoffzellenkartuschen oder Brennstoffzellenkartuschen in Ausrüstungen, oder Brennstoffzellenkartuschen, mit Ausrüstungen verpackt, verflüssigtes entzündbares Gas enthaltend',
      '3479 - Brennstoffzellenkartuschen oder Brennstoffzellenkartuschen in Ausrüstungen oder Brennstoffzellenkartuschen, mit Ausrüstungen verpackt, Wasserstoff in Metallhydrid enthaltend',
      '3480 - Lithium-Ionen-Batterien (einschließlich Lithium-Ionen-Polymer-Batterien)',
      '3481 - Lithium-Ionen-Batterien in Ausrüstungen oder Lithium-Ionen-Batterien, mit Ausrüstungen verpackt (einschließlich Lithium-Ionen-Polymer-Batterien)',
      '3482 - Alkalimetall-Dispersion, entzündbar oder Erdalkalimetall-Dispersion, entzündbar',
      '3483 - Antiklopfmischung für Motorkraftstoff, entzündbar',
      '3484 - Hydrazin, wässrige Lösung, mit mehr als 37 Masse-% Hydrazin',
      '3485 - Calciumhypochlorit, trocken, ätzend oder Calciumhypochlorit, Mischung, trocken, ätzend mit mehr als 39 % aktivem Chlor (8,8 % aktivem Sauerstoff)',
      '3486 - Calciumhypochlorit, Mischung, trocken, ätzend mit mehr als 10 %, aber höchstens 39 % aktivem Chlor',
      '3487 - Calciumhypochlorit, hydratisiert, ätzend oder Calciumhypochlorit, hydratisierte Mischung, ätzend mit mindestens 5,5 %, aber höchstens 16 % Wasser',
      '3488 - Beim Einatmen giftiger flüssiger Stoff, entzündbar, ätzend, N.A.G., mit einem LC50-Wert von höchstens 200 ml/m³ und einer gesättigten Dampfkonzentration von mindestens 500 LC50',
      '3489 - Beim Einatmen giftiger flüssiger Stoff, entzündbar, ätzend, N.A.G., mit einem LC50-Wert von höchstens 1000 ml/m³ und einer gesättigten Dampfkonzentration von mindestens 10 LC50',
      '3490 - Beim Einatmen giftiger flüssiger Stoff, mit Wasser reagierend, entzündbar, N.A.G., mit einem LC50-Wert von höchstens 200 ml/m³ und einer gesättigten Dampfkonzentration von mindestens 500 LC50',
      '3491 - Beim Einatmen giftiger flüssiger Stoff, mit Wasser reagierend, entzündbar, N.A.G., mit einem LC50-Wert von höchstens 1000 ml/m³ und einer gesättigten Dampfkonzentration von mindestens 10 LC50',
      '3494 - Schwefelreiches Roherdöl, entzündbar, giftig',
      '3495 - Quecksilberiodid',
      '3496 - Batterien, Nickelmetallhydrid',
      '3497 - Krillmehl',
      '3498 - Iodmonochlorid, flüssig',
      '3499 - Kondensator, elektrische Doppelschicht, mit einer Energiespeicherkapazität von mehr als 0,3 Wh',
      '3500 - Chemikalie unter Druck, N.A.G.',
      '3501 - Chemikalie unter Druck, entzündbar, N.A.G.',
      '3502 - Chemikalie unter Druck, giftig, N.A.G.',
      '3503 - Chemikalie unter Druck, ätzend, N.A.G.',
      '3504 - Chemikalie unter Druck, entzündbar, giftig, N.A.G.',
      '3505 - Chemikalie unter Druck, entzündbar, ätzend, N.A.G.',
      '3506 - Quecksilber in hergestellten Gegenständen',
      '3507 - Uranhexafluorid, radioaktive Stoffe, freigestelltes Versandstück, mit weniger als 0,1 kg je Versandstück, nicht spaltbar oder spaltbar, freigestellt',
      '3508 - Kondensator, asymmetrisch, mit einer Energiespeicherkapazität von mehr als 0,3 Wh',
      '3509 - Altverpackungen, leer, ungereinigt',
      '3510 - Adsorbiertes Gas, entzündbar, N.A.G.',
      '3511 - Adsorbiertes Gas, N.A.G.',
      '3512 - Adsorbiertes Gas, giftig, N.A.G.',
      '3513 - Adsorbiertes Gas, oxidierend, N.A.G.',
      '3514 - Adsorbiertes Gas, giftig, entzündbar, N.A.G.',
      '3515 - Adsorbiertes Gas, giftig, oxidierend, N.A.G.',
      '3516 - Adsorbiertes Gas, giftig, ätzend, N.A.G.',
      '3517 - Adsorbiertes Gas, giftig, entzündbar, ätzend, N.A.G.',
      '3518 - Adsorbiertes Gas, giftig, oxidierend, ätzend, N.A.G.',
      '3519 - Bortrifluorid, adsorbiert',
      '3520 - Chlor, adsorbiert',
      '3521 - Siliciumtetrafluorid, adsorbiert',
      '3522 - Arsenwasserstoff (Arsin), adsorbiert',
      '3523 - Germaniumwasserstoff (German), adsorbiert',
      '3524 - Phosphorpentafluorid, adsorbiert',
      '3525 - Phosphorwasserstoff (Phosphin), adsorbiert',
      '3526 - Selenwasserstoff, adsorbiert',
      '3527 - Polyesterharz-Mehrkomponentensysteme, festes Grundprodukt',
      '3528 - Verbrennungsmotor mit Antrieb durch entzündbare Flüssigkeit oder Brennstoffzellen-Motor mit Antrieb durch entzündbare Flüssigkeit oder Verbrennungsmaschine mit Antrieb durch entzündbare Flüssigkeit oder Maschine mit Brennstoffzellen-Motor mit Antrieb durch entzündbare Flüssigkeit',
      '3529 - Verbrennungsmotor mit Antrieb durch entzündbares Gas oder Brennstoffzellen-Motor mit Antrieb durch entzündbares Gas oder Verbrennungsmaschine mit Antrieb durch entzündbares Gas oder Maschine mit Brennstoffzellen-Motor mit Antrieb durch entzündbares Gas',
      '3530 - Verbrennungsmaschine, Verbrennungsmotor',
      '3531 - Polymerisierender Stoff, fest, stabilisiert, N.A.G.',
      '3532 - Polymerisierender Stoff, flüssig, stabilisiert, N.A.G.',
      '3533 - Polymerisierender Stoff, fest, temperaturkontrolliert, stabilisiert, N.A.G.',
      '3534 - Polymerisierender Stoff, flüssig, temperaturkontrolliert, stabilisiert, N.A.G.',
      '3535 - Giftiger anorganischer fester Stoff, entzündbar, N.A.G.',
      '3536 - Lithiumbatterien, in Güterbeförderungseinheiten eingebaut, Lithium-Ionen-Batterien oder Lithium-Metall-Batterien',
      '3537 - Gegenstände, die entzündbares Gas enthalten, N.A.G.',
      '3538 - Gegenstände, die nicht entzündbares, nicht giftiges Gas enthalten, N.A.G.',
      '3539 - Gegenstände, die giftiges Gas enthalten, N.A.G.',
      '3540 - Gegenstände, die einen entzündbaren flüssigen Stoff enthalten, N.A.G.',
      '3541 - Gegenstände, die einen entzündbaren festen Stoff enthalten, N.A.G.',
      '3542 - Gegenstände, die einen selbsterhitzungsfähigen Stoff enthalten, N.A.G.',
      '3543 - Gegenstände, die einen Stoff enthalten, der in Berührung mit Wasser entzündbare Gase entwickelt, N.A.G.',
      '3544 - Gegenstände, die einen entzündend (oxidierend) wirkenden Stoff enthalten, N.A.G.',
      '3545 - Gegenstände, die organisches Peroxid enthalten, N.A.G.',
      '3546 - Gegenstände, die einen giftigen Stoff enthalten, N.A.G.',
      '3547 - Gegenstände, die einen ätzenden Stoff enthalten, N.A.G.',
      '3548 - Gegenstände, die verschiedene gefährliche Güter enthalten, N.A.G.',
      '3549 - Medizinische Abfälle, Kategorie A, Gefährlich für Menschen, fest oder Medizinische Abfälle, Kategorie A, nur Gefährlich für Tiere, fest',
      '3550 - Cobaltdihydroxid-Pulver mit mindestens 10 % lungengängigen Partikeln',
      '3551 - Natrium-Ionen-Batterien mit einem organischen Elektrolyt',
      '3552 - Natrium-Ionen-Batterien in Ausrüstungen oder Natrium-Ionen-Batterien, mit Ausrüstungen verpackt, mit einem organischen Elektrolyt',
      '3553 - Disilan',
      '3554 - Gallium in hergestellten Gegenständen',
      '3555 - Trifluormethyltetrazol-Natriumsalz in Aceton mit mindestens 68 Masse-% Aceton',
      '3556 - Fahrzeug mit Antrieb durch Lithium-Ionen-Batterien',
      '3557 - Fahrzeug mit Antrieb durch Lithium-Metall-Batterien',
      '3558 - Fahrzeug mit Antrieb durch Natrium-Ionen-Batterien',
      '3559 - Feuerlöschmittel-Dispergiervorrichtungen',
      '3560 - Tetramethylammoniumhydroxid, wässrige Lösung mit mindestens 25 % Tetramethylammoniumhydroxid',
      '8000 - Konsumgüter',
      '9000 - Ammoniak, tiefgekühlt',
      '9001 - Stoffe mit einem Flammpunkt über 61 °C, die in einem Grenzbereich von 15 K unterhalb des Flammpunktes erwärmt zur Beförderung aufgegeben oder befördert werden',
      '9002 - Stoffe mit einer Zündtemperatur von höchstens 200 °C und nicht anderweitig aufgeführt',
      '9003 - Stoffe mit einem Flammpunkt über 61 °C und höchstens 100 °C, die nicht anderen Klassen zuzuordnen sind',
      '9004 - Diphenylmethan-4,4\'-diisocyanat',
      '9005 - Umweltgefährdende Stoffe, fest, geschmolzen, N.A.G.',
      '9006 - Umweltgefährdende Stoffe, flüssig, N.A.G.',
    ]
    
    // Lagerklasse options
    const lagerklasseOptions = [
      '1 - Explosive Gefahrstoffe',
      '2A - Gase (ohne Aerosolpackungen und Feuerzeuge)',
      '2B - Aerosolpackungen und Feuerzeuge',
      '3 - Entzündbare Flüssigkeiten',
      '4.1A - Sonstige explosionsgefährliche Gefahrstoffe',
      '4.1B - Entzündbare feste Gefahrstoffe',
      '4.2 - Pyrophore oder selbsterhitzungsfähige Gefahrstoffe',
      '4.3 - Gefahrstoffe, die in Berührung mit Wasser entzündbare Gase entwickeln',
      '5.1A - Stark oxidierende Gefahrstoffe',
      '5.1B - Oxidierende Gefahrstoffe',
      '5.1C - Ammoniumnitrat und ammoniumnitrathaltige Zubereitungen',
      '5.2 - Organische Peroxide und selbstzersetzliche Gefahrstoffe',
      '6.1A - Brennbare, akut toxische Kat. 1 und 2 / sehr giftige Gefahrstoffe',
      '6.1B - Nicht brennbare, akut toxische Kat. 1 und 2 / sehr giftige Gefahrstoffe',
      '6.1C - Brennbare, akut toxische Kat. 3 / giftige oder chronisch wirkende Gefahrstoffe',
      '6.1D - Nicht brennbare, akut toxische Kat. 3 / giftige oder chronisch wirkende Gefahrstoffe',
      '6.2 - Ansteckungsgefährliche Stoffe',
      '7 - Radioaktive Stoffe',
      '8A - Brennbare ätzende Gefahrstoffe',
      '8B - Nicht brennbare ätzende Gefahrstoffe',
      '10 - Brennbare Flüssigkeiten, die keiner der vorgenannten LGK zuzuordnen sind',
      '11 - Brennbare Feststoffe, die keiner der vorgenannten LGK zuzuordnen sind',
      '12 - Nicht brennbare Flüssigkeiten, die keiner der vorgenannten LGK zuzuordnen sind',
      '13 - Nicht brennbare Feststoffe, die keiner der vorgenannten LGK zuzuordnen sind',
    ]
    function lagerklasseCodeToDisplay(code: string) {
      if (!code) return ''
      const full = lagerklasseOptions.find(opt => opt === code || opt.startsWith(code + ' - '))
      return full || code
    }
    
    // EAK number options
    const eakNumberOptions = [
      '01 - Abfälle, die beim Aufsuchen, Ausbeuten und Gewinnen sowie bei der physikalischen und chemischen Behandlung von Bodenschätzen entstehen',
      '02 - Abfälle aus Landwirtschaft, Gartenbau, Teichwirtschaft, Forstwirtschaft, Jagd und Fischerei sowie der Herstellung und Verarbeitung von Nahrungsmitteln',
      '03 - Abfälle aus der Holzbearbeitung und der Herstellung von Platten, Möbeln, Zellstoffen, Papier und Pappe',
      '04 - Abfälle aus der Leder-, Pelz- und Textilindustrie',
      '05 - Abfälle aus der Erdölraffination, Erdgasreinigung und Kohlepyrolyse',
      '06 - Abfälle aus anorganisch-chemischen Prozessen',
      '07 - Abfälle aus organisch-chemischen Prozessen',
      '08 - Abfälle aus Herstellung, Zubereitung, Vertrieb und Anwendung (HZVA) von Beschichtungen (Farben, Lacke, Email), Klebstoffen, Dichtmassen und Druckfarben',
      '09 - Abfälle aus der fotografischen Industrie',
      '10 - Abfälle aus thermischen Prozessen',
      '11 - Abfälle aus der chemischen Oberflächenbearbeitung und Beschichtung von Metallen und anderen Werkstoffen; Nichteisenhydrometallurgie',
      '12 - Abfälle aus Prozessen der mechanischen Formgebung sowie der physikalischen und mechanischen Oberflächenbearbeitung von Metallen und Kunststoffen',
      '13 - Ölabfälle und Abfälle aus flüssigen Brennstoffen (außer Speiseöle, 05 und 12)',
      '14 - Abfälle aus organischen Lösemitteln, Kühlmitteln und Treibgasen (außer 07 und 08)',
      '15 - Verpackungsabfall, Aufsaugmassen, Wischtücher, Filtermaterialien und Schutzkleidung (a. n. g.)',
      '16 - Abfälle, die nicht anderswo im Verzeichnis aufgeführt sind',
      '17 - Bau- und Abbruchabfälle (einschließlich Aushub von verunreinigten Standorten)',
      '18 - Abfälle aus der humanmedizinischen oder tierärztlichen Versorgung und Forschung (ohne Küchen- und Restaurantabfälle, die nicht aus der unmittelbaren Krankenpflege stammen)',
      '19 - Abfälle aus Abfallbehandlungsanlagen, öffentlichen Abwasserbehandlungsanlagen sowie der Aufbereitung von Wasser für den menschlichen Gebrauch und Wasser für industrielle Zwecke',
      '20 - Siedlungsabfälle (Haushaltsabfälle und ähnliche gewerbliche und industrielle Abfälle sowie Abfälle aus Einrichtungen), einschließlich getrennt gesammelter Fraktionen',
      '01 01 01 - Abfälle aus dem Abbau von metallhaltigen Bodenschätzen',
      '01 01 02 - Abfälle aus dem Abbau von nichtmetallhaltigen Bodenschätzen',
      '01 03 04* - Säure bildende Aufbereitungsrückstände aus der Verarbeitung von sulfidischem Erz',
      '01 03 05* - andere Aufbereitungsrückstände, die gefährliche Stoffe enthalten',
      '01 03 06 - Aufbereitungsrückstände mit Ausnahme derjenigen, die unter 01 03 04 und 01 03 05 fallen',
      '01 03 07* - andere, gefährliche Stoffe enthaltende Abfälle aus der physikalischen und chemischen Verarbeitung von metallhaltigen Bodenschätzen',
      '01 03 08 - staubende und pulvrige Abfälle mit Ausnahme derjenigen, die unter 01 03 07 fallen',
      '01 03 09 - Rotschlamm aus der Aluminiumoxidherstellung mit Ausnahme von Abfällen, die unter 01 03 10 fallen',
      '01 03 10* - Rotschlamm aus der Aluminiumoxidherstellung, der gefährliche Stoffe enthält, mit Ausnahme der unter 01 03 07 genannten Abfälle',
      '01 03 99 - Abfälle a. n. g.',
      '01 04 07* - gefährliche Stoffe enthaltende Abfälle aus der physikalischen und chemischen Weiterverarbeitung von nichtmetallhaltigen Bodenschätzen',
      '01 04 08 - Abfälle von Kies- und Gesteinsbruch mit Ausnahme derjenigen, die unter 01 04 07 fallen',
      '01 04 09 - Abfälle von Sand und Ton',
      '01 04 10 - staubende und pulvrige Abfälle mit Ausnahme derjenigen, die unter 01 04 07 fallen',
      '01 04 11 - Abfälle aus der Verarbeitung von Kali- und Steinsalz mit Ausnahme derjenigen, die unter 01 04 07 fallen',
      '01 04 12 - Aufbereitungsrückstände und andere Abfälle aus der Wäsche und Reinigung von Bodenschätzen mit Ausnahme derjenigen, die unter 01 04 07 und 01 04 11 fallen',
      '01 04 13 - Abfälle aus Steinmetz- und -sägearbeiten mit Ausnahme derjenigen, die unter 01 04 07 fallen',
      '01 04 99 - Abfälle a. n. g.',
      '01 05 04 - Schlämme und Abfälle aus Süßwasserbohrungen',
      '01 05 05* - ölhaltige Bohrschlämme und -abfälle',
      '01 05 06* - Bohrschlämme und andere Bohrabfälle, die gefährliche Stoffe enthalten',
      '01 05 07 - barythaltige Bohrschlämme und -abfälle mit Ausnahme derjenigen, die unter 01 05 05 und 01 05 06 fallen',
      '01 05 08 - chloridhaltige Bohrschlämme und -abfälle mit Ausnahme derjenigen, die unter 01 05 05 und 01 05 06 fallen',
      '01 05 99 - Abfälle a. n. g.',
      '02 01 01 - Schlämme von Wasch- und Reinigungsvorgängen',
      '02 01 02 - Abfälle aus tierischem Gewebe',
      '02 01 03 - Abfälle aus pflanzlichem Gewebe',
      '02 01 04 - Kunststoffabfälle (ohne Verpackungen)',
      '02 01 06 - tierische Ausscheidungen, Gülle/Jauche und Stallmist (einschließlich verdorbenes Stroh), Abwässer, getrennt gesammelt und extern behandelt',
      '02 01 07 - Abfälle aus der Forstwirtschaft',
      '02 01 08* - Abfälle von Chemikalien für die Landwirtschaft, die gefährliche Stoffe enthalten',
      '02 01 09 - Abfälle von Chemikalien für die Landwirtschaft mit Ausnahme derjenigen, die unter 02 01 08 fallen',
      '02 01 10 - Metallabfälle',
      '02 01 99 - Abfälle a. n. g.',
      '02 02 01 - Schlämme von Wasch- und Reinigungsvorgängen',
      '02 02 02 - Abfälle aus tierischem Gewebe',
      '02 02 03 - für Verzehr oder Verarbeitung ungeeignete Stoffe',
      '02 02 04 - Schlämme aus der betriebseigenen Abwasserbehandlung',
      '02 02 99 - Abfälle a. n. g.',
      '02 03 01 - Schlämme aus Wasch-, Reinigungs-, Schäl-, Zentrifugier- und Abtrennprozessen',
      '02 03 02 - Abfälle von Konservierungsstoffen',
      '02 03 03 - Abfälle aus der Extraktion mit Lösemitteln',
      '02 03 04 - für Verzehr oder Verarbeitung ungeeignete Stoffe',
      '02 03 05 - Schlämme aus der betriebseigenen Abwasserbehandlung',
      '02 03 99 - Abfälle a. n. g.',
      '02 04 01 - Rübenerde',
      '02 04 02 - nicht spezifikationsgerechter Calciumcarbonatschlamm',
      '02 04 03 - Schlämme aus der betriebseigenen Abwasserbehandlung',
      '02 04 99 - Abfälle a. n. g.',
      '02 05 01 - für Verzehr oder Verarbeitung ungeeignete Stoffe',
      '02 05 02 - Schlämme aus der betriebseigenen Abwasserbehandlung',
      '02 05 99 - Abfälle a. n. g.',
      '02 06 01 - für Verzehr oder Verarbeitung ungeeignete Stoffe',
      '02 06 02 - Abfälle von Konservierungsstoffen',
      '02 06 03 - Schlämme aus der betriebseigenen Abwasserbehandlung',
      '02 06 99 - Abfälle a. n. g.',
      '02 07 01 - Abfälle aus der Wäsche, Reinigung und mechanischen Zerkleinerung des Rohmaterials',
      '02 07 02 - Abfälle aus der Alkoholdestillation',
      '02 07 03 - Abfälle aus der chemischen Behandlung',
      '02 07 04 - für Verzehr oder Verarbeitung ungeeignete Stoffe',
      '02 07 05 - Schlämme aus der betriebseigenen Abwasserbehandlung',
      '02 07 99 - Abfälle a. n. g.',
      '03 01 01 - Rinden und Korkabfälle',
      '03 01 04* - Sägemehl, Späne, Abschnitte, Holz, Spanplatten und Furniere, die gefährliche Stoffe enthalten',
      '03 01 05 - Sägemehl, Späne, Abschnitte, Holz, Spanplatten und Furniere mit Ausnahme derjenigen, die unter 03 01 04 fallen',
      '03 01 99 - Abfälle a. n. g.',
      '03 02 01* - halogenfreie organische Holzschutzmittel',
      '03 02 02* - chlororganische Holzschutzmittel',
      '03 02 03* - metallorganische Holzschutzmittel',
      '03 02 04* - anorganische Holzschutzmittel',
      '03 02 05* - andere Holzschutzmittel, die gefährliche Stoffe enthalten',
      '03 02 99 - Holzschutzmittel a. n. g.',
      '03 03 01 - Rinden- und Holzabfälle',
      '03 03 02 - Sulfitschlämme (aus der Rückgewinnung von Kochlaugen)',
      '03 03 05 - De-inking-Schlämme aus dem Papierrecycling',
      '03 03 07 - mechanisch abgetrennte Abfälle aus der Auflösung von Papier- und Pappabfällen',
      '03 03 08 - Abfälle aus dem Sortieren von Papier und Pappe für das Recycling',
      '03 03 09 - Kalkschlammabfälle',
      '03 03 10 - Faserabfälle, Faser-, Füller- und Überzugsschlämme aus der mechanischen Abtrennung',
      '03 03 11 - Schlämme aus der betriebseigenen Abwasserbehandlung mit Ausnahme derjenigen, die unter 03 03 10 fallen',
      '03 03 99 - Abfälle a. n. g.',
      '04 01 01 - Fleischabschabungen und Häuteabfälle',
      '04 01 02 - geäschertes Leimleder',
      '04 01 03* - Entfettungsabfälle, lösemittelhaltig, ohne flüssige Phase',
      '04 01 04 - chromhaltige Gerbereibrühe',
      '04 01 05 - chromfreie Gerbereibrühe',
      '04 01 06 - chromhaltige Schlämme, insbesondere aus der betriebseigenen Abwasserbehandlung',
      '04 01 07 - chromfreie Schlämme, insbesondere aus der betriebseigenen Abwasserbehandlung',
      '04 01 08 - chromhaltige Abfälle aus gegerbtem Leder (Abschnitte, Schleifstaub, Falzspäne)',
      '04 01 09 - Abfälle aus der Zurichtung und dem Finish',
      '04 01 99 - Abfälle a. n. g.',
      '04 02 09 - Abfälle aus Verbundmaterialien (imprägnierte Textilien, Elastomer, Plastomer)',
      '04 02 10 - organische Stoffe aus Naturstoffen (z. B. Fette, Wachse)',
      '04 02 14* - Abfälle aus dem Finish, die organische Lösungsmittel enthalten',
      '04 02 15 - Abfälle aus dem Finish mit Ausnahme derjenigen, die unter 04 02 14 fallen',
      '04 02 16* - Farbstoffe und Pigmente, die gefährliche Stoffe enthalten',
      '04 02 17 - Farbstoffe und Pigmente mit Ausnahme derjenigen, die unter 04 02 16 fallen',
      '04 02 19* - Schlämme aus der betriebseigenen Abwasserbehandlung, die gefährliche Stoffe enthalten',
      '04 02 20 - Schlämme aus der betriebseigenen Abwasserbehandlung mit Ausnahme derjenigen, die unter 04 02 19 fallen',
      '04 02 21 - Abfälle aus unbehandelten Textilfasern',
      '04 02 22 - Abfälle aus verarbeiteten Textilfasern',
      '04 02 99 - Abfälle a. n. g.',
      '05 01 02* - Entsalzungsschlämme',
      '05 01 03* - Bodenschlämme aus Tanks',
      '05 01 04* - saure Alkylschlämme',
      '05 01 05* - verschüttetes Öl',
      '05 01 06* - ölhaltige Schlämme aus Betriebsvorgängen und Instandhaltung',
      '05 01 07* - Säureteere',
      '05 01 08* - andere Teere',
      '05 01 09* - Schlämme aus der betriebseigenen Abwasserbehandlung, die gefährliche Stoffe enthalten',
      '05 01 10 - Schlämme aus der betriebseigenen Abwasserbehandlung mit Ausnahme derjenigen, die unter 05 01 09 fallen',
      '05 01 11* - Abfälle aus der Brennstoffreinigung mit Basen',
      '05 01 12* - säurehaltige Öle',
      '05 01 13 - Schlämme aus der Kesselspeisewasseraufbereitung',
      '05 01 14 - Abfälle aus Kühlkolonnen',
      '05 01 15* - gebrauchte Filtertone',
      '05 01 16 - schwefelhaltige Abfälle aus der Ölentschwefelung',
      '05 01 17 - Bitumen',
      '05 01 99 - Abfälle a. n. g.',
      '05 06 01* - Säureteere',
      '05 06 03* - andere Teere',
      '05 06 04 - Abfälle aus Kühlkolonnen',
      '05 06 99 - Abfälle a. n. g.',
      '05 07 01* - quecksilberhaltige Abfälle',
      '05 07 02 - schwefelhaltige Abfälle',
      '05 07 99 - Abfälle a. n. g.',
      '06 01 01* - Schwefelsäure und schweflige Säure',
      '06 01 02* - Salzsäure',
      '06 01 03* - Flusssäure',
      '06 01 04* - Phosphorsäure und phosphorige Säure',
      '06 01 05* - Salpetersäure und salpetrige Säure',
      '06 01 06* - andere Säuren',
      '06 01 99 - Abfälle a. n. g.',
      '06 02 01* - Calciumhydroxid',
      '06 02 03* - Ammoniumhydroxid',
      '06 02 04* - Natrium- und Kaliumhydroxid',
      '06 02 05* - andere Basen',
      '06 02 99 - Abfälle a. n. g.',
      '06 03 11* - feste Salze und Lösungen, die Cyanid enthalten',
      '06 03 13* - feste Salze und Lösungen, die Schwermetalle enthalten',
      '06 03 14 - feste Salze und Lösungen mit Ausnahme derjenigen, die unter 06 03 11 und 06 03 13 fallen',
      '06 03 15* - Metalloxide, die Schwermetalle enthalten',
      '06 03 16 - Metalloxide mit Ausnahme derjenigen, die unter 06 03 15 fallen',
      '06 03 99 - Abfälle a. n. g.',
      '06 04 03* - arsenhaltige Abfälle',
      '06 04 04* - quecksilberhaltige Abfälle',
      '06 04 05* - Abfälle, die andere Schwermetalle enthalten',
      '06 04 99 - Abfälle a. n. g.',
      '06 05 02* - Schlämme aus der betriebseigenen Abwasserbehandlung, die gefährliche Stoffe enthalten',
      '06 05 03 - Schlämme aus der betriebseigenen Abwasserbehandlung mit Ausnahme derjenigen, die unter 06 05 02 fallen',
      '06 06 02* - Abfälle, die gefährliche Sulfide enthalten',
      '06 06 03 - sulfidhaltige Abfälle mit Ausnahme derjenigen, die unter 06 06 02 fallen',
      '06 06 99 - Abfälle a. n. g.',
      '06 07 01* - asbesthaltige Abfälle aus der Elektrolyse',
      '06 07 02* - Aktivkohle aus der Chlorherstellung',
      '06 07 03* - quecksilberhaltige Bariumsulfatschlämme',
      '06 07 04* - Lösungen und Säuren, z. B. Kontaktsäure',
      '06 07 99 - Abfälle a. n. g.',
      '06 08 02* - Abfälle, die gefährliche Chlorsilane enthalten',
      '06 08 99 - Abfälle a. n. g.',
      '06 09 02 - phosphorhaltige Schlacke',
      '06 09 03* - Reaktionsabfälle auf Calciumbasis, die gefährliche Stoffe enthalten oder durch gefährliche Stoffe verunreinigt sind',
      '06 09 04 - Reaktionsabfälle auf Calciumbasis mit Ausnahme derjenigen, die unter 06 09 03 fallen',
      '06 09 99 - Abfälle a. n. g.',
      '06 10 02* - Abfälle, die gefährliche Stoffe enthalten',
      '06 10 99 - Abfälle a. n. g.',
      '06 11 01 - Reaktionsabfälle auf Calciumbasis aus der Titandioxidherstellung',
      '06 11 99 - Abfälle a. n. g.',
      '06 13 01* - anorganische Pflanzenschutzmittel, Holzschutzmittel und andere Biozide',
      '06 13 02* - gebrauchte Aktivkohle (außer 06 07 02)',
      '06 13 03 - Industrieruß',
      '06 13 04* - Abfälle aus der Asbestverarbeitung',
      '06 13 05* - Ofen- und Kaminruß',
      '06 13 99 - Abfälle a. n. g.',
      '07 01 01* - wässrige Waschflüssigkeiten und Mutterlaugen',
      '07 01 03* - halogenorganische Lösemittel, Waschflüssigkeiten und Mutterlaugen',
      '07 01 04* - andere organische Lösemittel, Waschflüssigkeiten und Mutterlaugen',
      '07 01 07* - halogenierte Reaktions- und Destillationsrückstände',
      '07 01 08* - andere Reaktions- und Destillationsrückstände',
      '07 01 09* - halogenierte Filterkuchen, gebrauchte Aufsaugmaterialien',
      '07 01 10* - andere Filterkuchen, gebrauchte Aufsaugmaterialien',
      '07 01 11* - Schlämme aus der betriebseigenen Abwasserbehandlung, die gefährliche Stoffe enthalten',
      '07 01 12 - Schlämme aus der betriebseigenen Abwasserbehandlung mit Ausnahme derjenigen, die unter 07 01 11 fallen',
      '07 01 99 - Abfälle a. n. g.',
      '07 02 01* - wässrige Waschflüssigkeiten und Mutterlaugen',
      '07 02 03* - halogenorganische Lösemittel, Waschflüssigkeiten und Mutterlaugen',
      '07 02 04* - andere organische Lösemittel, Waschflüssigkeiten und Mutterlaugen',
      '07 02 07* - halogenierte Reaktions- und Destillationsrückstände',
      '07 02 08* - andere Reaktions- und Destillationsrückstände',
      '07 02 09* - halogenierte Filterkuchen, gebrauchte Aufsaugmaterialien',
      '07 02 10* - andere Filterkuchen, gebrauchte Aufsaugmaterialien',
      '07 02 11* - Schlämme aus der betriebseigenen Abwasserbehandlung, die gefährliche Stoffe enthalten',
      '07 02 12 - Schlämme aus der betriebseigenen Abwasserbehandlung mit Ausnahme derjenigen, die unter 07 02 11 fallen',
      '07 02 13 - Kunststoffabfälle',
      '07 02 14* - Abfälle von Zusatzstoffen, die gefährliche Stoffe enthalten',
      '07 02 15 - Abfälle von Zusatzstoffen mit Ausnahme derjenigen, die unter 07 02 14 fallen',
      '07 02 16* - "Abfälle; die gefährliche Silicone enthalten"',
      '07 02 17 - siliconhaltige Abfälle, andere als die in 07 02 16 genannten',
      '07 02 99 - Abfälle a. n. g.',
      '07 03 01* - wässrige Waschflüssigkeiten und Mutterlaugen',
      '07 03 03* - halogenorganische Lösemittel, Waschflüssigkeiten und Mutterlaugen',
      '07 03 04* - andere organische Lösemittel, Waschflüssigkeiten und Mutterlaugen',
      '07 03 07* - halogenierte Reaktions- und Destillationsrückstände',
      '07 03 08* - andere Reaktions- und Destillationsrückstände',
      '07 03 09* - halogenierte Filterkuchen, gebrauchte Aufsaugmaterialien',
      '07 03 10* - andere Filterkuchen, gebrauchte Aufsaugmaterialien',
      '07 03 11* - Schlämme aus der betriebseigenen Abwasserbehandlung, die gefährliche Stoffe enthalten',
      '07 03 12 - Schlämme aus der betriebseigenen Abwasserbehandlung mit Ausnahme derjenigen, die unter 07 03 11 fallen',
      '07 03 99 - Abfälle a. n. g.',
      '07 04 01* - wässrige Waschflüssigkeiten und Mutterlaugen',
      '07 04 03* - halogenorganische Lösemittel, Waschflüssigkeiten und Mutterlaugen',
      '07 04 04* - andere organische Lösemittel, Waschflüssigkeiten und Mutterlaugen',
      '07 04 07* - halogenierte Reaktions- und Destillationsrückstände',
      '07 04 08* - andere Reaktions- und Destillationsrückstände',
      '07 04 09* - halogenierte Filterkuchen, gebrauchte Aufsaugmaterialien',
      '07 04 10* - andere Filterkuchen, gebrauchte Aufsaugmaterialien',
      '07 04 11* - Schlämme aus der betriebseigenen Abwasserbehandlung, die gefährliche Stoffe enthalten',
      '07 04 12 - Schlämme aus der betriebseigenen Abwasserbehandlung mit Ausnahme derjenigen, die unter 07 04 11 fallen',
      '07 04 13* - feste Abfälle, die gefährliche Stoffe enthalten',
      '07 04 99 - Abfälle a. n. g.',
      '07 05 01* - wässrige Waschflüssigkeiten und Mutterlaugen',
      '07 05 03* - halogenorganische Lösemittel, Waschflüssigkeiten und Mutterlaugen',
      '07 05 04* - andere organische Lösemittel, Waschflüssigkeiten und Mutterlaugen',
      '07 05 07* - halogenierte Reaktions- und Destillationsrückstände',
      '07 05 08* - andere Reaktions- und Destillationsrückstände',
      '07 05 09* - halogenierte Filterkuchen, gebrauchte Aufsaugmaterialien',
      '07 05 10* - andere Filterkuchen, gebrauchte Aufsaugmaterialien',
      '07 05 11* - Schlämme aus der betriebseigenen Abwasserbehandlung, die gefährliche Stoffe enthalten',
      '07 05 12 - Schlämme aus der betriebseigenen Abwasserbehandlung mit Ausnahme derjenigen, die unter 07 05 11 fallen',
      '07 05 13* - feste Abfälle, die gefährliche Stoffe enthalten',
      '07 05 14 - feste Abfälle mit Ausnahme derjenigen, die unter 07 05 13 fallen',
      '07 05 99 - Abfälle a. n. g.',
      '07 06 01* - wässrige Waschflüssigkeiten und Mutterlaugen',
      '07 06 03* - halogenorganische Lösemittel, Waschflüssigkeiten und Mutterlaugen',
      '07 06 04* - andere organische Lösemittel, Waschflüssigkeiten und Mutterlaugen',
      '07 06 07* - halogenierte Reaktions- und Destillationsrückstände',
      '07 06 08* - andere Reaktions- und Destillationsrückstände',
      '07 06 09* - halogenierte Filterkuchen, gebrauchte Aufsaugmaterialien',
      '07 06 10* - andere Filterkuchen, gebrauchte Aufsaugmaterialien',
      '07 06 11* - Schlämme aus der betriebseigenen Abwasserbehandlung, die gefährliche Stoffe enthalten',
      '07 06 12 - Schlämme aus der betriebseigenen Abwasserbehandlung mit Ausnahme derjenigen, die unter 07 06 11 fallen',
      '07 06 99 - Abfälle a. n. g.',
      '07 07 01* - wässrige Waschflüssigkeiten und Mutterlaugen',
      '07 07 03* - halogenorganische Lösemittel, Waschflüssigkeiten und Mutterlaugen',
      '07 07 04* - andere organische Lösemittel, Waschflüssigkeiten und Mutterlaugen',
      '07 07 07* - halogenierte Reaktions- und Destillationsrückstände',
      '07 07 08* - andere Reaktions- und Destillationsrückstände',
      '07 07 09* - halogenierte Filterkuchen, gebrauchte Aufsaugmaterialien',
      '07 07 10* - andere Filterkuchen, gebrauchte Aufsaugmaterialien',
      '07 07 11* - Schlämme aus der betriebseigenen Abwasserbehandlung, die gefährliche Stoffe enthalten',
      '07 07 12 - Schlämme aus der betriebseigenen Abwasserbehandlung mit Ausnahme derjenigen, die unter 07 07 11 fallen',
      '07 07 99 - Abfälle a. n. g.',
      '08 01 11* - Farb- und Lackabfälle, die organische Lösemittel oder andere gefährliche Stoffe enthalten',
      '08 01 12 - Farb- und Lackabfälle mit Ausnahme derjenigen, die unter 08 01 11 fallen',
      '08 01 13* - Farb- und Lackschlämme, die organische Lösemittel oder andere gefährliche Stoffe enthalten',
      '08 01 14 - Farb- und Lackschlämme mit Ausnahme derjenigen, die unter 08 01 13 fallen',
      '08 01 15* - wässrige Schlämme, die Farben oder Lacke mit organischen Lösemitteln oder anderen gefährlichen Stoffen enthalten',
      '08 01 16 - wässrige Schlämme, die Farben oder Lacke enthalten, mit Ausnahme derjenigen, die unter 08 01 15 fallen',
      '08 01 17* - Abfälle aus der Farb- oder Lackentfernung, die organische Lösemittel oder andere gefährliche Stoffe enthalten',
      '08 01 18 - Abfälle aus der Farb- oder Lackentfernung mit Ausnahme derjenigen, die unter 08 01 17 fallen',
      '08 01 19* - wässrige Suspensionen, die Farben oder Lacke mit organischen Lösemitteln oder anderen gefährlichen Stoffen enthalten',
      '08 01 20 - wässrige Suspensionen, die Farben oder Lacke enthalten, mit Ausnahme derjenigen, die unter 08 01 19 fallen',
      '08 01 21* - Farb- oder Lackentfernerabfälle',
      '08 01 99 - Abfälle a. n. g.',
      '08 02 01 - Abfälle von Beschichtungspulver',
      '08 02 02 - wässrige Schlämme, die keramische Werkstoffe enthalten',
      '08 02 03 - wässrige Suspensionen, die keramische Werkstoffe enthalten',
      '08 02 99 - Abfälle a. n. g.',
      '08 03 07 - wässrige Schlämme, die Druckfarben enthalten',
      '08 03 08 - wässrige flüssige Abfälle, die Druckfarben enthalten',
      '08 03 12* - Druckfarbenabfälle, die gefährliche Stoffe enthalten',
      '08 03 13 - Druckfarbenabfälle mit Ausnahme derjenigen, die unter 08 03 12 fallen',
      '08 03 14* - Druckfarbenschlämme, die gefährliche Stoffe enthalten',
      '08 03 15 - Druckfarbenschlämme mit Ausnahme derjenigen, die unter 08 03 14 fallen',
      '08 03 16* - Abfälle von Ätzlösungen',
      '08 03 17* - Tonerabfälle, die gefährliche Stoffe enthalten',
      '08 03 18 - Tonerabfälle mit Ausnahme derjenigen, die unter 08 03 17 fallen',
      '08 03 19* - Dispersionsöl',
      '08 03 99 - Abfälle a. n. g.',
      '08 04 09* - Klebstoff- und Dichtmassenabfälle, die organische Lösemittel oder andere gefährliche Stoffe enthalten',
      '08 04 10 - Klebstoff- und Dichtmassenabfälle mit Ausnahme derjenigen, die unter 08 04 09 fallen',
      '08 04 11* - klebstoff- und dichtmassenhaltige Schlämme, die organische Lösemittel oder andere gefährliche Stoffe enthalten',
      '08 04 12 - klebstoff- und dichtmassenhaltige Schlämme mit Ausnahme derjenigen, die unter 08 04 11 fallen',
      '08 04 13* - wässrige Schlämme, die Klebstoffe oder Dichtmassen mit organischen Lösemitteln oder anderen gefährlichen Stoffen enthalten',
      '08 04 14 - wässrige Schlämme, die Klebstoffe oder Dichtmassen enthalten, mit Ausnahme derjenigen, die unter 08 04 13 fallen',
      '08 04 15* - wässrige flüssige Abfälle, die Klebstoffe oder Dichtmassen mit organischen Lösemitteln oder anderen gefährlichen Stoffen enthalten',
      '08 04 16 - wässrige flüssige Abfälle, die Klebstoffe oder Dichtmassen enthalten, mit Ausnahme derjenigen, die unter 08 04 15 fallen',
      '08 04 17* - Harzöle',
      '08 04 99 - Abfälle a. n. g.',
      '08 05 01* - Isocyanatabfälle',
      '09 01 01* - Entwickler und Aktivatorenlösungen auf Wasserbasis',
      '09 01 02* - Offsetdruckplatten-Entwicklerlösungen auf Wasserbasis',
      '09 01 03* - Entwicklerlösungen auf Lösemittelbasis',
      '09 01 04* - Fixierbäder',
      '09 01 05* - Bleichlösungen und Bleich-Fixier-Bäder',
      '09 01 06* - silberhaltige Abfälle aus der betriebseigenen Behandlung fotografischer Abfälle',
      '09 01 07 - Filme und fotografische Papiere, die Silber oder Silberverbindungen enthalten',
      '09 01 08 - Filme und fotografische Papiere, die kein Silber und keine Silberverbindungen enthalten',
      '09 01 10 - Einwegkameras ohne Batterien',
      '09 01 11* - Einwegkameras mit Batterien, die unter 16 06 01, 16 06 02 oder 16 06 03 fallen',
      '09 01 12 - Einwegkameras mit Batterien mit Ausnahme derjenigen, die unter 09 01 11 fallen',
      '09 01 13* - wässrige flüssige Abfälle aus der betriebseigenen Silberrückgewinnung mit Ausnahme derjenigen, die unter 09 01 06 fallen',
      '09 01 99 - Abfälle a. n. g.',
      '10 01 01 - Rost- und Kesselasche, Schlacken und Kesselstaub mit Ausnahme von Kesselstaub, der unter 10 01 04 fällt',
      '10 01 02 - Filterstäube aus Kohlefeuerung',
      '10 01 03 - Filterstäube aus Torffeuerung und Feuerung mit (unbehandeltem) Holz',
      '10 01 04* - Filterstäube und Kesselstaub aus Ölfeuerung',
      '10 01 05 - Reaktionsabfälle auf Calciumbasis aus der Rauchgasentschwefelung in fester Form',
      '10 01 07 - Reaktionsabfälle auf Calciumbasis aus der Rauchgasentschwefelung in Form von Schlämmen',
      '10 01 09* - Schwefelsäure',
      '10 01 13* - Filterstäube aus emulgierten, als Brennstoffe verwendeten Kohlenwasserstoffen',
      '10 01 14* - Rost- und Kesselasche, Schlacken und Kesselstaub aus der Abfallmitverbrennung, die gefährliche Stoffe enthalten',
      '10 01 15 - Rost- und Kesselasche, Schlacken und Kesselstaub aus der Abfallmitverbrennung mit Ausnahme derjenigen, die unter 10 01 14 fallen',
      '10 01 16* - Filterstäube aus der Abfallmitverbrennung, die gefährliche Stoffe enthalten',
      '10 01 17 - Filterstäube aus der Abfallmitverbrennung mit Ausnahme derjenigen, die unter 10 01 16 fallen',
      '10 01 18* - Abfälle aus der Abgasbehandlung, die gefährliche Stoffe enthalten',
      '10 01 19 - Abfälle aus der Abgasbehandlung mit Ausnahme derjenigen, die unter 10 01 05, 10 01 07 und 10 01 18 fallen',
      '10 01 20* - Schlämme aus der betriebseigenen Abwasserbehandlung, die gefährliche Stoffe enthalten',
      '10 01 21 - Schlämme aus der betriebseigenen Abwasserbehandlung mit Ausnahme derjenigen, die unter 10 01 20 fallen',
      '10 01 22* - wässrige Schlämme aus der Kesselreinigung, die gefährliche Stoffe enthalten',
      '10 01 23 - wässrige Schlämme aus der Kesselreinigung mit Ausnahme derjenigen, die unter 10 01 22 fallen',
      '10 01 24 - Sande aus der Wirbelschichtfeuerung',
      '10 01 25 - Abfälle aus der Lagerung und Vorbereitung von Brennstoffen für Kohlekraftwerke',
      '10 01 26 - Abfälle aus der Kühlwasserbehandlung',
      '10 01 99 - Abfälle a. n. g.',
      '10 02 01 - Abfälle aus der Verarbeitung von Schlacke',
      '10 02 02 - unbearbeitete Schlacke',
      '10 02 07* - feste Abfälle aus der Abgasbehandlung, die gefährliche Stoffe enthalten',
      '10 02 08 - feste Abfälle aus der Abgasbehandlung mit Ausnahme derjenigen, die unter 10 02 07 fallen',
      '10 02 10 - Walzzunder',
      '10 02 11* - ölhaltige Abfälle aus der Kühlwasserbehandlung',
      '10 02 12 - Abfälle aus der Kühlwasserbehandlung mit Ausnahme derjenigen, die unter 10 02 11 fallen',
      '10 02 13* - Schlämme und Filterkuchen aus der Abgasbehandlung, die gefährliche Stoffe enthalten',
      '10 02 14 - Schlämme und Filterkuchen aus der Abgasbehandlung mit Ausnahme derjenigen, die unter 10 02 13 fallen',
      '10 02 15 - andere Schlämme und Filterkuchen',
      '10 02 99 - Abfälle a. n. g.',
      '10 03 02 - Anodenschrott',
      '10 03 04* - Schlacken aus der Erstschmelze',
      '10 03 05 - Aluminiumoxidabfälle',
      '10 03 08* - Salzschlacken aus der Zweitschmelze',
      '10 03 09* - schwarze Krätzen aus der Zweitschmelze',
      '10 03 15* - Abschaum, der entzündlich ist oder in Kontakt mit Wasser entzündliche Gase in gefährlicher Menge abgibt',
      '10 03 16 - Abschaum mit Ausnahme desjenigen, der unter 10 03 15 fällt',
      '10 03 17* - teerhaltige Abfälle aus der Anodenherstellung',
      '10 03 18 - Abfälle aus der Anodenherstellung, die Kohlenstoff enthalten, mit Ausnahme derjenigen, die unter 10 03 17 fallen',
      '10 03 19* - Filterstaub, der gefährliche Stoffe enthält',
      '10 03 20 - Filterstaub mit Ausnahme von Filterstaub, der unter 10 03 19 fällt',
      '10 03 21* - andere Teilchen und Staub (einschließlich Kugelmühlenstaub), die gefährliche Stoffe enthalten',
      '10 03 22 - andere Teilchen und Staub (einschließlich Kugelmühlenstaub) mit Ausnahme derjenigen, die unter 10 03 21 fallen',
      '10 03 23* - feste Abfälle aus der Abgasbehandlung, die gefährliche Stoffe enthalten',
      '10 03 24 - feste Abfälle aus der Abgasbehandlung mit Ausnahme derjenigen, die unter 10 03 23 fallen',
      '10 03 25* - Schlämme und Filterkuchen aus der Abgasbehandlung, die gefährliche Stoffe enthalten',
      '10 03 26 - Schlämme und Filterkuchen aus der Abgasbehandlung mit Ausnahme derjenigen, die unter 10 03 25 fallen',
      '10 03 27* - ölhaltige Abfälle aus der Kühlwasserbehandlung',
      '10 03 28 - Abfälle aus der Kühlwasserbehandlung mit Ausnahme derjenigen, die unter 10 03 27 fallen',
      '10 03 29* - gefährliche Stoffe enthaltende Abfälle aus der Behandlung von Salzschlacken und schwarzen Krätzen',
      '10 03 30 - Abfälle aus der Behandlung von Salzschlacken und schwarzen Krätzen mit Ausnahme derjenigen, die unter 10 03 29 fallen',
      '10 03 99 - Abfälle a. n. g.',
      '10 04 01* - Schlacken (Erst- und Zweitschmelze)',
      '10 04 02* - Krätzen und Abschaum (Erst- und Zweitschmelze)',
      '10 04 03* - Calciumarsenat',
      '10 04 04* - Filterstaub',
      '10 04 05* - andere Teilchen und Staub',
      '10 04 06* - feste Abfälle aus der Abgasbehandlung',
      '10 04 07* - Schlämme und Filterkuchen aus der Abgasbehandlung',
      '10 04 09* - ölhaltige Abfälle aus der Kühlwasserbehandlung',
      '10 04 10 - Abfälle aus der Kühlwasserbehandlung mit Ausnahme derjenigen, die unter 10 04 09 fallen',
      '10 04 99 - Abfälle a. n. g.',
      '10 05 01 - Schlacken (Erst- und Zweitschmelze)',
      '10 05 03* - Filterstaub',
      '10 05 04 - andere Teilchen und Staub',
      '10 05 05* - feste Abfälle aus der Abgasbehandlung',
      '10 05 06* - Schlämme und Filterkuchen aus der Abgasbehandlung',
      '10 05 08* - ölhaltige Abfälle aus der Kühlwasserbehandlung',
      '10 05 09 - Abfälle aus der Kühlwasserbehandlung mit Ausnahme derjenigen, die unter 10 05 08 fallen',
      '10 05 10* - Krätzen und Abschaum, die entzündlich sind oder in Kontakt mit Wasser entzündliche Gase in gefährlicher Menge abgeben',
      '10 05 11 - Krätzen und Abschaum mit Ausnahme derjenigen, die unter 10 05 10 fallen',
      '10 05 99 - Abfälle a. n. g.',
      '10 06 01 - Schlacken (Erst- und Zweitschmelze)',
      '10 06 02 - Krätzen und Abschaum (Erst- und Zweitschmelze)',
      '10 06 03* - Filterstaub',
      '10 06 04 - andere Teilchen und Staub',
      '10 06 06* - feste Abfälle aus der Abgasbehandlung',
      '10 06 07* - Schlämme und Filterkuchen aus der Abgasbehandlung',
      '10 06 09* - ölhaltige Abfälle aus der Kühlwasserbehandlung',
      '10 06 10 - Abfälle aus der Kühlwasserbehandlung mit Ausnahme derjenigen, die unter 10 06 09 fallen',
      '10 06 99 - Abfälle a. n. g.',
      '10 07 01 - Schlacken (Erst- und Zweitschmelze)',
      '10 07 02 - Krätzen und Abschaum (Erst- und Zweitschmelze)',
      '10 07 03 - feste Abfälle aus der Abgasbehandlung',
      '10 07 04 - andere Teilchen und Staub',
      '10 07 05 - Schlämme und Filterkuchen aus der Abgasbehandlung',
      '10 07 07* - ölhaltige Abfälle aus der Kühlwasserbehandlung',
      '10 07 08 - Abfälle aus der Kühlwasserbehandlung mit Ausnahme derjenigen, die unter 10 07 07 fallen',
      '10 07 99 - Abfälle a. n. g.',
      '10 08 04 - Teilchen und Staub',
      '10 08 08* - Salzschlacken (Erst- und Zweitschmelze)',
      '10 08 09 - andere Schlacken',
      '10 08 10* - Krätzen und Abschaum, die entzündlich sind oder in Kontakt mit Wasser entzündliche Gase in gefährlicher Menge abgeben',
      '10 08 11 - Krätzen und Abschaum mit Ausnahme derjenigen, die unter 10 08 10 fallen',
      '10 08 12* - teerhaltige Abfälle aus der Anodenherstellung',
      '10 08 13 - Abfälle aus der Anodenherstellung, die Kohlenstoff enthalten, mit Ausnahme derjenigen, die unter 10 08 12 fallen',
      '10 08 14 - Anodenschrott',
      '10 08 15* - Filterstaub, der gefährliche Stoffe enthält',
      '10 08 16 - Filterstaub mit Ausnahme desjenigen, der unter 10 08 15 fällt',
      '10 08 17* - Schlämme und Filterkuchen aus der Abgasbehandlung, die gefährliche Stoffe enthalten',
      '10 08 18 - Schlämme und Filterkuchen aus der Abgasbehandlung mit Ausnahme derjenigen, die unter 10 08 17 fallen',
      '10 08 19* - ölhaltige Abfälle aus der Kühlwasserbehandlung',
      '10 08 20 - Abfälle aus der Kühlwasserbehandlung mit Ausnahme derjenigen, die unter 10 08 19 fallen',
      '10 08 99 - Abfälle a. n. g.',
      '10 09 03 - Ofenschlacke',
      '10 09 05* - gefährliche Stoffe enthaltende Gießformen und -sande vor dem Gießen',
      '10 09 06 - Gießformen und -sande vor dem Gießen mit Ausnahme derjenigen, die unter 10 09 05 fallen',
      '10 09 07* - gefährliche Stoffe enthaltende Gießformen und -sande nach dem Gießen',
      '10 09 08 - Gießformen und -sande nach dem Gießen mit Ausnahme derjenigen, die unter 10 09 07 fallen',
      '10 09 09* - Filterstaub, der gefährliche Stoffe enthält',
      '10 09 10 - Filterstaub mit Ausnahme desjenigen, der unter 10 09 09 fällt',
      '10 09 11* - andere Teilchen, die gefährliche Stoffe enthalten',
      '10 09 12 - andere Teilchen mit Ausnahme derjenigen, die unter 10 09 11 fallen',
      '10 09 13* - Abfälle von Bindemitteln, die gefährliche Stoffe enthalten',
      '10 09 14 - Abfälle von Bindemitteln mit Ausnahme derjenigen, die unter 10 09 13 fallen',
      '10 09 15* - Abfälle aus rissanzeigenden Substanzen, die gefährliche Stoffe enthalten',
      '10 09 16 - Abfälle aus rissanzeigenden Substanzen mit Ausnahme derjenigen, die unter 10 09 15 fallen',
      '10 09 99 - Abfälle a. n. g.',
      '10 10 03 - Ofenschlacke',
      '10 10 05* - gefährliche Stoffe enthaltende Gießformen und -sande vor dem Gießen',
      '10 10 06 - Gießformen und -sande vor dem Gießen mit Ausnahme derjenigen, die unter 10 10 05 fallen',
      '10 10 07* - gefährliche Stoffe enthaltende Gießformen und -sande nach dem Gießen',
      '10 10 08 - Gießformen und -sande nach dem Gießen mit Ausnahme derjenigen, die unter 10 10 07 fallen',
      '10 10 09* - Filterstaub, der gefährliche Stoffe enthält',
      '10 10 10 - Filterstaub mit Ausnahme desjenigen, der unter 10 10 09 fällt',
      '10 10 11* - andere Teilchen, die gefährliche Stoffe enthalten',
      '10 10 12 - andere Teilchen mit Ausnahme derjenigen, die unter 10 10 11 fallen',
      '10 10 13* - Abfälle von Bindemitteln, die gefährliche Stoffe enthalten',
      '10 10 14 - Abfälle von Bindemitteln mit Ausnahme derjenigen, die unter 10 10 13 fallen',
      '10 10 15* - Abfälle aus rissanzeigenden Substanzen, die gefährliche Stoffe enthalten',
      '10 10 16 - Abfälle aus rissanzeigenden Substanzen mit Ausnahme derjenigen, die unter 10 10 15 fallen',
      '10 10 99 - Abfälle a. n. g.',
      '10 11 03 - Glasfaserabfall',
      '10 11 05 - Teilchen und Staub',
      '10 11 09* - Gemengeabfall mit gefährlichen Stoffen vor dem Schmelzen',
      '10 11 10 - Gemengeabfall vor dem Schmelzen mit Ausnahme desjenigen, der unter 10 11 09 fällt',
      '10 11 11* - Glasabfall in kleinen Teilchen und Glasstaub, die Schwermetalle enthalten (z. B. aus Kathodenstrahlröhren)',
      '10 11 12 - Glasabfall mit Ausnahme desjenigen, der unter 10 11 11 fällt',
      '10 11 13* - Glaspolier- und Glasschleifschlämme, die gefährliche Stoffe enthalten',
      '10 11 14 - Glaspolier- und Glasschleifschlämme mit Ausnahme derjenigen, die unter 10 11 13 fallen',
      '10 11 15* - feste Abfälle aus der Abgasbehandlung, die gefährliche Stoffe enthalten',
      '10 11 16 - feste Abfälle aus der Abgasbehandlung mit Ausnahme derjenigen, die unter 10 11 15 fallen',
      '10 11 17* - Schlämme und Filterkuchen aus der Abgasbehandlung, die gefährliche Stoffe enthalten',
      '10 11 18 - Schlämme und Filterkuchen aus der Abgasbehandlung mit Ausnahme derjenigen, die unter 10 11 17 fallen',
      '10 11 19* - feste Abfälle aus der betriebseigenen Abwasserbehandlung, die gefährliche Stoffe enthalten',
      '10 11 20 - feste Abfälle aus der betriebseigenen Abwasserbehandlung mit Ausnahme derjenigen, die unter 10 11 19 fallen',
      '10 11 99 - Abfälle a. n. g.',
      '10 12 01 - Rohmischungen vor dem Brennen',
      '10 12 03 - Teilchen und Staub',
      '10 12 05 - Schlämme und Filterkuchen aus der Abgasbehandlung',
      '10 12 06 - verworfene Formen',
      '10 12 08 - Abfälle aus Keramikerzeugnissen, Ziegeln, Fliesen und Steinzeug (nach dem Brennen)',
      '10 12 09* - feste Abfälle aus der Abgasbehandlung, die gefährliche Stoffe enthalten',
      '10 12 10 - feste Abfälle aus der Abgasbehandlung mit Ausnahme derjenigen, die unter 10 12 09 fallen',
      '10 12 11* - Glasurabfälle, die Schwermetalle enthalten',
      '10 12 12 - Glasurabfälle mit Ausnahme derjenigen, die unter 10 12 11 fallen',
      '10 12 13 - Schlämme aus der betriebseigenen Abwasserbehandlung',
      '10 12 99 - Abfälle a. n. g.',
      '10 13 01 - Abfälle von Rohgemenge vor dem Brennen',
      '10 13 04 - Abfälle aus der Kalzinierung und Hydratisierung von Branntkalk',
      '10 13 06 - Teilchen und Staub (außer 10 13 12 und 10 13 13)',
      '10 13 07 - Schlämme und Filterkuchen aus der Abgasbehandlung',
      '10 13 09* - asbesthaltige Abfälle aus der Herstellung von Asbestzement',
      '10 13 10 - Abfälle aus der Herstellung von Asbestzement mit Ausnahme derjenigen, die unter 10 13 09 fallen',
      '10 13 11 - Abfälle aus der Herstellung anderer Verbundstoffe auf Zementbasis mit Ausnahme derjenigen, die unter 10 13 09 und 10 13 10 fallen',
      '10 13 12* - feste Abfälle aus der Abgasbehandlung, die gefährliche Stoffe enthalten',
      '10 13 13 - feste Abfälle aus der Abgasbehandlung mit Ausnahme derjenigen, die unter 10 13 12 fallen',
      '10 13 14 - Betonabfälle und Betonschlämme',
      '10 13 99 - Abfälle a. n. g.',
      '10 14 01* - quecksilberhaltige Abfälle aus der Gasreinigung',
      '11 01 05* - saure Beizlösungen',
      '11 01 06* - Säuren a. n. g.',
      '11 01 07* - alkalische Beizlösungen',
      '11 01 08* - Phosphatierschlämme',
      '11 01 09* - Schlämme und Filterkuchen, die gefährliche Stoffe enthalten',
      '11 01 10 - Schlämme und Filterkuchen mit Ausnahme derjenigen, die unter 11 01 09 fallen',
      '11 01 11* - wässrige Spülflüssigkeiten, die gefährliche Stoffe enthalten',
      '11 01 12 - wässrige Spülflüssigkeiten mit Ausnahme derjenigen, die unter 11 01 11 fallen',
      '11 01 13* - Abfälle aus der Entfettung, die gefährliche Stoffe enthalten',
      '11 01 14 - Abfälle aus der Entfettung mit Ausnahme derjenigen, die unter 11 01 13 fallen',
      '11 01 15* - Eluate und Schlämme aus Membransystemen oder Ionenaustauschsystemen, die gefährliche Stoffe enthalten',
      '11 01 16* - gesättigte oder verbrauchte Ionenaustauscherharze',
      '11 01 98* - andere Abfälle, die gefährliche Stoffe enthalten',
      '11 01 99 - Abfälle a. n. g.',
      '11 02 02* - Schlämme aus der Zink-Hydrometallurgie (einschließlich Jarosit, Goethit)',
      '11 02 03 - Abfälle aus der Herstellung von Anoden für wässrige elektrolytische Prozesse',
      '11 02 05* - Abfälle aus Prozessen der Kupfer-Hydrometallurgie, die gefährliche Stoffe enthalten',
      '11 02 06 - Abfälle aus Prozessen der Kupfer-Hydrometallurgie mit Ausnahme derjenigen, die unter 11 02 05 fallen',
      '11 02 07* - andere Abfälle, die gefährliche Stoffe enthalten',
      '11 02 99 - Abfälle a. n. g.',
      '11 03 01* - cyanidhaltige Abfälle',
      '11 03 02* - andere Abfälle',
      '11 05 01 - Hartzink',
      '11 05 02 - Zinkasche',
      '11 05 03* - feste Abfälle aus der Abgasbehandlung',
      '11 05 04* - gebrauchte Flussmittel',
      '11 05 99 - Abfälle a. n. g.',
      '12 01 01 - Eisenfeil- und -drehspäne',
      '12 01 02 - Eisenstaub und -teilchen',
      '12 01 03 - NE-Metallfeil- und -drehspäne',
      '12 01 04 - NE-Metallstaub und -teilchen',
      '12 01 05 - Kunststoffspäne und -drehspäne',
      '12 01 06* - halogenhaltige Bearbeitungsöle auf Mineralölbasis (außer Emulsionen und Lösungen)',
      '12 01 07* - halogenfreie Bearbeitungsöle auf Mineralölbasis (außer Emulsionen und Lösungen)',
      '12 01 08* - halogenhaltige Bearbeitungsemulsionen und -lösungen',
      '12 01 09* - halogenfreie Bearbeitungsemulsionen und -lösungen',
      '12 01 10* - synthetische Bearbeitungsöle',
      '12 01 12* - gebrauchte Wachse und Fette',
      '12 01 13 - Schweißabfälle',
      '12 01 14* - Bearbeitungsschlämme, die gefährliche Stoffe enthalten',
      '12 01 15 - Bearbeitungsschlämme mit Ausnahme derjenigen, die unter 12 01 14 fallen',
      '12 01 16* - Strahlmittelabfälle, die gefährliche Stoffe enthalten',
      '12 01 17 - Strahlmittelabfälle mit Ausnahme derjenigen, die unter 12 01 16 fallen',
      '12 01 18* - ölhaltige Metallschlämme (Schleif-, Hon- und Läppschlämme)',
      '12 01 19* - biologisch leicht abbaubare Bearbeitungsöle',
      '12 01 20* - gebrauchte Hon- und Schleifmittel, die gefährliche Stoffe enthalten',
      '12 01 21 - gebrauchte Hon- und Schleifmittel mit Ausnahme derjenigen, die unter 12 01 20 fallen',
      '12 01 99 - Abfälle a. n. g.',
      '12 03 01* - wässrige Waschflüssigkeiten',
      '12 03 02* - Abfälle aus der Dampfentfettung',
      '13 01 01* - Hydrauliköle, die PCB enthalten',
      '13 01 04* - chlorierte Emulsionen',
      '13 01 05* - nichtchlorierte Emulsionen',
      '13 01 09* - chlorierte Hydrauliköle auf Mineralölbasis',
      '13 01 10* - nichtchlorierte Hydrauliköle auf Mineralölbasis',
      '13 01 11* - synthetische Hydrauliköle',
      '13 01 12* - biologisch leicht abbaubare Hydrauliköle',
      '13 01 13* - andere Hydrauliköle',
      '13 02 04* - chlorierte Maschinen-, Getriebe- und Schmieröle auf Mineralölbasis',
      '13 02 05* - nichtchlorierte Maschinen-, Getriebe- und Schmieröle auf Mineralölbasis',
      '13 02 06* - synthetische Maschinen-, Getriebe- und Schmieröle',
      '13 02 07* - biologisch leicht abbaubare Maschinen-, Getriebe- und Schmieröle',
      '13 02 08* - andere Maschinen-, Getriebe- und Schmieröle',
      '13 03 01* - Isolier- und Wärmeübertragungsöle, die PCB enthalten',
      '13 03 06* - chlorierte Isolier- und Wärmeübertragungsöle auf Mineralölbasis mit Ausnahme derjenigen, die unter 13 03 01 fallen',
      '13 03 07* - nichtchlorierte Isolier- und Wärmeübertragungsöle auf Mineralölbasis',
      '13 03 08* - synthetische Isolier- und Wärmeübertragungsöle',
      '13 03 09* - biologisch leicht abbaubare Isolier- und Wärmeübertragungsöle',
      '13 03 10* - andere Isolier- und Wärmeübertragungsöle',
      '13 04 01* - Bilgenöle aus der Binnenschifffahrt',
      '13 04 02* - Bilgenöle aus Molenablaufkanälen',
      '13 04 03* - Bilgenöle aus der übrigen Schifffahrt',
      '13 05 01* - feste Abfälle aus Sandfanganlagen und Öl-/Wasserabscheidern',
      '13 05 02* - Schlämme aus Öl-/Wasserabscheidern',
      '13 05 03* - Schlämme aus Einlaufschächten',
      '13 05 06* - Öle aus Öl-/Wasserabscheidern',
      '13 05 07* - öliges Wasser aus Öl-/Wasserabscheidern',
      '13 05 08* - Abfallgemische aus Sandfanganlagen und Öl-/Wasserabscheidern',
      '13 07 01* - Heizöl und Diesel',
      '13 07 02* - Benzin',
      '13 07 03* - andere Brennstoffe (einschließlich Gemische)',
      '13 08 01* - Schlämme oder Emulsionen aus Entsalzern',
      '13 08 02* - andere Emulsionen',
      '13 08 99* - Abfälle a. n. g.',
      '14 06 01* - Fluorchlorkohlenwasserstoffe, HFCKW, HFKW',
      '14 06 02* - andere halogenierte Lösemittel und Lösemittelgemische',
      '14 06 03* - andere Lösemittel und Lösemittelgemische',
      '14 06 04* - Schlämme oder feste Abfälle, die halogenierte Lösemittel enthalten',
      '14 06 05* - Schlämme oder feste Abfälle, die andere Lösemittel enthalten',
      '15 01 01 - Verpackungen aus Papier und Pappe',
      '15 01 02 - Verpackungen aus Kunststoff',
      '15 01 03 - Verpackungen aus Holz',
      '15 01 04 - Verpackungen aus Metall',
      '15 01 05 - Verbundverpackungen',
      '15 01 06 - gemischte Verpackungen',
      '15 01 07 - Verpackungen aus Glas',
      '15 01 09 - Verpackungen aus Textilien',
      '15 01 10* - Verpackungen, die Rückstände gefährlicher Stoffe enthalten oder durch gefährliche Stoffe verunreinigt sind',
      '15 01 11* - Verpackungen aus Metall, die eine gefährliche feste poröse Matrix (z. B. Asbest) enthalten, einschließlich geleerter Druckbehältnisse',
      '15 02 02* - Aufsaug- und Filtermaterialien (einschließlich Ölfilter a. n. g.), Wischtücher und Schutzkleidung, die durch gefährliche Stoffe verunreinigt sind',
      '15 02 03 - Aufsaug- und Filtermaterialien, Wischtücher und Schutzkleidung mit Ausnahme derjenigen, die unter 15 02 02 fallen',
      '16 01 03 - Altreifen',
      '16 01 04* - Altfahrzeuge',
      '16 01 06 - Altfahrzeuge, die weder Flüssigkeiten noch andere gefährliche Bestandteile enthalten',
      '16 01 07* - Ölfilter',
      '16 01 08* - quecksilberhaltige Bauteile',
      '16 01 09* - Bauteile, die PCB enthalten',
      '16 01 10* - explosive Bauteile (z. B. aus Airbags)',
      '16 01 11* - asbesthaltige Bremsbeläge',
      '16 01 12 - Bremsbeläge mit Ausnahme derjenigen, die unter 16 01 11 fallen',
      '16 01 13* - Bremsflüssigkeiten',
      '16 01 14* - Frostschutzmittel, die gefährliche Stoffe enthalten',
      '16 01 15 - Frostschutzmittel mit Ausnahme derjenigen, die unter 16 01 14 fallen',
      '16 01 16 - Flüssiggasbehälter',
      '16 01 17 - Eisenmetalle',
      '16 01 18 - Nichteisenmetalle',
      '16 01 19 - Kunststoffe',
      '16 01 20 - Glas',
      '16 01 21* - gefährliche Bauteile mit Ausnahme derjenigen, die unter 16 01 07 bis 16 01 11, 16 01 13 und 16 01 14 fallen',
      '16 01 22 - Bauteile a. n. g.',
      '16 01 99 - Abfälle a. n. g.',
      '16 02 09* - Transformatoren und Kondensatoren, die PCB enthalten',
      '16 02 10* - gebrauchte Geräte, die PCB enthalten oder damit verunreinigt sind, mit Ausnahme derjenigen, die unter 16 02 09 fallen',
      '16 02 11* - gebrauchte Geräte, die Fluorchlorkohlenwasserstoffe, HFCKW, HFKW enthalten',
      '16 02 12* - gebrauchte Geräte, die freies Asbest enthalten',
      '16 02 13* - gefährliche Bauteile enthaltende gebrauchte Geräte mit Ausnahme derjenigen, die unter 16 02 09 bis 16 02 12 fallen',
      '16 02 14 - gebrauchte Geräte mit Ausnahme derjenigen, die unter 16 02 09 bis 16 02 13 fallen',
      '16 02 15* - aus gebrauchten Geräten entfernte gefährliche Bauteile',
      '16 02 16 - aus gebrauchten Geräten entfernte Bauteile mit Ausnahme derjenigen, die unter 16 02 15 fallen',
      '16 03 03* - anorganische Abfälle, die gefährliche Stoffe enthalten',
      '16 03 04 - anorganische Abfälle mit Ausnahme derjenigen, die unter 16 03 03 fallen',
      '16 03 05* - organische Abfälle, die gefährliche Stoffe enthalten',
      '16 03 06 - organische Abfälle mit Ausnahme derjenigen, die unter 16 03 05 fallen',
      '16 03 07* - metallisches Quecksilber',
      '16 04 01* - Munitionsabfälle',
      '16 04 02* - Feuerwerkskörperabfälle',
      '16 04 03* - andere Explosivabfälle',
      '16 05 04* - gefährliche Stoffe enthaltende Gase in Druckbehältern (einschließlich Halonen)',
      '16 05 05 - Gase in Druckbehältern mit Ausnahme derjenigen, die unter 16 05 04 fallen',
      '16 05 06* - Laborchemikalien, die aus gefährlichen Stoffen bestehen oder solche enthalten, einschließlich Gemische von Laborchemikalien',
      '16 05 07* - gebrauchte anorganische Chemikalien, die aus gefährlichen Stoffen bestehen oder solche enthalten',
      '16 05 08* - gebrauchte organische Chemikalien, die aus gefährlichen Stoffen bestehen oder solche enthalten',
      '16 05 09 - gebrauchte Chemikalien mit Ausnahme derjenigen, die unter 16 05 06, 16 05 07 oder 16 05 08 fallen',
      '16 06 01* - Bleibatterien',
      '16 06 02* - Ni-Cd-Batterien',
      '16 06 03* - Quecksilber enthaltende Batterien',
      '16 06 04 - Alkalibatterien (außer 16 06 03)',
      '16 06 05 - andere Batterien und Akkumulatoren',
      '16 06 06* - getrennt gesammelte Elektrolyte aus Batterien und Akkumulatoren',
      '16 07 08* - ölhaltige Abfälle',
      '16 07 09* - Abfälle, die sonstige gefährliche Stoffe enthalten',
      '16 07 99 - Abfälle a. n. g.',
      '16 08 01 - gebrauchte Katalysatoren, die Gold, Silber, Rhenium, Rhodium, Palladium, Iridium oder Platin enthalten (außer 16 08 07)',
      '16 08 02* - gebrauchte Katalysatoren, die gefährliche Übergangsmetalle oder deren Verbindungen enthalten',
      '16 08 03 - gebrauchte Katalysatoren, die Übergangsmetalle oder deren Verbindungen enthalten, a. n. g.',
      '16 08 04 - gebrauchte Katalysatoren von Crackprozessen (außer 16 08 07)',
      '16 08 05* - gebrauchte Katalysatoren, die Phosphorsäure enthalten',
      '16 08 06* - gebrauchte Flüssigkeiten, die als Katalysatoren verwendet wurden',
      '16 08 07* - gebrauchte Katalysatoren, die durch gefährliche Stoffe verunreinigt sind',
      '16 09 01* - Permanganate, z. B. Kaliumpermanganat',
      '16 09 02* - Chromate, z. B. Kaliumchromat, Kalium- oder Natriumdichromat',
      '16 09 03* - Peroxide, z. B. Wasserstoffperoxid',
      '16 09 04* - oxidierende Stoffe a. n. g.',
      '16 10 01* - wässrige flüssige Abfälle, die gefährliche Stoffe enthalten',
      '16 10 02 - wässrige flüssige Abfälle mit Ausnahme derjenigen, die unter 16 10 01 fallen',
      '16 10 03* - wässrige Konzentrate, die gefährliche Stoffe enthalten',
      '16 10 04 - wässrige Konzentrate mit Ausnahme derjenigen, die unter 16 10 03 fallen',
      '16 11 01* - Auskleidungen und feuerfeste Materialien auf Kohlenstoffbasis aus metallurgischen Prozessen, die gefährliche Stoffe enthalten',
      '16 11 02 - Auskleidungen und feuerfeste Materialien auf Kohlenstoffbasis aus metallurgischen Prozessen mit Ausnahme derjenigen, die unter 16 11 01 fallen',
      '16 11 03* - andere Auskleidungen und feuerfeste Materialien aus metallurgischen Prozessen, die gefährliche Stoffe enthalten',
      '16 11 04 - andere Auskleidungen und feuerfeste Materialien aus metallurgischen Prozessen mit Ausnahme derjenigen, die unter 16 11 03 fallen',
      '16 11 05* - Auskleidungen und feuerfeste Materialien aus nichtmetallurgischen Prozessen, die gefährliche Stoffe enthalten',
      '16 11 06 - Auskleidungen und feuerfeste Materialien aus nichtmetallurgischen Prozessen mit Ausnahme derjenigen, die unter 16 11 05 fallen',
      '17 01 01 - Beton',
      '17 01 02 - Ziegel',
      '17 01 03 - Fliesen und Keramik',
      '17 01 06* - Gemische aus oder getrennte Fraktionen von Beton, Ziegeln, Fliesen und Keramik, die gefährliche Stoffe enthalten',
      '17 01 07 - Gemische aus Beton, Ziegeln, Fliesen und Keramik mit Ausnahme derjenigen, die unter 17 01 06 fallen',
      '17 02 01 - Holz',
      '17 02 02 - Glas',
      '17 02 03 - Kunststoff',
      '17 02 04* - Glas, Kunststoff und Holz, die gefährliche Stoffe enthalten oder durch gefährliche Stoffe verunreinigt sind',
      '17 03 01* - kohlenteerhaltige Bitumengemische',
      '17 03 02 - Bitumengemische mit Ausnahme derjenigen, die unter 17 03 01 fallen',
      '17 03 03* - Kohlenteer und teerhaltige Produkte',
      '17 04 01 - Kupfer, Bronze, Messing',
      '17 04 02 - Aluminium',
      '17 04 03 - Blei',
      '17 04 04 - Zink',
      '17 04 05 - Eisen und Stahl',
      '17 04 06 - Zinn',
      '17 04 07 - gemischte Metalle',
      '17 04 09* - Metallabfälle, die durch gefährliche Stoffe verunreinigt sind',
      '17 04 10* - Kabel, die Öl, Kohlenteer oder andere gefährliche Stoffe enthalten',
      '17 04 11 - Kabel mit Ausnahme derjenigen, die unter 17 04 10 fallen',
      '17 05 03* - Boden und Steine, die gefährliche Stoffe enthalten',
      '17 05 04 - Boden und Steine mit Ausnahme derjenigen, die unter 17 05 03 fallen',
      '17 05 05* - Baggergut, das gefährliche Stoffe enthält',
      '17 05 06 - Baggergut mit Ausnahme desjenigen, das unter 17 05 05 fällt',
      '17 05 07* - Gleisschotter, der gefährliche Stoffe enthält',
      '17 05 08 - Gleisschotter mit Ausnahme desjenigen, der unter 17 05 07 fällt',
      '17 06 01* - Dämmmaterial, das Asbest enthält',
      '17 06 03* - anderes Dämmmaterial, das aus gefährlichen Stoffen besteht oder solche Stoffe enthält',
      '17 06 04 - Dämmmaterial mit Ausnahme desjenigen, das unter 17 06 01 und 17 06 03 fällt',
      '17 06 05* - asbesthaltige Baustoffe',
      '17 08 01* - Baustoffe auf Gipsbasis, die durch gefährliche Stoffe verunreinigt sind',
      '17 08 02 - Baustoffe auf Gipsbasis mit Ausnahme derjenigen, die unter 17 08 01 fallen',
      '17 09 01* - Bau- und Abbruchabfälle, die Quecksilber enthalten',
      '17 09 02* - Bau- und Abbruchabfälle, die PCB enthalten (z. B. PCB-haltige Dichtungsmassen, PCB-haltige Bodenbeläge auf Harzbasis, PCB-haltige Isolierverglasungen, PCB-haltige Kondensatoren)',
      '17 09 03* - sonstige Bau- und Abbruchabfälle (einschließlich gemischte Abfälle), die gefährliche Stoffe enthalten',
      '17 09 04 - gemischte Bau- und Abbruchabfälle mit Ausnahme derjenigen, die unter 17 09 01, 17 09 02 und 17 09 03 fallen',
      '18 01 01 - spitze oder scharfe Gegenstände (außer 18 01 03)',
      '18 01 02 - Körperteile und Organe, einschließlich Blutbeutel und Blutkonserven (außer 18 01 03)',
      '18 01 03* - Abfälle, an deren Sammlung und Entsorgung aus infektionspräventiver Sicht besondere Anforderungen gestellt werden',
      '18 01 04 - Abfälle, an deren Sammlung und Entsorgung aus infektionspräventiver Sicht keine besonderen Anforderungen gestellt werden (z. B. Wund- und Gipsverbände, Wäsche, Einwegkleidung, Windeln)',
      '18 01 06* - Chemikalien, die aus gefährlichen Stoffen bestehen oder solche enthalten',
      '18 01 07 - Chemikalien mit Ausnahme derjenigen, die unter 18 01 06 fallen',
      '18 01 08* - zytotoxische und zytostatische Arzneimittel',
      '18 01 09 - Arzneimittel mit Ausnahme derjenigen, die unter 18 01 08 fallen',
      '18 01 10* - Amalgamabfälle aus der Zahnmedizin',
      '18 02 01 - spitze oder scharfe Gegenstände mit Ausnahme derjenigen, die unter 18 02 02 fallen',
      '18 02 02* - Abfälle, an deren Sammlung und Entsorgung aus infektionspräventiver Sicht besondere Anforderungen gestellt werden',
      '18 02 03 - Abfälle, an deren Sammlung und Entsorgung aus infektionspräventiver Sicht keine besonderen Anforderungen gestellt werden',
      '18 02 05* - Chemikalien, die aus gefährlichen Stoffen bestehen oder solche enthalten',
      '18 02 06 - Chemikalien mit Ausnahme derjenigen, die unter 18 02 05 fallen',
      '18 02 07* - zytotoxische und zytostatische Arzneimittel',
      '18 02 08 - Arzneimittel mit Ausnahme derjenigen, die unter 18 02 07 fallen',
      '19 01 02 - Eisenteile, aus der Rost- und Kesselasche entfernt',
      '19 01 05* - Filterkuchen aus der Abgasbehandlung',
      '19 01 06* - wässrige flüssige Abfälle aus der Abgasbehandlung und andere wässrige flüssige Abfälle',
      '19 01 07* - feste Abfälle aus der Abgasbehandlung',
      '19 01 10* - gebrauchte Aktivkohle aus der Abgasbehandlung',
      '19 01 11* - Rost- und Kesselaschen sowie Schlacken, die gefährliche Stoffe enthalten',
      '19 01 12 - Rost- und Kesselaschen sowie Schlacken mit Ausnahme derjenigen, die unter 19 01 11 fallen',
      '19 01 13* - Filterstaub, der gefährliche Stoffe enthält',
      '19 01 14 - Filterstaub mit Ausnahme desjenigen, der unter 19 01 13 fällt',
      '19 01 15* - Kesselstaub, der gefährliche Stoffe enthält',
      '19 01 16 - Kesselstaub mit Ausnahme desjenigen, der unter 19 01 15 fällt',
      '19 01 17* - Pyrolyseabfälle, die gefährliche Stoffe enthalten',
      '19 01 18 - Pyrolyseabfälle mit Ausnahme derjenigen, die unter 19 01 17 fallen',
      '19 01 19 - Sande aus der Wirbelschichtfeuerung',
      '19 01 99 - Abfälle a. n. g.',
      '19 02 03 - vorgemischte Abfälle, die ausschließlich aus nichtgefährlichen Abfällen bestehen',
      '19 02 04* - vorgemischte Abfälle, die wenigstens einen gefährlichen Abfall enthalten',
      '19 02 05* - Schlämme aus der physikalisch-chemischen Behandlung, die gefährliche Stoffe enthalten',
      '19 02 06 - Schlämme aus der physikalisch-chemischen Behandlung mit Ausnahme derjenigen, die unter 19 02 05 fallen',
      '19 02 07* - Öl und Konzentrate aus Abtrennprozessen',
      '19 02 08* - flüssige brennbare Abfälle, die gefährliche Stoffe enthalten',
      '19 02 09* - feste brennbare Abfälle, die gefährliche Stoffe enthalten',
      '19 02 10 - brennbare Abfälle mit Ausnahme derjenigen, die unter 19 02 08 und 19 02 09 fallen',
      '19 02 11* - sonstige Abfälle, die gefährliche Stoffe enthalten',
      '19 02 99 - Abfälle a. n. g.',
      '19 03 04* - als gefährlich eingestufte teilweise stabilisierte Abfälle mit Ausnahme derjenigen, die unter 19 03 08 fallen',
      '19 03 05 - stabilisierte Abfälle mit Ausnahme derjenigen, die unter 19 03 04 fallen',
      '19 03 06* - als gefährlich eingestufte verfestigte Abfälle',
      '19 03 07 - verfestigte Abfälle mit Ausnahme derjenigen, die unter 19 03 06 fallen',
      '19 03 08* - teilweise stabilisiertes Quecksilber',
      '19 04 01 - verglaste Abfälle',
      '19 04 02* - Filterstaub und andere Abfälle aus der Abgasbehandlung',
      '19 04 03* - nicht verglaste Festphase',
      '19 04 04 - wässrige flüssige Abfälle aus dem Tempern',
      '19 05 01 - nicht kompostierte Fraktion von Siedlungs- und ähnlichen Abfällen',
      '19 05 02 - nicht kompostierte Fraktion von tierischen und pflanzlichen Abfällen',
      '19 05 03 - nicht spezifikationsgerechter Kompost',
      '19 05 99 - Abfälle a. n. g.',
      '19 06 03 - Flüssigkeiten aus der anaeroben Behandlung von Siedlungsabfällen',
      '19 06 04 - Gärrückstand/-schlamm aus der anaeroben Behandlung von Siedlungsabfällen',
      '19 06 05 - Flüssigkeiten aus der anaeroben Behandlung von tierischen und pflanzlichen Abfällen',
      '19 06 06 - Gärrückstand/-schlamm aus der anaeroben Behandlung von tierischen und pflanzlichen Abfällen',
      '19 06 99 - Abfälle a. n. g.',
      '19 07 02* - Deponiesickerwasser, das gefährliche Stoffe enthält',
      '19 07 03 - Deponiesickerwasser mit Ausnahme desjenigen, das unter 19 07 02 fällt',
      '19 08 01 - Sieb- und Rechenrückstände',
      '19 08 02 - Sandfangrückstände',
      '19 08 05 - Schlämme aus der Behandlung von kommunalem Abwasser',
      '19 08 06* - gesättigte oder verbrauchte Ionenaustauscherharze',
      '19 08 07* - Lösungen und Schlämme aus der Regeneration von Ionenaustauschern',
      '19 08 08* - schwermetallhaltige Abfälle aus Membransystemen',
      '19 08 09 - Fett- und Ölmischungen aus Ölabscheidern, die ausschließlich Speiseöle und -fette enthalten',
      '19 08 10* - Fett- und Ölmischungen aus Ölabscheidern mit Ausnahme derjenigen, die unter 19 08 09 fallen',
      '19 08 11* - Schlämme aus der biologischen Behandlung von industriellem Abwasser, die gefährliche Stoffe enthalten',
      '19 08 12 - Schlämme aus der biologischen Behandlung von industriellem Abwasser mit Ausnahme derjenigen, die unter 19 08 11 fallen',
      '19 08 13* - Schlämme, die gefährliche Stoffe aus einer anderen Behandlung von industriellem Abwasser enthalten',
      '19 08 14 - Schlämme aus einer anderen Behandlung von industriellem Abwasser mit Ausnahme derjenigen, die unter 19 08 13 fallen',
      '19 08 99 - Abfälle a. n. g.',
      '19 09 01 - feste Abfälle aus der Erstfiltration und Siebrückstände',
      '19 09 02 - Schlämme aus der Wasserklärung',
      '19 09 03 - Schlämme aus der Dekarbonatisierung',
      '19 09 04 - gebrauchte Aktivkohle',
      '19 09 05 - gesättigte oder gebrauchte Ionenaustauscherharze',
      '19 09 06 - Lösungen und Schlämme aus der Regeneration von Ionenaustauschern',
      '19 09 99 - Abfälle a. n. g.',
      '19 10 01 - Eisen und Stahlabfälle',
      '19 10 02 - NE-Metall-Abfälle',
      '19 10 03* - Schredderleichtfraktionen und Staub, die gefährliche Stoffe enthalten',
      '19 10 04 - Schredderleichtfraktionen und Staub mit Ausnahme derjenigen, die unter 19 10 03 fallen',
      '19 10 05* - andere Fraktionen, die gefährliche Stoffe enthalten',
      '19 10 06 - andere Fraktionen mit Ausnahme derjenigen, die unter 19 10 05 fallen',
      '19 11 01* - gebrauchte Filtertone',
      '19 11 02* - Säureteere',
      '19 11 03* - wässrige flüssige Abfälle',
      '19 11 04* - Abfälle aus der Brennstoffreinigung mit Basen',
      '19 11 05* - Schlämme aus der betriebseigenen Abwasserbehandlung, die gefährliche Stoffe enthalten',
      '19 11 06 - Schlämme aus der betriebseigenen Abwasserbehandlung mit Ausnahme derjenigen, die unter 19 11 05 fallen',
      '19 11 07* - Abfälle aus der Abgasreinigung',
      '19 11 99 - Abfälle a. n. g.',
      '19 12 01 - Papier und Pappe',
      '19 12 02 - Eisenmetalle',
      '19 12 03 - Nichteisenmetalle',
      '19 12 04 - Kunststoff und Gummi',
      '19 12 05 - Glas',
      '19 12 06* - Holz, das gefährliche Stoffe enthält',
      '19 12 07 - Holz mit Ausnahme desjenigen, das unter 19 12 06 fällt',
      '19 12 08 - Textilien',
      '19 12 09 - Mineralien (z. B. Sand, Steine)',
      '19 12 10 - brennbare Abfälle (Brennstoffe aus Abfällen)',
      '19 12 11* - sonstige Abfälle (einschließlich Materialmischungen) aus der mechanischen Behandlung von Abfällen, die gefährliche Stoffe enthalten',
      '19 12 12 - sonstige Abfälle (einschließlich Materialmischungen) aus der mechanischen Behandlung von Abfällen mit Ausnahme derjenigen, die unter 19 12 11 fallen',
      '19 13 01* - feste Abfälle aus der Sanierung von Böden, die gefährliche Stoffe enthalten',
      '19 13 02 - feste Abfälle aus der Sanierung von Böden mit Ausnahme derjenigen, die unter 19 13 01 fallen',
      '19 13 03* - Schlämme aus der Sanierung von Böden, die gefährliche Stoffe enthalten',
      '19 13 04 - Schlämme aus der Sanierung von Böden mit Ausnahme derjenigen, die unter 19 13 03 fallen',
      '19 13 05* - Schlämme aus der Sanierung von Grundwasser, die gefährliche Stoffe enthalten',
      '19 13 06 - Schlämme aus der Sanierung von Grundwasser mit Ausnahme derjenigen, die unter 19 13 05 fallen',
      '19 13 07* - wässrige flüssige Abfälle und wässrige Konzentrate aus der Sanierung von Grundwasser, die gefährliche Stoffe enthalten',
      '19 13 08 - wässrige flüssige Abfälle und wässrige Konzentrate aus der Sanierung von Grundwasser mit Ausnahme derjenigen, die unter 19 13 07 fallen',
      '20 01 01 - Papier und Pappe',
      '20 01 02 - Glas',
      '20 01 08 - biologisch abbaubare Küchen- und Kantinenabfälle',
      '20 01 10 - Bekleidung',
      '20 01 11 - Textilien',
      '20 01 13* - Lösemittel',
      '20 01 14* - Säuren',
      '20 01 15* - Laugen',
      '20 01 17* - Fotochemikalien',
      '20 01 19* - Pestizide',
      '20 01 21* - Leuchtstoffröhren und andere quecksilberhaltige Abfälle',
      '20 01 23* - gebrauchte Geräte, die Fluorchlorkohlenwasserstoffe enthalten',
      '20 01 25 - Speiseöle und -fette',
      '20 01 26* - Öle und Fette mit Ausnahme derjenigen, die unter 20 01 25 fallen',
      '20 01 27* - Farben, Druckfarben, Klebstoffe und Kunstharze, die gefährliche Stoffe enthalten',
      '20 01 28 - Farben, Druckfarben, Klebstoffe und Kunstharze mit Ausnahme derjenigen, die unter 20 01 27 fallen',
      '20 01 29* - Reinigungsmittel, die gefährliche Stoffe enthalten',
      '20 01 30 - Reinigungsmittel mit Ausnahme derjenigen, die unter 20 01 29 fallen',
      '20 01 31* - zytotoxische und zytostatische Arzneimittel',
      '20 01 32 - Arzneimittel mit Ausnahme derjenigen, die unter 20 01 31 fallen',
      '20 01 33* - Batterien und Akkumulatoren, die unter 16 06 01, 16 06 02 oder 16 06 03 fallen, sowie gemischte Batterien und Akkumulatoren, die solche Batterien enthalten',
      '20 01 34 - Batterien und Akkumulatoren mit Ausnahme derjenigen, die unter 20 01 33 fallen',
      '20 01 35* - gebrauchte elektrische und elektronische Geräte, die gefährliche Bauteile enthalten, mit Ausnahme derjenigen, die unter 20 01 21 und 20 01 23 fallen',
      '20 01 36 - gebrauchte elektrische und elektronische Geräte mit Ausnahme derjenigen, die unter 20 01 21, 20 01 23 und 20 01 35 fallen',
      '20 01 37* - Holz, das gefährliche Stoffe enthält',
      '20 01 38 - Holz mit Ausnahme desjenigen, das unter 20 01 37 fällt',
      '20 01 39 - Kunststoffe',
      '20 01 40 - Metalle',
      '20 01 41 - Abfälle aus der Reinigung von Schornsteinen',
      '20 01 99 - sonstige Fraktionen a. n. g.',
      '20 02 01 - biologisch abbaubare Abfälle',
      '20 02 02 - Boden und Steine',
      '20 02 03 - andere nicht biologisch abbaubare Abfälle',
      '20 03 01 - gemischte Siedlungsabfälle',
      '20 03 02 - Marktabfälle',
      '20 03 03 - Straßenkehricht',
      '20 03 04 - Fäkalschlamm',
      '20 03 06 - Abfälle aus der Kanalreinigung',
      '20 03 07 - Sperrmüll',
      '20 03 99 - Siedlungsabfälle a. n. g.',
    ]
    
    // Display value for UN number (shows only the number)
    const unNumberDisplay = ref('')
    
    // Watch for changes in unNumberDisplay and extract the number part
    watch(unNumberDisplay, (newValue) => {
      if (newValue && newValue.includes(' - ')) {
        // Extract the number part (everything before " - ")
        const numberPart = newValue.split(' - ')[0].trim()
        formData.value.un_number = numberPart
        // Update display to show only the number (avoid infinite loop by checking if different)
        if (unNumberDisplay.value !== numberPart) {
          unNumberDisplay.value = numberPart
        }
      } else if (newValue && !newValue.includes(' - ')) {
        // If it's just a number (user typed manually), use it as is
        formData.value.un_number = newValue.trim()
      } else {
        formData.value.un_number = ''
      }
    })
    
    // Display value for Lagerklasse (shows full string; we save only the code)
    const lagerklasseDisplay = ref('')
    
    // Watch for changes in lagerklasseDisplay and save only the code part
    watch(lagerklasseDisplay, (newValue) => {
      if (newValue && newValue.includes(' - ')) {
        formData.value.inventory_class = newValue.split(' - ')[0].trim()
      } else {
        formData.value.inventory_class = newValue ? newValue.trim() : ''
      }
    })
    
    // Display value for EAK number (shows only the code)
    const eakNumberDisplay = ref('')
    
    // Watch for changes in eakNumberDisplay and extract the code part
    watch(eakNumberDisplay, (newValue) => {
      if (newValue && newValue.includes(' - ')) {
        // Extract the code part (everything before " - ")
        const codePart = newValue.split(' - ')[0].trim()
        formData.value.eak_number = codePart
        // Update display to show only the code (avoid infinite loop by checking if different)
        if (eakNumberDisplay.value !== codePart) {
          eakNumberDisplay.value = codePart
        }
      } else if (newValue && !newValue.includes(' - ')) {
        // If it's just a code (user typed manually), use it as is
        formData.value.eak_number = newValue.trim()
      } else {
        formData.value.eak_number = ''
      }
    })
    
    const isEditMode = computed(() => !!props.id)

    async function fillFormFromGefahrstoffData(data: any) {
        formData.value.name = data.name || ''
        if (data.substitutiontest) {
          formData.value.substitutiontest.next_check = data.substitutiontest.next_check || ''
          formData.value.substitutiontest.name_responsible = data.substitutiontest.name_responsible || ''
          formData.value.substitutiontest.email_responsible = data.substitutiontest.email_responsible || ''
          formData.value.substitutiontest.email_department = data.substitutiontest.email_department || ''
          formData.value.substitutiontest.version_date = data.substitutiontest.version_date || ''
          formData.value.substitutiontest.result = data.substitutiontest.result || ''
        }
        
        // Load boolean fields
        formData.value.hazardous_material = data.hazardous_material || false
        formData.value.gas = data.gas || false
        formData.value.spray = data.spray || false
        formData.value.carcinogenic_material = data.carcinogenic_material || false
        
        // Load additional fields
        formData.value.un_number = data.un_number || ''
        // Set display value for UN number (show only the number)
        unNumberDisplay.value = data.un_number || ''
        formData.value.eak_number = data.eak_number || ''
        // Set display value for EAK number (show only the code)
        eakNumberDisplay.value = data.eak_number || ''
        // Parse flammpunkt value (number only; unit is always °C)
        const flammpunktValue = data.flammpunkt || ''
        const flammpunktMatch = flammpunktValue.match(/^([\d.]+)/)
        formData.value.flammpunkt = flammpunktMatch ? flammpunktMatch[1] : flammpunktValue
        formData.value.water_hazard_class = data.water_hazard_class || ''
        formData.value.inventory_class = data.inventory_class || ''
        // Set display value for Lagerklasse (show full string from code)
        lagerklasseDisplay.value = lagerklasseCodeToDisplay(data.inventory_class || '')
        formData.value.use = data.use || ''
        formData.value.number_of_workers = data.number_of_workers || null
        formData.value.workspace = data.workspace || ''
        // Parse yearly_usage value and unit
        const yearlyUsageValue = data.yearly_usage || ''
        const yearlyUsageMatch = yearlyUsageValue.match(/^([\d.]+)\s*(m³|m3|l|g|mg|kg|t)?$/i)
        if (yearlyUsageMatch) {
          formData.value.yearly_usage = yearlyUsageMatch[1]
          // Normalize m3 to m³
          formData.value.yearly_usage_unit = yearlyUsageMatch[2] === 'm3' ? 'm³' : (yearlyUsageMatch[2] || '')
        } else {
          formData.value.yearly_usage = yearlyUsageValue
          formData.value.yearly_usage_unit = ''
        }
        // Parse inventory_size value and unit
        const inventorySizeValue = data.inventory_size || ''
        const inventorySizeMatch = inventorySizeValue.match(/^([\d.]+)\s*(m³|m3|l|g|mg|kg|t)?$/i)
        if (inventorySizeMatch) {
          formData.value.inventory_size = inventorySizeMatch[1]
          // Normalize m3 to m³
          formData.value.inventory_size_unit = inventorySizeMatch[2] === 'm3' ? 'm³' : (inventorySizeMatch[2] || '')
        } else {
          formData.value.inventory_size = inventorySizeValue
          formData.value.inventory_size_unit = ''
        }
        formData.value.inventory_space_name = data.inventory_space_name || ''
        formData.value.inventory_space_area_name = data.inventory_space_area_name || ''
        formData.value.location = data.location || ''
        
        // Load inventory_space_area options when loading data
        if (data.inventory_space_name) {
          try {
            const response = await axios.get('/gefahrstoffe/inventory_space_areas.json', {
              params: { inventory_space_name: data.inventory_space_name }
            })
            inventorySpaceAreaOptions.value = response.data.map((item: any) => item.name) || []
          } catch (error: any) {
            console.error('Failed to load inventory space area options:', error)
          }
        }
        formData.value.inventory_space_type = data.inventory_space_type || ''
        formData.value.comment = data.comment || ''
        formData.value.active = data.active !== false
        formData.value.use_start = data.use_start ? (typeof data.use_start === 'string' ? data.use_start.split('T')[0] : data.use_start) : ''
        formData.value.use_end = data.use_end ? (typeof data.use_end === 'string' ? data.use_end.split('T')[0] : data.use_end) : ''
        formData.value.use_duration_periodicity = data.use_duration_periodicity || ''
        formData.value.use_location_or_machinery = data.use_location_or_machinery || ''
        formData.value.extinguishing_agent_suitable = data.extinguishing_agent_suitable || ''
        formData.value.extinguishing_agent_unsuitable = data.extinguishing_agent_unsuitable || ''
        formData.value.inventory_conditions = data.inventory_conditions || ''
        formData.value.gebinde_number = data.gebinde_number || ''
        formData.value.physical_state_at_room_temperature = data.physical_state_at_room_temperature || ''
        formData.value.form = data.form || ''
        formData.value.color = data.color || ''
        formData.value.odor = data.odor || ''
        formData.value.solubility_in_water = data.solubility_in_water || ''
        formData.value.melting_point = data.melting_point || ''
        formData.value.boiling_point = data.boiling_point || ''
        formData.value.ignition_temperature = data.ignition_temperature || ''
        formData.value.autoignition_temperature = data.autoignition_temperature || ''
        formData.value.decomposition_point = data.decomposition_point || ''
        formData.value.density = data.density || ''
        formData.value.viscosity = data.viscosity || ''
        formData.value.vapor_pressure = data.vapor_pressure || ''
        formData.value.relative_vapor_density = data.relative_vapor_density || ''
        formData.value.ph_value = data.ph_value || ''
        formData.value.lower_explosion_limit_vol_percent = data.lower_explosion_limit_vol_percent || ''
        formData.value.upper_explosion_limit_vol_percent = data.upper_explosion_limit_vol_percent || ''
        // Backend column renamed from reactivity_stability -> reactivity; keep fallback for older payloads
        formData.value.reactivity = data.reactivity || ''
        formData.value.stability = data.stability || ''

        if (data.datasheet) {
          formData.value.datasheet.next_check = data.datasheet.next_check || ''
          formData.value.datasheet.name_responsible = data.datasheet.name_responsible || ''
          formData.value.datasheet.email_responsible = data.datasheet.email_responsible || ''
          formData.value.datasheet.email_department = data.datasheet.email_department || ''
          formData.value.datasheet.manufacturer = data.datasheet.manufacturer || ''
          formData.value.datasheet.version_date = data.datasheet.version_date || ''
          formData.value.datasheet.last_check = data.datasheet.last_check || new Date().toISOString().split('T')[0]
          formData.value.datasheet.last_check_hmc = data.datasheet.last_check_hmc || ''
          formData.value.datasheet.last_check_hmc_comment = data.datasheet.last_check_hmc_comment || ''
          if (data.datasheet.datasheet_url) {
            formData.value.datasheet.existing_datasheet_url = data.datasheet.datasheet_url
          }
        }
        
        if (data.gbu) {
          formData.value.gbu.next_check = data.gbu.next_check || ''
          formData.value.gbu.name_responsible = data.gbu.name_responsible || ''
          formData.value.gbu.email_responsible = data.gbu.email_responsible || ''
          formData.value.gbu.email_department = data.gbu.email_department || ''
          formData.value.gbu.version_date = data.gbu.version_date || ''
          formData.value.gbu.identifier = data.gbu.identifier || ''
          formData.value.gbu.comment = data.gbu.comment || ''
          if (data.gbu.gbu_url) {
            formData.value.gbu.existing_gbu_url = data.gbu.gbu_url
          }
        }
        
        if (data.betriebsanweisung) {
          formData.value.betriebsanweisung.next_check = data.betriebsanweisung.next_check || ''
          formData.value.betriebsanweisung.name_responsible = data.betriebsanweisung.name_responsible || ''
          formData.value.betriebsanweisung.email_responsible = data.betriebsanweisung.email_responsible || ''
          formData.value.betriebsanweisung.email_department = data.betriebsanweisung.email_department || ''
          formData.value.betriebsanweisung.version_date = data.betriebsanweisung.version_date || ''
          formData.value.betriebsanweisung.identifier = data.betriebsanweisung.identifier || ''
          formData.value.betriebsanweisung.comment = data.betriebsanweisung.comment || ''
          if (data.betriebsanweisung.betriebsanweisung_url) {
            formData.value.betriebsanweisung.existing_betriebsanweisung_url = data.betriebsanweisung.betriebsanweisung_url
          }
        }
        
        // Load H-phrases
        if (data.h_phrases && Array.isArray(data.h_phrases)) {
          formData.value.h_phrase_ids = data.h_phrases.map((hp: any) => hp.id)
        }
        
        // Load EURH-phrases
        if (data.eurh_phrases && Array.isArray(data.eurh_phrases)) {
          formData.value.eurh_phrase_ids = data.eurh_phrases.map((ep: any) => ep.id)
        }
        
        // Load P-phrases
        if (data.p_phrases && Array.isArray(data.p_phrases)) {
          formData.value.p_phrase_ids = data.p_phrases.map((pp: any) => pp.id)
        }
        
        // Load CAS numbers
        if (data.cas_numbers && Array.isArray(data.cas_numbers)) {
          formData.value.cas_number_ids = data.cas_numbers.map((cn: any) => cn.id)
          // Store selected CAS numbers for display
          selectedCasNumberOptions.value = data.cas_numbers.map((cn: any) => ({
            id: cn.id,
            label: cn.name,
            value: cn.cas_number
          }))
        }
    }

    /** Prefill only: Name, H/P/EUH-Sätze, CAS, booleans, UN-Nr., EAK-Nr., Flammpunkt, WGK, Lagerklasse */
    function prefillFormFromGefahrstoffData(data: any) {
      formData.value.name = data.name || ''
      formData.value.hazardous_material = data.hazardous_material ?? false
      formData.value.gas = data.gas ?? false
      formData.value.spray = data.spray ?? false
      formData.value.carcinogenic_material = data.carcinogenic_material ?? false
      formData.value.un_number = data.un_number || ''
      unNumberDisplay.value = data.un_number || ''
      formData.value.eak_number = data.eak_number || ''
      eakNumberDisplay.value = data.eak_number || ''
      const flammpunktValue = data.flammpunkt || ''
      const flammpunktMatch = flammpunktValue.match(/^([\d.]+)/)
      formData.value.flammpunkt = flammpunktMatch ? flammpunktMatch[1] : flammpunktValue
      formData.value.water_hazard_class = data.water_hazard_class || ''
      formData.value.inventory_class = data.inventory_class || ''
      lagerklasseDisplay.value = lagerklasseCodeToDisplay(data.inventory_class || '')
      if (data.h_phrases && Array.isArray(data.h_phrases)) {
        formData.value.h_phrase_ids = data.h_phrases.map((hp: any) => hp.id)
      }
      if (data.eurh_phrases && Array.isArray(data.eurh_phrases)) {
        formData.value.eurh_phrase_ids = data.eurh_phrases.map((ep: any) => ep.id)
      }
      if (data.p_phrases && Array.isArray(data.p_phrases)) {
        formData.value.p_phrase_ids = data.p_phrases.map((pp: any) => pp.id)
      }
      if (data.cas_numbers && Array.isArray(data.cas_numbers)) {
        formData.value.cas_number_ids = data.cas_numbers.map((cn: any) => cn.id)
        selectedCasNumberOptions.value = data.cas_numbers.map((cn: any) => ({
          id: cn.id,
          label: cn.name,
          value: cn.cas_number
        }))
      }
    }

    async function loadGefahrstoff() {
      if (!props.id) return
      isLoading.value = true
      try {
        const response = await axios.get(`/gefahrstoffe/${props.id}.json`)
        await fillFormFromGefahrstoffData(response.data)
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.response?.data?.error || "Failed to load Gefahrstoff",
          variant: "destructive"
        })
      } finally {
        isLoading.value = false
      }
    }

    function runSearch() {
      const q = searchQuery.value.trim()
      if (!q) {
        searchResults.value = []
        return
      }
      isSearching.value = true
      axios.get('/gefahrstoffe/search.json', { params: { q, limit: 20 } })
        .then((res) => { searchResults.value = res.data; selectedSearchId.value = null })
        .catch(() => { searchResults.value = [] })
        .finally(() => { isSearching.value = false })
    }

    function onSearchInput() {
      if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
      searchDebounceTimer = setTimeout(runSearch, 300)
    }

    async function applyPrefill() {
      if (selectedSearchId.value == null) return
      try {
        const response = await axios.get(`/gefahrstoffe/${selectedSearchId.value}.json`)
        prefillFormFromGefahrstoffData(response.data)
        showSearchDialog.value = false
        searchQuery.value = ''
        searchResults.value = []
        selectedSearchId.value = null
        toast({ title: 'Vorausgefüllt', description: 'Die Daten wurden in das Formular übernommen.' })
      } catch (error: any) {
        toast({
          title: "Fehler",
          description: error.response?.data?.error || "Eintrag konnte nicht geladen werden.",
          variant: "destructive"
        })
      }
    }

    function onCsvFileChange(event: Event) {
      const input = event.target as HTMLInputElement
      const file = input.files?.[0] || null
      csvImportFile.value = file
      csvImportError.value = ''
      if (!file) return
      if (!file.name.toLowerCase().endsWith('.csv')) {
        csvImportError.value = 'Bitte eine CSV-Datei wählen.'
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        const text = (reader.result as string) || ''
        const firstLine = text.split(/\r?\n/)[0] || ''
        if (!firstLine.includes(';')) {
          csvImportError.value = 'Die Datei muss semicolon-separated (Semikolon-getrennt) sein. Erste Zeile enthält keinen Semikolon.'
        }
      }
      reader.readAsText(file.slice(0, 2048), 'UTF-8')
    }

    async function doImportCsv() {
      if (!csvImportFile.value) {
        csvImportError.value = 'Bitte zuerst eine CSV-Datei auswählen.'
        return
      }
      isImportingCsv.value = true
      csvImportError.value = ''
      const formDataUpload = new FormData()
      formDataUpload.append('csv_file', csvImportFile.value)
      try {
        const res = await axios.post('/gefahrstoffe/import_csv', formDataUpload, {
          headers: { 'Accept': 'application/json', 'Content-Type': 'multipart/form-data' }
        })
        const data = res.data
        if (data.success) {
          if (data.errors?.length) console.warn('Import errors:', data.errors)
          csvImportSuccess.value = { created: data.created, updated: data.updated, errors: data.errors }
          showImportCsvDialog.value = false
          showImportSuccessDialog.value = true
        } else {
          csvImportError.value = data.error || 'Unbekannter Fehler.'
        }
      } catch (err: any) {
        csvImportError.value = err.response?.data?.error || err.message || 'Import fehlgeschlagen.'
        toast({ title: 'CSV-Import fehlgeschlagen', description: csvImportError.value, variant: 'destructive' })
      } finally {
        isImportingCsv.value = false
      }
    }

    function goToGefahrstoffeListAfterImport() {
      showImportSuccessDialog.value = false
      csvImportSuccess.value = null
      csvImportFile.value = null
      csvImportError.value = ''
      viewStore.setActionView('registry.gefahrstoffe')
    }

    function closeImportCsvDialog() {
      showImportCsvDialog.value = false
      csvImportSuccess.value = null
    }

    function onFileChange(event: Event, field: string) {
      const input = event.target as HTMLInputElement
      const file = input.files?.[0] || null
      
      if (field === 'datasheet') {
        formData.value.datasheet.datasheet_file = file
        formData.value.datasheet.existing_datasheet_url = null
      } else if (field === 'gbu') {
        formData.value.gbu.gbu_file = file
        formData.value.gbu.existing_gbu_url = null
      } else if (field === 'betriebsanweisung') {
        formData.value.betriebsanweisung.betriebsanweisung_file = file
        formData.value.betriebsanweisung.existing_betriebsanweisung_url = null
      }
    }
    
    async function save() {
      isSaving.value = true
      
      try {
        const formDataToSend = new FormData()
        
        // Main gefahrstoff fields
        formDataToSend.append('gefahrstoff[name]', formData.value.name)
        // Substitutiontest fields
        if (formData.value.substitutiontest.next_check) {
          formDataToSend.append('substitutiontest[next_check]', formData.value.substitutiontest.next_check)
        }
        if (formData.value.substitutiontest.name_responsible) {
          formDataToSend.append('substitutiontest[name_responsible]', formData.value.substitutiontest.name_responsible)
        }
        if (formData.value.substitutiontest.email_responsible) {
          formDataToSend.append('substitutiontest[email_responsible]', formData.value.substitutiontest.email_responsible)
        }
        if (formData.value.substitutiontest.email_department) {
          formDataToSend.append('substitutiontest[email_department]', formData.value.substitutiontest.email_department)
        }
        if (formData.value.substitutiontest.version_date) {
          formDataToSend.append('substitutiontest[version_date]', formData.value.substitutiontest.version_date)
        }
        if (formData.value.substitutiontest.result) {
          formDataToSend.append('substitutiontest[result]', formData.value.substitutiontest.result)
        }
        
        // Boolean fields
        formDataToSend.append('gefahrstoff[hazardous_material]', formData.value.hazardous_material ? '1' : '0')
        formDataToSend.append('gefahrstoff[gas]', formData.value.gas ? '1' : '0')
        formDataToSend.append('gefahrstoff[spray]', formData.value.spray ? '1' : '0')
        formDataToSend.append('gefahrstoff[carcinogenic_material]', formData.value.carcinogenic_material ? '1' : '0')
        
        // Additional fields
        if (formData.value.un_number) {
          formDataToSend.append('gefahrstoff[un_number]', formData.value.un_number)
        }
        if (formData.value.eak_number) {
          formDataToSend.append('gefahrstoff[eak_number]', formData.value.eak_number)
        }
        if (formData.value.flammpunkt) {
          formDataToSend.append('gefahrstoff[flammpunkt]', `${formData.value.flammpunkt} °C`)
        }
        if (formData.value.water_hazard_class) {
          formDataToSend.append('gefahrstoff[water_hazard_class]', formData.value.water_hazard_class)
        }
        if (formData.value.inventory_class) {
          formDataToSend.append('gefahrstoff[inventory_class]', formData.value.inventory_class)
        }
        if (formData.value.use) {
          formDataToSend.append('gefahrstoff[use]', formData.value.use)
        }
        if (formData.value.number_of_workers !== null && formData.value.number_of_workers !== undefined) {
          formDataToSend.append('gefahrstoff[number_of_workers]', formData.value.number_of_workers.toString())
        }
        if (formData.value.workspace) {
          formDataToSend.append('gefahrstoff[workspace]', formData.value.workspace)
        }
        if (formData.value.yearly_usage) {
          const yearlyUsageValue = formData.value.yearly_usage_unit 
            ? `${formData.value.yearly_usage} ${formData.value.yearly_usage_unit}`
            : formData.value.yearly_usage
          formDataToSend.append('gefahrstoff[yearly_usage]', yearlyUsageValue)
        }
        if (formData.value.inventory_size) {
          const inventorySizeValue = formData.value.inventory_size_unit 
            ? `${formData.value.inventory_size} ${formData.value.inventory_size_unit}`
            : formData.value.inventory_size
          formDataToSend.append('gefahrstoff[inventory_size]', inventorySizeValue)
        }
        if (formData.value.inventory_space_name) {
          formDataToSend.append('gefahrstoff[inventory_space_name]', formData.value.inventory_space_name)
        }
        if (formData.value.inventory_space_area_name) {
          formDataToSend.append('gefahrstoff[inventory_space_area_name]', formData.value.inventory_space_area_name)
        }
        formDataToSend.append('gefahrstoff[location]', formData.value.location || '')
        if (formData.value.inventory_space_type) {
          formDataToSend.append('gefahrstoff[inventory_space_type]', formData.value.inventory_space_type)
        }
        if (formData.value.comment) {
          formDataToSend.append('gefahrstoff[comment]', formData.value.comment)
        }
        formDataToSend.append('gefahrstoff[active]', formData.value.active ? '1' : '0')
        formDataToSend.append('gefahrstoff[use_start]', formData.value.use_start || '')
        formDataToSend.append('gefahrstoff[use_end]', formData.value.use_end || '')
        formDataToSend.append('gefahrstoff[use_duration_periodicity]', formData.value.use_duration_periodicity || '')
        formDataToSend.append('gefahrstoff[use_location_or_machinery]', formData.value.use_location_or_machinery || '')
        formDataToSend.append('gefahrstoff[extinguishing_agent_suitable]', formData.value.extinguishing_agent_suitable || '')
        formDataToSend.append('gefahrstoff[extinguishing_agent_unsuitable]', formData.value.extinguishing_agent_unsuitable || '')
        formDataToSend.append('gefahrstoff[inventory_conditions]', formData.value.inventory_conditions || '')
        formDataToSend.append('gefahrstoff[gebinde_number]', formData.value.gebinde_number || '')
        formDataToSend.append('gefahrstoff[physical_state_at_room_temperature]', formData.value.physical_state_at_room_temperature || '')
        formDataToSend.append('gefahrstoff[form]', formData.value.form || '')
        formDataToSend.append('gefahrstoff[color]', formData.value.color || '')
        formDataToSend.append('gefahrstoff[odor]', formData.value.odor || '')
        formDataToSend.append('gefahrstoff[solubility_in_water]', formData.value.solubility_in_water || '')
        formDataToSend.append('gefahrstoff[melting_point]', formData.value.melting_point || '')
        formDataToSend.append('gefahrstoff[boiling_point]', formData.value.boiling_point || '')
        formDataToSend.append('gefahrstoff[ignition_temperature]', formData.value.ignition_temperature || '')
        formDataToSend.append('gefahrstoff[autoignition_temperature]', formData.value.autoignition_temperature || '')
        formDataToSend.append('gefahrstoff[decomposition_point]', formData.value.decomposition_point || '')
        formDataToSend.append('gefahrstoff[density]', formData.value.density || '')
        formDataToSend.append('gefahrstoff[viscosity]', formData.value.viscosity || '')
        formDataToSend.append('gefahrstoff[vapor_pressure]', formData.value.vapor_pressure || '')
        formDataToSend.append('gefahrstoff[relative_vapor_density]', formData.value.relative_vapor_density || '')
        formDataToSend.append('gefahrstoff[ph_value]', formData.value.ph_value || '')
        formDataToSend.append('gefahrstoff[lower_explosion_limit_vol_percent]', formData.value.lower_explosion_limit_vol_percent || '')
        formDataToSend.append('gefahrstoff[upper_explosion_limit_vol_percent]', formData.value.upper_explosion_limit_vol_percent || '')
        formDataToSend.append('gefahrstoff[reactivity]', formData.value.reactivity || '')
        formDataToSend.append('gefahrstoff[stability]', formData.value.stability || '')

        // Datasheet fields
        if (formData.value.datasheet.next_check) {
          formDataToSend.append('datasheet[next_check]', formData.value.datasheet.next_check)
        }
        if (formData.value.datasheet.name_responsible) {
          formDataToSend.append('datasheet[name_responsible]', formData.value.datasheet.name_responsible)
        }
        if (formData.value.datasheet.email_responsible) {
          formDataToSend.append('datasheet[email_responsible]', formData.value.datasheet.email_responsible)
        }
        if (formData.value.datasheet.email_department) {
          formDataToSend.append('datasheet[email_department]', formData.value.datasheet.email_department)
        }
        if (formData.value.datasheet.manufacturer) {
          formDataToSend.append('datasheet[manufacturer]', formData.value.datasheet.manufacturer)
        }
        if (formData.value.datasheet.version_date) {
          formDataToSend.append('datasheet[version_date]', formData.value.datasheet.version_date)
        }
        if (formData.value.datasheet.last_check) {
          formDataToSend.append('datasheet[last_check]', formData.value.datasheet.last_check)
        }
        if (isHoeppner) {
          if (formData.value.datasheet.last_check_hmc) {
            formDataToSend.append('datasheet[last_check_hmc]', formData.value.datasheet.last_check_hmc)
          }
          if (formData.value.datasheet.last_check_hmc_comment) {
            formDataToSend.append('datasheet[last_check_hmc_comment]', formData.value.datasheet.last_check_hmc_comment)
          }
        }
        if (formData.value.datasheet.datasheet_file) {
          formDataToSend.append('datasheet[datasheet]', formData.value.datasheet.datasheet_file)
        }
        
        // GBU fields
        if (formData.value.gbu.next_check) {
          formDataToSend.append('gbu[next_check]', formData.value.gbu.next_check)
        }
        if (formData.value.gbu.name_responsible) {
          formDataToSend.append('gbu[name_responsible]', formData.value.gbu.name_responsible)
        }
        if (formData.value.gbu.email_responsible) {
          formDataToSend.append('gbu[email_responsible]', formData.value.gbu.email_responsible)
        }
        if (formData.value.gbu.email_department) {
          formDataToSend.append('gbu[email_department]', formData.value.gbu.email_department)
        }
        if (formData.value.gbu.version_date) {
          formDataToSend.append('gbu[version_date]', formData.value.gbu.version_date)
        }
        if (formData.value.gbu.identifier) {
          formDataToSend.append('gbu[identifier]', formData.value.gbu.identifier)
        }
        if (formData.value.gbu.comment) {
          formDataToSend.append('gbu[comment]', formData.value.gbu.comment)
        }
        if (formData.value.gbu.gbu_file) {
          formDataToSend.append('gbu[gbu]', formData.value.gbu.gbu_file)
        }
        
        // Betriebsanweisung fields
        if (formData.value.betriebsanweisung.next_check) {
          formDataToSend.append('betriebsanweisung[next_check]', formData.value.betriebsanweisung.next_check)
        }
        if (formData.value.betriebsanweisung.name_responsible) {
          formDataToSend.append('betriebsanweisung[name_responsible]', formData.value.betriebsanweisung.name_responsible)
        }
        if (formData.value.betriebsanweisung.email_responsible) {
          formDataToSend.append('betriebsanweisung[email_responsible]', formData.value.betriebsanweisung.email_responsible)
        }
        if (formData.value.betriebsanweisung.email_department) {
          formDataToSend.append('betriebsanweisung[email_department]', formData.value.betriebsanweisung.email_department)
        }
        if (formData.value.betriebsanweisung.version_date) {
          formDataToSend.append('betriebsanweisung[version_date]', formData.value.betriebsanweisung.version_date)
        }
        if (formData.value.betriebsanweisung.identifier) {
          formDataToSend.append('betriebsanweisung[identifier]', formData.value.betriebsanweisung.identifier)
        }
        if (formData.value.betriebsanweisung.comment) {
          formDataToSend.append('betriebsanweisung[comment]', formData.value.betriebsanweisung.comment)
        }
        if (formData.value.betriebsanweisung.betriebsanweisung_file) {
          formDataToSend.append('betriebsanweisung[betriebsanweisung]', formData.value.betriebsanweisung.betriebsanweisung_file)
        }
        
        // H-phrases
        formData.value.h_phrase_ids.forEach((id) => {
          formDataToSend.append('h_phrase_ids[]', id.toString())
        })
        
        // EURH-phrases
        formData.value.eurh_phrase_ids.forEach((id) => {
          formDataToSend.append('eurh_phrase_ids[]', id.toString())
        })
        
        // P-phrases
        formData.value.p_phrase_ids.forEach((id) => {
          formDataToSend.append('p_phrase_ids[]', id.toString())
        })
        
        // CAS numbers
        formData.value.cas_number_ids.forEach((id) => {
          formDataToSend.append('cas_number_ids[]', id.toString())
        })
        
        if (isEditMode.value) {
          await axios.patch(`/gefahrstoffe/${props.id}.json`, formDataToSend, {
            headers: { "Content-Type": "multipart/form-data" }
          })
        } else {
          await axios.post('/gefahrstoffe.json', formDataToSend, {
            headers: { "Content-Type": "multipart/form-data" }
          })
        }
        
        toast({
          title: "Erfolg",
          description: "Gefahrstoff erfolgreich gespeichert.",
        })
        
        // Navigate back to list
        viewStore.setActionView('registry.gefahrstoffe')
        viewStore.forceRefresh()
      } catch (error: any) {
        const message = error.response?.data?.error || error.response?.data?.message || "Fehler beim Speichern"
        toast({
          title: "Fehler",
          description: message,
          variant: "destructive"
        })
      } finally {
        isSaving.value = false
      }
    }
    
    function cancel() {
      viewStore.setActionView('registry.gefahrstoffe')
    }
    
    function onSDBFileChange(event: Event) {
      const input = event.target as HTMLInputElement
      sdbFile.value = input.files?.[0] || null
      sdbUploadError.value = ''
    }
    
    function isSDBFieldEmpty(val: unknown): boolean {
      if (val === null || val === undefined) return true
      if (typeof val === 'string') return val.trim() === ''
      if (Array.isArray(val)) return val.length === 0
      return false
    }
    function shouldSetSDBField(current: unknown): boolean {
      if (sdbMergeMode.value === 'overwrite') return true
      return isSDBFieldEmpty(current)
    }
    
    async function uploadSDB() {
      if (!sdbFile.value) {
        toast({
          title: "Fehler",
          description: "Bitte wählen Sie eine PDF-Datei aus",
          variant: "destructive"
        })
        return
      }
      
      isUploadingSDB.value = true
      
      try {
        const formDataToSend = new FormData()
        formDataToSend.append('pdf_file', sdbFile.value)
        
        const response = await axios.post('/gefahrstoffe/parse_sdb.json', formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        
        if (response.data.success && response.data.data) {
          // Prefill form with API response
          const apiData = response.data.data
          
          // Debug: Log the API response
          console.log('📥 API Response:', apiData)
          
          // Basic fields
          if (apiData.name && shouldSetSDBField(formData.value.name)) {
            formData.value.name = apiData.name
          }
          
          // Boolean fields
          if (apiData.hazardous_material !== undefined && shouldSetSDBField(formData.value.hazardous_material)) {
            formData.value.hazardous_material = apiData.hazardous_material
          }
          if (apiData.gas !== undefined && shouldSetSDBField(formData.value.gas)) {
            formData.value.gas = apiData.gas
          }
          if (apiData.spray !== undefined && shouldSetSDBField(formData.value.spray)) {
            formData.value.spray = apiData.spray
          }
          if (apiData.carcinogenic_material !== undefined && shouldSetSDBField(formData.value.carcinogenic_material)) {
            formData.value.carcinogenic_material = apiData.carcinogenic_material
          }
          
          // Additional fields
          if (apiData.un_number && shouldSetSDBField(formData.value.un_number)) {
            formData.value.un_number = apiData.un_number
          }
          if (apiData.eak_number && shouldSetSDBField(formData.value.eak_number)) {
            formData.value.eak_number = apiData.eak_number
          }
          if (apiData.flammpunkt && shouldSetSDBField(formData.value.flammpunkt)) {
            formData.value.flammpunkt = apiData.flammpunkt.toString()
          }
          if (apiData.water_hazard_class && shouldSetSDBField(formData.value.water_hazard_class)) {
            formData.value.water_hazard_class = apiData.water_hazard_class
          }
          // Handle both lager_klasse (from API) and inventory_class (from backend mapping)
          if ((apiData.lager_klasse || apiData.inventory_class) && shouldSetSDBField(lagerklasseDisplay.value)) {
            if (apiData.lager_klasse) {
              lagerklasseDisplay.value = apiData.lager_klasse.includes(' - ')
                ? apiData.lager_klasse
                : lagerklasseCodeToDisplay(apiData.lager_klasse)
            } else if (apiData.inventory_class) {
              lagerklasseDisplay.value = apiData.inventory_class.includes(' - ')
                ? apiData.inventory_class
                : lagerklasseCodeToDisplay(apiData.inventory_class)
            }
          }
          
          // Extinguishing agents (API returns list[str], form expects string)
          if (apiData.extinguishing_agent_suitable && shouldSetSDBField(formData.value.extinguishing_agent_suitable)) {
            if (Array.isArray(apiData.extinguishing_agent_suitable)) {
              formData.value.extinguishing_agent_suitable = apiData.extinguishing_agent_suitable.join(', ')
            } else {
              formData.value.extinguishing_agent_suitable = apiData.extinguishing_agent_suitable
            }
          }
          if (apiData.extinguishing_agent_unsuitable && shouldSetSDBField(formData.value.extinguishing_agent_unsuitable)) {
            if (Array.isArray(apiData.extinguishing_agent_unsuitable)) {
              formData.value.extinguishing_agent_unsuitable = apiData.extinguishing_agent_unsuitable.join(', ')
            } else {
              formData.value.extinguishing_agent_unsuitable = apiData.extinguishing_agent_unsuitable
            }
          }
          
          // Physical/chemical properties (strings)
          if (apiData.physical_state_at_room_temperature && shouldSetSDBField(formData.value.physical_state_at_room_temperature)) {
            formData.value.physical_state_at_room_temperature = apiData.physical_state_at_room_temperature
          }
          if (apiData.form && shouldSetSDBField(formData.value.form)) {
            formData.value.form = apiData.form
          }
          if (apiData.color && shouldSetSDBField(formData.value.color)) {
            formData.value.color = apiData.color
          }
          if (apiData.odor && shouldSetSDBField(formData.value.odor)) {
            formData.value.odor = apiData.odor
          }
          if (apiData.solubility_in_water && shouldSetSDBField(formData.value.solubility_in_water)) {
            formData.value.solubility_in_water = apiData.solubility_in_water
          }
          
          // Numeric properties (API returns float, form expects string)
          if (apiData.melting_point_in_celsius != null && shouldSetSDBField(formData.value.melting_point)) {
            formData.value.melting_point = String(apiData.melting_point_in_celsius)
          }
          if (apiData.boiling_point_in_celsius != null && shouldSetSDBField(formData.value.boiling_point)) {
            formData.value.boiling_point = String(apiData.boiling_point_in_celsius)
          }
          if (apiData.ignition_temperature_in_celsius != null && shouldSetSDBField(formData.value.ignition_temperature)) {
            formData.value.ignition_temperature = String(apiData.ignition_temperature_in_celsius)
          }
          if (apiData.autoignition_temperature_in_celsius != null && shouldSetSDBField(formData.value.autoignition_temperature)) {
            formData.value.autoignition_temperature = String(apiData.autoignition_temperature_in_celsius)
          }
          if (apiData.decomposition_point_in_celsius != null && shouldSetSDBField(formData.value.decomposition_point)) {
            formData.value.decomposition_point = String(apiData.decomposition_point_in_celsius)
          }
          if (apiData.density_in_g_per_cm_cubed != null && shouldSetSDBField(formData.value.density)) {
            formData.value.density = String(apiData.density_in_g_per_cm_cubed)
          }
          if (apiData.viscosity_in_mm_squared_per_second != null && shouldSetSDBField(formData.value.viscosity)) {
            formData.value.viscosity = String(apiData.viscosity_in_mm_squared_per_second)
          }
          if (apiData.vapor_pressure_in_hecto_pascal != null && shouldSetSDBField(formData.value.vapor_pressure)) {
            formData.value.vapor_pressure = String(apiData.vapor_pressure_in_hecto_pascal)
          }
          if (apiData.relative_vapor_density_to_air != null && shouldSetSDBField(formData.value.relative_vapor_density)) {
            formData.value.relative_vapor_density = String(apiData.relative_vapor_density_to_air)
          }
          if (apiData.ph_value != null && shouldSetSDBField(formData.value.ph_value)) {
            formData.value.ph_value = String(apiData.ph_value)
          }
          if (apiData.lower_explosion_limit_vol_percent != null && shouldSetSDBField(formData.value.lower_explosion_limit_vol_percent)) {
            formData.value.lower_explosion_limit_vol_percent = String(apiData.lower_explosion_limit_vol_percent)
          }
          if (apiData.upper_explosion_limit_vol_percent != null && shouldSetSDBField(formData.value.upper_explosion_limit_vol_percent)) {
            formData.value.upper_explosion_limit_vol_percent = String(apiData.upper_explosion_limit_vol_percent)
          }
          
          // Backend now provides separate comments for reactivity and stability.
          // Keep fallback to legacy `reactivity_stability_comment` for older payloads.
          if ((apiData.reactivity_comment) && shouldSetSDBField(formData.value.reactivity)) {
            formData.value.reactivity = apiData.reactivity_comment
          }
          if ((apiData.stability_comment) && shouldSetSDBField(formData.value.stability)) {
            formData.value.stability = apiData.stability_comment
          }
          
          // Ensure phrase options are loaded before mapping
          if (hPhraseOptions.value.length === 0) {
            await loadHPhrases()
          }
          if (eurhPhraseOptions.value.length === 0) {
            await loadEURHPhrases()
          }
          if (pPhraseOptions.value.length === 0) {
            await loadPPhrases()
          }
          
          // Debug: Log available options
          console.log('📋 Available H-phrases:', hPhraseOptions.value.length)
          console.log('📋 Available EUH-phrases:', eurhPhraseOptions.value.length)
          console.log('📋 Available P-phrases:', pPhraseOptions.value.length)
          
          // Phrase IDs - handle both mapped IDs and raw strings
          // First try mapped IDs (if backend mapping worked)
          if (shouldSetSDBField(formData.value.h_phrase_ids) && (apiData.h_phrase_ids && Array.isArray(apiData.h_phrase_ids))) {
            formData.value.h_phrase_ids = apiData.h_phrase_ids
          } else if (shouldSetSDBField(formData.value.h_phrase_ids) && apiData.h_saetze && Array.isArray(apiData.h_saetze)) {
            // Map raw H-phrase strings to IDs
            console.log('🔍 Mapping H-phrases:', apiData.h_saetze)
            const hIds: number[] = []
            apiData.h_saetze.forEach((hString: string) => {
              // Normalize: "H 302" -> "H302", "H302" -> "H302"
              const normalized = hString.replace(/\s+/g, '').toUpperCase()
              const matched = hPhraseOptions.value.find(opt => {
                // Match by removing spaces and comparing uppercase
                const optNormalized = (opt.value || '').replace(/\s+/g, '').toUpperCase()
                return optNormalized === normalized
              })
              if (matched) {
                console.log(`✅ Matched H-phrase: "${hString}" → ID: ${matched.id} (${matched.label})`)
                hIds.push(matched.id)
              } else {
                console.warn(`❌ H-phrase not found: "${hString}" (normalized: "${normalized}")`)
                console.log('Available H-codes (first 10):', hPhraseOptions.value.slice(0, 10).map(opt => opt.value))
              }
            })
            console.log('📊 Final H-phrase IDs:', hIds)
            formData.value.h_phrase_ids = hIds
          }
          
          if (shouldSetSDBField(formData.value.eurh_phrase_ids) && apiData.eurh_phrase_ids && Array.isArray(apiData.eurh_phrase_ids)) {
            formData.value.eurh_phrase_ids = apiData.eurh_phrase_ids
          } else if (shouldSetSDBField(formData.value.eurh_phrase_ids) && apiData.euh_saetze && Array.isArray(apiData.euh_saetze)) {
            // Map raw EUH-phrase strings to IDs
            console.log('🔍 Mapping EUH-phrases:', apiData.euh_saetze)
            const eurhIds: number[] = []
            apiData.euh_saetze.forEach((eurhString: string) => {
              // Normalize: "EUH001" -> "EUH001"
              const normalized = eurhString.replace(/\s+/g, '').toUpperCase()
              const matched = eurhPhraseOptions.value.find(opt => {
                const optNormalized = (opt.value || '').replace(/\s+/g, '').toUpperCase()
                return optNormalized === normalized
              })
              if (matched) {
                console.log(`✅ Matched EUH-phrase: "${eurhString}" → ID: ${matched.id} (${matched.label})`)
                eurhIds.push(matched.id)
              } else {
                console.warn(`❌ EUH-phrase not found: "${eurhString}" (normalized: "${normalized}")`)
                console.log('Available EUH-codes (first 10):', eurhPhraseOptions.value.slice(0, 10).map(opt => opt.value))
              }
            })
            console.log('📊 Final EUH-phrase IDs:', eurhIds)
            formData.value.eurh_phrase_ids = eurhIds
          }
          
          if (shouldSetSDBField(formData.value.p_phrase_ids) && apiData.p_phrase_ids && Array.isArray(apiData.p_phrase_ids)) {
            formData.value.p_phrase_ids = apiData.p_phrase_ids
          } else if (shouldSetSDBField(formData.value.p_phrase_ids) && apiData.p_saetze && Array.isArray(apiData.p_saetze)) {
            // Map raw P-phrase strings to IDs
            console.log('🔍 Mapping P-phrases:', apiData.p_saetze)
            const pIds: number[] = []
            apiData.p_saetze.forEach((pString: string) => {
              // Normalize: "P 304+P 340" -> "P304+P340" (treat as single combined phrase)
              const normalized = pString.replace(/\s+/g, '').toUpperCase()
              const matched = pPhraseOptions.value.find(opt => {
                const optNormalized = (opt.value || '').replace(/\s+/g, '').toUpperCase()
                return optNormalized === normalized
              })
              if (matched) {
                console.log(`✅ Matched P-phrase: "${pString}" → ID: ${matched.id} (${matched.label})`)
                pIds.push(matched.id)
              } else {
                console.warn(`❌ P-phrase not found: "${pString}" (normalized: "${normalized}")`)
                // Show combined P-phrases that exist
                const combinedPhrases = pPhraseOptions.value.filter(opt => opt.value.includes('+'))
                console.log('Available combined P-phrases:', combinedPhrases.map(opt => opt.value))
                console.log('Available P-codes (first 10):', pPhraseOptions.value.slice(0, 10).map(opt => opt.value))
              }
            })
            console.log('📊 Final P-phrase IDs:', pIds)
            formData.value.p_phrase_ids = pIds
          }
          if (shouldSetSDBField(formData.value.cas_number_ids) && apiData.cas_number_ids && Array.isArray(apiData.cas_number_ids)) {
            formData.value.cas_number_ids = apiData.cas_number_ids
            
            // Load CAS number details for display
            try {
              const casResponse = await axios.get('/gefahrstoffe/cas_numbers.json', {
                params: { limit: 1000 }
              })
              const allCasNumbers = casResponse.data
              selectedCasNumberOptions.value = allCasNumbers
                .filter((cn: any) => apiData.cas_number_ids.includes(cn.id))
                .map((cn: any) => ({
                  id: cn.id,
                  label: cn.name || cn.cas_number,
                  value: cn.cas_number
                }))
            } catch (error) {
              console.error('Failed to load CAS number details:', error)
            }
          }
          
          // Set the uploaded file to the datasheet file field (always set when user just uploaded this file)
          if (sdbFile.value) {
            formData.value.datasheet.datasheet_file = sdbFile.value
            formData.value.datasheet.existing_datasheet_url = null
          }

          // Set the manufacturer
          if (apiData.manufacturer && shouldSetSDBField(formData.value.datasheet.manufacturer)) {
            formData.value.datasheet.manufacturer = apiData.manufacturer
          }
          // Debug: Log final form state
          console.log('📝 Final form data:', {
            h_phrase_ids: formData.value.h_phrase_ids,
            eurh_phrase_ids: formData.value.eurh_phrase_ids,
            p_phrase_ids: formData.value.p_phrase_ids,
            cas_number_ids: formData.value.cas_number_ids,
            datasheet_file: formData.value.datasheet.datasheet_file?.name
          })
          
          toast({
            title: "Erfolg",
            description: "Sicherheitsdatenblatt erfolgreich verarbeitet. Formular wurde vorausgefüllt.",
            variant: "default"
          })
          
          showSDBDialog.value = false
          sdbFile.value = null
        } else {
          throw new Error('Ungültige Antwort vom Server')
        }
      } catch (error: any) {
        console.error('Error uploading SDB:', error)
        const data = error.response?.data
        const message =
          (typeof data?.error === 'string' && data.error) ||
          (typeof data?.message === 'string' && data.message) ||
          (typeof data === 'string' && data) ||
          (error.response?.status === 503 && 'Sicherheitsdatenblatt-API ist nicht konfiguriert. Bitte API-Zugangsdaten prüfen.') ||
          (error.response?.status && `Anfrage fehlgeschlagen (${error.response.status}).`) ||
          error?.message ||
          'Fehler beim Verarbeiten des Sicherheitsdatenblatts.'
        sdbUploadError.value = message
        toast({
          title: "Fehler beim Sicherheitsdatenblatt",
          description: message,
          variant: "destructive"
        })
      } finally {
        isUploadingSDB.value = false
      }
    }
    
    async function loadHPhrases() {
      try {
        const response = await axios.get('/gefahrstoffe/h_phrases.json')
        hPhraseOptions.value = response.data.map((hp: any) => ({
          id: hp.id,
          label: hp.h_phrase,
          value: hp.h_code
        }))
      } catch (error: any) {
        console.error('Failed to load H-phrases:', error)
        toast({
          title: "Fehler",
          description: "H-Sätze konnten nicht geladen werden",
          variant: "destructive"
        })
      }
    }
    
    async function loadEURHPhrases() {
      try {
        const response = await axios.get('/gefahrstoffe/eurh_phrases.json')
        eurhPhraseOptions.value = response.data.map((ep: any) => ({
          id: ep.id,
          label: ep.eurh_phrase,
          value: ep.eurh_code
        }))
      } catch (error: any) {
        console.error('Failed to load EURH-phrases:', error)
        toast({
          title: "Fehler",
          description: "EUH-Sätze konnten nicht geladen werden",
          variant: "destructive"
        })
      }
    }
    
    async function loadPPhrases() {
      try {
        const response = await axios.get('/gefahrstoffe/p_phrases.json')
        pPhraseOptions.value = response.data.map((pp: any) => ({
          id: pp.id,
          label: pp.p_phrase,
          value: pp.p_code
        }))
      } catch (error: any) {
        console.error('Failed to load P-phrases:', error)
        toast({
          title: "Fehler",
          description: "P-Sätze konnten nicht geladen werden",
          variant: "destructive"
        })
      }
    }
    
    async function loadCasNumbers() {
      // CAS numbers are now loaded asynchronously via the MultiSelect component
      // Only load selected options if in edit mode
      if (isEditMode.value && props.id) {
        try {
          const response = await axios.get(`/gefahrstoffe/${props.id}.json`)
          if (response.data.cas_numbers && Array.isArray(response.data.cas_numbers)) {
            selectedCasNumberOptions.value = response.data.cas_numbers.map((cn: any) => ({
              id: cn.id,
              label: cn.name,
              value: cn.cas_number
            }))
          }
        } catch (error: any) {
          console.error('Failed to load selected CAS numbers:', error)
        }
      }
    }
    
    async function loadFieldOptions() {
      try {
        // Load workspace options
        const workspaceResponse = await axios.get('/gefahrstoffe/field_options.json', {
          params: { field: 'workspace' }
        })
        workspaceOptions.value = workspaceResponse.data || []
        
        // Load inventory_space options
        try {
          const inventorySpaceResponse = await axios.get('/gefahrstoffe/inventory_spaces.json')
          inventorySpaceOptions.value = inventorySpaceResponse.data.map((item: any) => item.name) || []
        } catch (error: any) {
          console.error('Failed to load inventory space options:', error)
        }
        
        // Load inventory_space_type options
        const inventorySpaceTypeResponse = await axios.get('/gefahrstoffe/field_options.json', {
          params: { field: 'inventory_space_type' }
        })
        inventorySpaceTypeOptions.value = inventorySpaceTypeResponse.data || []
        
        // Load use options
        const useResponse = await axios.get('/gefahrstoffe/field_options.json', {
          params: { field: 'use' }
        })
        useOptions.value = useResponse.data || []

        // Load location options (Betriebsstätte)
        const locationResponse = await axios.get('/gefahrstoffe/field_options.json', {
          params: { field: 'location' }
        })
        locationOptions.value = locationResponse.data || []

        // Load name_responsible, email_responsible, email_department options (previously entered by customer)
        const nameResponsibleResponse = await axios.get('/gefahrstoffe/field_options.json', {
          params: { field: 'name_responsible' }
        })
        nameResponsibleOptions.value = nameResponsibleResponse.data || []
        const emailResponsibleResponse = await axios.get('/gefahrstoffe/field_options.json', {
          params: { field: 'email_responsible' }
        })
        emailResponsibleOptions.value = emailResponsibleResponse.data || []
        const emailDepartmentResponse = await axios.get('/gefahrstoffe/field_options.json', {
          params: { field: 'email_department' }
        })
        emailDepartmentOptions.value = emailDepartmentResponse.data || []
      } catch (error: any) {
        console.error('Failed to load field options:', error)
        // Don't show error toast as this is not critical
      }
    }
    
    // Watch inventory_space_name to update inventory_space_area options
    watch(() => formData.value.inventory_space_name, async (newValue) => {
      if (newValue) {
        try {
          const response = await axios.get('/gefahrstoffe/inventory_space_areas.json', {
            params: { inventory_space_name: newValue }
          })
          inventorySpaceAreaOptions.value = response.data.map((item: any) => item.name) || []
        } catch (error: any) {
          console.error('Failed to load inventory space area options:', error)
          inventorySpaceAreaOptions.value = []
        }
      } else {
        // If no inventory_space selected, load all areas
        try {
          const response = await axios.get('/gefahrstoffe/inventory_space_areas.json')
          inventorySpaceAreaOptions.value = response.data.map((item: any) => item.name) || []
        } catch (error: any) {
          console.error('Failed to load inventory space area options:', error)
          inventorySpaceAreaOptions.value = []
        }
      }
    }, { immediate: false })
    
    onMounted(async () => {
      await loadHPhrases()
      await loadEURHPhrases()
      await loadPPhrases()
      await loadFieldOptions()
      // CAS numbers are loaded asynchronously, no need to load all upfront
      
      // Load initial inventory_space_area options (all areas)
      try {
        const response = await axios.get('/gefahrstoffe/inventory_space_areas.json')
        inventorySpaceAreaOptions.value = response.data.map((item: any) => item.name) || []
      } catch (error: any) {
        console.error('Failed to load inventory space area options:', error)
      }
      
      // Hardcoded list of gefahrstoff names for now
      gefahrstoffNames.value = [
        'Aceton',
        'Acetaldehyd',
        'Acetonitril',
        'Acetylen',
        'Acrylamid',
        'Acrylsäure',
        'Ammoniak',
        'Anilin',
        'Arsen',
        'Arsentrioxid',
        'Asbest',
        'Barium',
        'Bariumsulfat',
        'Benzaldehyd',
        'Benzol',
        'Benzylalkohol',
        'Beryllium',
        'Blei',
        'Bleiacetat',
        'Bleioxid',
        'Borsäure',
        'Brom',
        'Butan',
        'Butanol',
        'Cadmium',
        'Cadmiumoxid',
        'Calciumhydroxid',
        'Chlor',
        'Chlorbenzol',
        'Chlordioxid',
        'Chloroform',
        'Chlormethan',
        'Chrom',
        'Chrom(VI)-oxid',
        'Chromsäure',
        'Cyanid',
        'Cyclohexan',
        'Cyclohexanol',
        'Dichlormethan',
        'Diethylether',
        'Dimethylformamid',
        'Dimethylsulfoxid',
        'Dioxan',
        'Epichlorhydrin',
        'Essigsäure',
        'Essigsäureanhydrid',
        'Ethylacetat',
        'Ethylenglycol',
        'Ethylenoxid',
        'Fluor',
        'Fluorwasserstoffsäure',
        'Formaldehyd',
        'Furan',
        'Glutaraldehyd',
        'Glykol',
        'Hydrazin',
        'Hydrazinhydrat',
        'Hydrochinon',
        'Hydrogenperoxid',
        'Isopropanol',
        'Kaliumcyanid',
        'Kaliumhydroxid',
        'Kaliumpermanganat',
        'Kohlenmonoxid',
        'Kohlenstoffdisulfid',
        'Kohlenstofftetrachlorid',
        'Kresol',
        'Kupfer',
        'Kupfer(II)-sulfat',
        'Lösungsbenzin',
        'Magnesium',
        'Mangan',
        'Mercaptan',
        'Naphthalin',
        'Natrium',
        'Natriumcyanid',
        'Natriumhydroxid',
        'Natriumhypochlorit',
        'Nickel',
        'Nickel(II)-sulfat',
        'Nitrilotriessigsäure',
        'Nitrobenzol',
        'Nitromethan',
        'Ozon',
        'Phenol',
        'Phosgen',
        'Phosphor',
        'Phosphorsäure',
        'Phthalat',
        'Propan',
        'Propanol',
        'Propylenoxid',
        'Quecksilber',
        'Quecksilber(II)-chlorid',
        'Salpetersäure',
        'Salzsäure',
        'Schwefel',
        'Schwefeldioxid',
        'Schwefelsäure',
        'Schwefelwasserstoff',
        'Selen',
        'Silber',
        'Silbernitrat',
        'Styrol',
        'Tetrachlorethen',
        'Tetrahydrofuran',
        'Thallium',
        'Titan',
        'Trichlorethen',
        'Trichlormethan',
        'Vanadium',
        'Vinylchlorid',
        'Wasserstoff',
        'Wasserstoffperoxid',
        'Zink',
        'Zinkchlorid',
        'Zinkoxid',
        'Zinn',
        'Zirconium',
        '1,1,1-Trichlorethan',
        '1,1,2-Trichlorethan',
        '1,2-Dichlorethan',
        '1,2-Dichlorethen',
        '1,2-Dichlorpropan',
        '1,3-Butadien',
        '1,4-Dioxan',
        '2-Butanon',
        '2-Ethoxyethanol',
        '2-Methoxyethanol',
        '2-Propenon',
        '3-Chlorpropen',
        'Acetamid',
        'Acetophenon',
        'Acrylnitril',
        'Adipinsäure',
        'Aluminium',
        'Aluminiumoxid',
        'Aluminiumphosphid',
        'Ameisensäure',
        'Ammoniumnitrat',
        'Ammoniumpersulfat',
        'Antimon',
        'Antimontrioxid',
        'Arsenwasserstoff',
        'Bariumchlorid',
        'Bariumnitrat',
        'Benzoylperoxid',
        'Berylliumoxid',
        'Biphenyl',
        'Bisphenol A',
        'Borax',
        'Bortrifluorid',
        'Bromethan',
        'Butadien',
        'Buttersäure',
        'Calciumcarbid',
        'Calciumchlorid',
        'Calciumhypochlorit',
        'Caprolactam',
        'Chloraceton',
        'Chlorat',
        'Chlorethan',
        'Chlorphenol',
        'Chlorsäure',
        'Chrom(III)-chlorid',
        'Chrom(VI)-Verbindungen',
        'Cobalt',
        'Cobalt(II)-chlorid',
        'Cumol',
        'Cyanwasserstoff',
        'Dibutylphthalat',
        'Dichlorbenzol',
        'Dichlordifluormethan',
        'Dichlormethan',
        'Diethylamin',
        'Diethylenglycol',
        'Diisocyanat',
        'Dimethylamin',
        'Dimethylbenzol',
        'Dinitrotoluol',
        'Diphenylmethan',
        'Dodecylbenzolsulfonat',
        'Epoxidharz',
        'Erdgas',
        'Ethylamin',
        'Ethylbenzol',
        'Ethylen',
        'Ethylenglycoldimethylether',
        'Ethylmethacrylat',
        'Fluorwasserstoff',
        'Furfural',
        'Glycerin',
        'Guanidin',
        'Hexachlorbenzol',
        'Hexachlorbutadien',
        'Hexachlorethan',
        'Hexamethylendiamin',
        'Hexan',
        'Hydrazinsulfat',
        'Hydroxylamin',
        'Indium',
        'Iod',
        'Isobutan',
        'Isobutylalkohol',
        'Isocyanat',
        'Isophoron',
        'Kaliumchlorat',
        'Kaliumdichromat',
        'Kaliumnitrat',
        'Kampfer',
        'Kerosin',
        'Kobalt',
        'Kohlenstoffdioxid',
        'Kohlenstoffmonoxid',
        'Kresolsäure',
        'Kupfer(I)-oxid',
        'Kupfer(II)-chlorid',
        'Kupfer(II)-nitrat',
        'Lanthan',
        'Lithium',
        'Lithiumhydroxid',
        'Lithiumnitrid',
        'Magnesiumoxid',
        'Mangan(II)-sulfat',
        'Mangan(IV)-oxid',
        'Methacrylsäure',
        'Methan',
        'Methansäure',
        'Methylacrylat',
        'Methylamin',
        'Methylbenzol',
        'Methylethylketon',
        'Methylisobutylketon',
        'Methylmethacrylat',
        'Molybdän',
        'Molybdäntrioxid',
        'Monochlorbenzol',
        'Naphtha',
        'Natriumazid',
        'Natriumborhydrid',
        'Natriumchlorat',
        'Natriumdichromat',
        'Natriumfluorid',
        'Natriummetall',
        'Natriumnitrat',
        'Natriumperoxid',
        'Nickel(II)-chlorid',
        'Nickel(II)-nitrat',
        'Nickelcarbonyl',
        'Nitroethan',
        'Nitroglycerin',
        'Nitromethan',
        'Octan',
        'Orthophosphorsäure',
        'Oxalsäure',
        'Ozon',
        'Paraffin',
        'Pentachlorphenol',
        'Pentachlorpropan',
        'Perchlorethylen',
        'Perchlorsäure',
        'Peroxid',
        'Phenanthren',
        'Phenylhydrazin',
        'Phosphin',
        'Phosphonitrilchlorid',
        'Phosphorpentachlorid',
        'Phosphorpentoxid',
        'Phosphortrichlorid',
        'Phthalat',
        'Phthalsäureanhydrid',
        'Piperidin',
        'Platin',
        'Polyvinylchlorid',
        'Propanal',
        'Propansäure',
        'Propylen',
        'Propylenglycol',
        'Pyridin',
        'Pyrogallol',
        'Quecksilber(I)-chlorid',
        'Quecksilber(II)-nitrat',
        'Resorcin',
        'Rhodanwasserstoff',
        'Ruthenium',
        'Salicylsäure',
        'Schwefelchlorid',
        'Schwefelhexafluorid',
        'Schwefelkohlenstoff',
        'Schwefelpentafluorid',
        'Schwefeltrioxid',
        'Selenwasserstoff',
        'Silberchlorid',
        'Silberoxid',
        'Silizium',
        'Siliziumtetrachlorid',
        'Stickstoff',
        'Stickstoffdioxid',
        'Stickstoffmonoxid',
        'Stickstofftrichlorid',
        'Strontium',
        'Styroloxid',
        'Sulfonsäure',
        'Tellur',
        'Terpentin',
        'Tetrachlorkohlenstoff',
        'Tetrachlormethan',
        'Tetrachlorsilan',
        'Tetrahydrofuran',
        'Thallium(I)-sulfat',
        'Thioharnstoff',
        'Thionylchlorid',
        'Titan(IV)-chlorid',
        'Titan(IV)-oxid',
        'Toluoldiisocyanat',
        'Tributylphosphat',
        'Trichloracetaldehyd',
        'Trichlorbenzol',
        'Trichlorethylen',
        'Trichlormethan',
        'Trichlorsilan',
        'Triethylamin',
        'Trimethylamin',
        'Uran',
        'Vanadium(V)-oxid',
        'Vanadiumpentoxid',
        'Vinylacetat',
        'Vinylidenchlorid',
        'Wismut',
        'Wismut(III)-oxid',
        'Wismuttrichlorid',
        'Xylen',
        'Zinkbromid',
        'Zinknitrat',
        'Zinkphosphid',
        'Zinn(II)-chlorid',
        'Zinn(IV)-chlorid',
        'Zirconium(IV)-chlorid',
        'Zirconium(IV)-oxid'
      ]
      
      if (isEditMode.value) {
        loadGefahrstoff()
      }
    })
    </script>
    
    <template>
      <div class="container mx-auto p-6 max-w-4xl">
        <div class="mb-6 flex items-center gap-4">
          <Button variant="ghost" @click="cancel" class="flex items-center gap-2">
            <ArrowLeft class="w-4 h-4" />
            Zurück
          </Button>
          <DangeroursMaterialsLogo class="w-8 h-8" />
          <h1 class="text-2xl font-bold">
            {{ isEditMode ? 'Gefahrstoff bearbeiten' : 'Neuer Gefahrstoff' }}
          </h1>
        </div>
    
        <form @submit.prevent="save" class="space-y-6">
          <!-- Gefahrstoff Information -->
          <Card>
            <CardHeader>
              <div class="flex items-center justify-between">
                <CardTitle>Gefahrstoff Information</CardTitle>
                <div class="flex items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger as-child>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        @click="showSearchDialog = true"
                        class="shrink-0"
                        title="Suche in der Datenbank"
                      >
                        <Search class="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Suche in der Datenbank
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger as-child>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        @click="showImportCsvDialog = true; csvImportFile = null; csvImportError = ''; csvImportSuccess = null"
                        class="shrink-0"
                        title="CSV-Import"
                      >
                        <FileSpreadsheet class="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      CSV-Import (Semikolon-getrennt)
                    </TooltipContent>
                  </Tooltip>
                  <Button 
                    type="button" 
                    variant="outline" 
                    @click="showSDBDialog = true; sdbUploadError = ''"
                    class="flex items-center gap-2"
                  >
                    <Upload class="w-4 h-4" />
                    KI Sicherheitsdatenblatt zu Eintrag
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent class="space-y-4">
              <div>
                <Label for="name">Name *</Label>
                <Combobox
                  id="name"
                  v-model="formData.name"
                  :options="gefahrstoffNames"
                  required
                  placeholder="Name des Gefahrstoffs"
                />
              </div>
              <div>
                <Label for="h_phrases">H-Sätze</Label>
                <MultiSelect
                  id="h_phrases"
                  v-model="formData.h_phrase_ids"
                  :options="hPhraseOptions"
                  placeholder="H-Sätze auswählen..."
                />
              </div>
              <div>
                <Label for="eurh_phrases">EUH-Sätze</Label>
                <MultiSelect
                  id="eurh_phrases"
                  v-model="formData.eurh_phrase_ids"
                  :options="eurhPhraseOptions"
                  placeholder="EUH-Sätze auswählen..."
                />
              </div>
              <div>
                <Label for="p_phrases">P-Sätze</Label>
                <MultiSelect
                  id="p_phrases"
                  v-model="formData.p_phrase_ids"
                  :options="pPhraseOptions"
                  placeholder="P-Sätze auswählen..."
                />
              </div>
              <div>
                <Label for="cas_numbers">CAS-Nummern</Label>
                <AsyncMultiSelect
                  id="cas_numbers"
                  v-model="formData.cas_number_ids"
                  :selected-options="selectedCasNumberOptions"
                  load-options-url="/gefahrstoffe/cas_numbers.json"
                  placeholder="CAS-Nummern auswählen..."
                />
              </div>
              
              <!-- Boolean fields -->
              <div class="grid grid-cols-2 gap-4">
                <div class="flex items-center space-x-2">
                  <input
                    id="hazardous_material"
                    type="checkbox"
                    v-model="formData.hazardous_material"
                    class="h-4 w-4"
                  />
                  <Label for="hazardous_material" class="cursor-pointer">Gefährlicher Stoff</Label>
                </div>
                <div class="flex items-center space-x-2">
                  <input
                    id="gas"
                    type="checkbox"
                    v-model="formData.gas"
                    class="h-4 w-4"
                  />
                  <Label for="gas" class="cursor-pointer">Gas</Label>
                </div>
                <div class="flex items-center space-x-2">
                  <input
                    id="spray"
                    type="checkbox"
                    v-model="formData.spray"
                    class="h-4 w-4"
                  />
                  <Label for="spray" class="cursor-pointer">Spray</Label>
                </div>
                <div class="flex items-center space-x-2">
                  <input
                    id="carcinogenic_material"
                    type="checkbox"
                    v-model="formData.carcinogenic_material"
                    class="h-4 w-4"
                  />
                  <Label for="carcinogenic_material" class="cursor-pointer">Krebserzeugender Stoff</Label>
                </div>
                <div class="flex items-center space-x-2">
                  <input
                    id="active"
                    type="checkbox"
                    v-model="formData.active"
                    class="h-4 w-4"
                  />
                  <Label for="active" class="cursor-pointer">Aktiv</Label>
                </div>
              </div>
              
              <!-- Additional fields -->
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <Label for="un_number">UN-Nr.</Label>
                  <Combobox
                    id="un_number"
                    v-model="unNumberDisplay"
                    :options="unNumberOptions"
                    placeholder="UN-Nummer auswählen oder eingeben..."
                  />
                </div>
                <div>
                  <Label for="eak_number">EAK-Nr.</Label>
                  <Combobox
                    id="eak_number"
                    v-model="eakNumberDisplay"
                    :options="eakNumberOptions"
                    placeholder="EAK-Nummer auswählen oder eingeben..."
                  />
                </div>
                <div>
                  <Label for="water_hazard_class">Wassergefährdungsklasse</Label>
                  <Combobox
                    id="water_hazard_class"
                    v-model="formData.water_hazard_class"
                    :options="waterHazardClassOptions"
                    placeholder="WGK auswählen oder eingeben..."
                  />
                </div>
                <div>
                  <Label for="inventory_class">Lagerklasse</Label>
                  <Combobox
                    id="inventory_class"
                    v-model="lagerklasseDisplay"
                    :options="lagerklasseOptions"
                    placeholder="Lagerklasse auswählen oder eingeben..."
                  />
                </div>
                <div>
                  <Label for="use">Verwendung</Label>
                  <Combobox
                    id="use"
                    v-model="formData.use"
                    :options="useOptions"
                    placeholder="Schneiden, Schweißen,..."
                  />
                </div>
                <div>
                  <Label for="use_start">Einsatz von</Label>
                  <Input id="use_start" type="date" v-model="formData.use_start" />
                </div>
                <div>
                  <Label for="use_end">Einsatz bis</Label>
                  <Input id="use_end" type="date" v-model="formData.use_end" />
                </div>
                <div>
                  <Label for="use_duration_periodicity">Einsatzdauer / Periodizität</Label>
                  <Input
                    id="use_duration_periodicity"
                    v-model="formData.use_duration_periodicity"
                    placeholder="z. B. jährlich, 3 Monate"
                  />
                </div>
                <div>
                  <Label for="number_of_workers">Anzahl Beschäftigte mit Gefahrstoff</Label>
                  <Input
                    id="number_of_workers"
                    type="number"
                    v-model.number="formData.number_of_workers"
                    placeholder="Anzahl"
                  />
                </div>
                <div>
                  <Label for="workspace">Arbeitsplätze mit Gefahrstoff</Label>
                  <Combobox
                    id="workspace"
                    v-model="formData.workspace"
                    :options="workspaceOptions"
                    placeholder="Werkstatt, Schweißraum,..."
                  />
                </div>
                <div>
                  <Label for="yearly_usage">Jahresverbrauch</Label>
                  <div class="flex gap-2">
                    <Input
                      id="yearly_usage"
                      v-model="formData.yearly_usage"
                      type="number"
                      step="any"
                      placeholder="Wert"
                      class="flex-1"
                    />
                    <select
                      id="yearly_usage_unit"
                      v-model="formData.yearly_usage_unit"
                      class="border rounded-lg p-2 text-sm focus:ring focus:ring-blue-200 min-w-[80px]"
                    >
                      <option value="">-</option>
                      <option v-for="unit in yearlyUsageUnitOptions" :key="unit" :value="unit">
                        {{ unit }}
                      </option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label for="inventory_size">Lagermenge</Label>
                  <div class="flex gap-2">
                    <Input
                      id="inventory_size"
                      v-model="formData.inventory_size"
                      type="number"
                      step="any"
                      placeholder="Wert"
                      class="flex-1"
                    />
                    <select
                      id="inventory_size_unit"
                      v-model="formData.inventory_size_unit"
                      class="border rounded-lg p-2 text-sm focus:ring focus:ring-blue-200 min-w-[80px]"
                    >
                      <option value="">-</option>
                      <option v-for="unit in inventorySizeUnitOptions" :key="unit" :value="unit">
                        {{ unit }}
                      </option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label for="location">Betriebsstätte</Label>
                  <Combobox
                    id="location"
                    v-model="formData.location"
                    :options="locationOptions"
                    placeholder="Standort Köln, Standort Berlin,..."
                  />
                </div>
                <div>
                  <Label for="use_location_or_machinery">Einsatzort / Anlage</Label>
                  <Input
                    id="use_location_or_machinery"
                    v-model="formData.use_location_or_machinery"
                    placeholder="Einsatzort oder Anlage"
                  />
                </div>
                <div>
                  <Label for="inventory_space">Lagerabschnitt</Label>
                  <Combobox
                    id="inventory_space"
                    v-model="formData.inventory_space_name"
                    :options="inventorySpaceOptions"
                    placeholder="Lager 1"
                  />
                </div>
                <div>
                  <div class="flex items-center gap-2">
                    <Label for="inventory_space_area">Lagerbereich im Lagerabschnitt</Label>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info class="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent class="whitespace-pre-line text-xs max-w-[400px] break-words">
                        Nach TRGS 510: 7.1 (3) liegt eine Getrenntlagerung vor, wenn verschiedene Lagergüter in verschiedenen Lagerbereichen desselben Lagerabschnittes durch ausreichende Abstände oder durch Barrieren (z. B. durch Wände, Schränke aus nicht brennbarem Material, Produkte aus nicht brennbaren Stoffen der Lagerklasse 12 oder 13) oder durch Lagerung in baulich getrennten Auffangräumen voneinander getrennt werden.
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Combobox
                    id="inventory_space_area"
                    v-model="formData.inventory_space_area_name"
                    :options="inventorySpaceAreaOptions"
                    placeholder="Gefahrstoffschrank 1"
                  />
                </div>
                <div>
                  <Label for="inventory_space_type">Lagerbehältertyp</Label>
                  <Combobox
                    id="inventory_space_type"
                    v-model="formData.inventory_space_type"
                    :options="inventorySpaceTypeOptions"
                    placeholder="Gasflasche, Druckluftflasche,..."
                  />
                </div>
                <div>
                  <Label for="gebinde_number">Gebindeanzahl</Label>
                  <Input
                    id="gebinde_number"
                    v-model="formData.gebinde_number"
                    placeholder="Anzahl"
                  />
                </div>
                <div class="col-span-2">
                  <Label for="extinguishing_agent_suitable">Geeignete Löschmittel</Label>
                  <textarea
                    id="extinguishing_agent_suitable"
                    v-model="formData.extinguishing_agent_suitable"
                    rows="2"
                    class="w-full border rounded-lg p-2 text-sm focus:ring focus:ring-blue-200"
                    placeholder="z. B. Wasser, Schaum, CO2"
                  ></textarea>
                </div>
                <div class="col-span-2">
                  <Label for="extinguishing_agent_unsuitable">Ungeeignete Löschmittel</Label>
                  <textarea
                    id="extinguishing_agent_unsuitable"
                    v-model="formData.extinguishing_agent_unsuitable"
                    rows="2"
                    class="w-full border rounded-lg p-2 text-sm focus:ring focus:ring-blue-200"
                    placeholder="z. B. kein Wasserstrahl"
                  ></textarea>
                </div>
                <div class="col-span-2">
                  <Label for="inventory_conditions">Lagerungsbedingungen</Label>
                  <textarea
                    id="inventory_conditions"
                    v-model="formData.inventory_conditions"
                    rows="2"
                    class="w-full border rounded-lg p-2 text-sm focus:ring focus:ring-blue-200"
                    placeholder="Temperatur, Licht, etc."
                  ></textarea>
                </div>
              </div>
              <div>
                <Label for="comment">Kommentar</Label>
                <textarea
                  id="comment"
                  v-model="formData.comment"
                  rows="4"
                  class="w-full border rounded-lg p-2 text-sm focus:ring focus:ring-blue-200"
                  placeholder="Kommentar"
                ></textarea>
              </div>
            </CardContent>
          </Card>

          <!-- Physikalisch-chemische Eigenschaften -->
          <Card>
            <CardHeader>
              <CardTitle>Physikalisch-chemische Eigenschaften</CardTitle>
            </CardHeader>
            <CardContent class="space-y-4">
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <Label for="physical_state_at_room_temperature">Aggregatzustand bei 20 °C</Label>
                  <Input id="physical_state_at_room_temperature" v-model="formData.physical_state_at_room_temperature" placeholder="fest, flüssig, gasförmig" />
                </div>
                <div>
                  <Label for="form">Form</Label>
                  <Input id="form" v-model="formData.form" placeholder="z. B. Pulver, Granulat" />
                </div>
                <div>
                  <Label for="color">Farbe</Label>
                  <Input id="color" v-model="formData.color" placeholder="Farbe" />
                </div>
                <div>
                  <Label for="odor">Geruch</Label>
                  <Input id="odor" v-model="formData.odor" placeholder="Geruch" />
                </div>
                <div>
                  <Label for="solubility_in_water">Löslichkeit in Wasser</Label>
                  <Input id="solubility_in_water" v-model="formData.solubility_in_water" placeholder="z. B. gut löslich" />
                </div>
                <div>
                  <Label for="flammpunkt">Flammpunkt in °C</Label>
                  <Input
                    id="flammpunkt"
                    v-model="formData.flammpunkt"
                    type="text"
                    placeholder="°C"
                  />
                </div>
                <div>
                  <Label for="melting_point">Schmelzpunkt in °C</Label>
                  <Input id="melting_point" v-model="formData.melting_point" placeholder="" />
                </div>
                <div>
                  <Label for="boiling_point">Siedepunkt in °C</Label>
                  <Input id="boiling_point" v-model="formData.boiling_point" placeholder="" />
                </div>
                <div>
                  <Label for="ignition_temperature">Zündtemperatur in °C</Label>
                  <Input id="ignition_temperature" v-model="formData.ignition_temperature" placeholder="" />
                </div>
                <div>
                  <Label for="autoignition_temperature">Selbstentzündungstemperatur in °C</Label>
                  <Input id="autoignition_temperature" v-model="formData.autoignition_temperature" placeholder="" />
                </div>
                <div>
                  <Label for="decomposition_point">Zersetzungspunkt in °C</Label>
                  <Input id="decomposition_point" v-model="formData.decomposition_point" placeholder="" />
                </div>
                <div>
                  <Label for="density">Dichte in g/cm³</Label>
                  <Input id="density" v-model="formData.density" placeholder="" />
                </div>
                <div>
                  <Label for="viscosity">Viskosität in mm²/s</Label>
                  <Input id="viscosity" v-model="formData.viscosity" placeholder="" />
                </div>
                <div>
                  <Label for="vapor_pressure">Dampfdruck in hPa</Label>
                  <Input id="vapor_pressure" v-model="formData.vapor_pressure" placeholder="" />
                </div>
                <div>
                  <Label for="relative_vapor_density">Relative Dampfdichte (Luft = 1)</Label>
                  <Input id="relative_vapor_density" v-model="formData.relative_vapor_density" placeholder="" />
                </div>
                <div>
                  <Label for="ph_value">pH-Wert</Label>
                  <Input id="ph_value" v-model="formData.ph_value" placeholder="" />
                </div>
                <div>
                  <Label for="lower_explosion_limit_vol_percent">Untere Explosionsgrenze (Vol.-%)</Label>
                  <Input id="lower_explosion_limit_vol_percent" v-model="formData.lower_explosion_limit_vol_percent" placeholder="" />
                </div>
               <div>
                 <Label for="upper_explosion_limit_vol_percent">Obere Explosionsgrenze (Vol.-%)</Label>
                 <Input id="upper_explosion_limit_vol_percent" v-model="formData.upper_explosion_limit_vol_percent" placeholder="" />
               </div>
               <div class="col-span-2">
                 <div class="grid grid-cols-2 gap-4">
                   <div>
                     <Label for="reactivity">Reaktivität</Label>
                     <textarea
                       id="reactivity"
                       v-model="formData.reactivity"
                       rows="3"
                       class="w-full border rounded-lg p-2 text-sm focus:ring focus:ring-blue-200"
                       placeholder="Reaktivität"
                     ></textarea>
                   </div>
                   <div>
                     <Label for="stability">Stabilität</Label>
                     <textarea
                       id="stability"
                       v-model="formData.stability"
                       rows="3"
                       class="w-full border rounded-lg p-2 text-sm focus:ring focus:ring-blue-200"
                       placeholder="Stabilität"
                     ></textarea>
                   </div>
                 </div>
               </div>
              </div>
            </CardContent>
          </Card>
    
          <!-- Datasheet -->
          <Card>
            <CardHeader>
              <CardTitle>Sicherheitsdatenblatt</CardTitle>
            </CardHeader>
            <CardContent class="space-y-4">
              <div>
                <Label for="datasheet_version_date">Versionsdatum</Label>
                <Input
                  id="datasheet_version_date"
                  type="date"
                  v-model="formData.datasheet.version_date"
                />
              </div>
              <div>
                <Label for="datasheet_next_check">Nächste Prüfung</Label>
                <Input
                  id="datasheet_next_check"
                  type="date"
                  v-model="formData.datasheet.next_check"
                />
              </div>
              <div>
                <Label for="name_responsible">Name Verantwortlicher</Label>
                <Combobox
                  id="name_responsible"
                  v-model="formData.datasheet.name_responsible"
                  :options="nameResponsibleOptions"
                  placeholder="Name des Verantwortlichen"
                />
              </div>
              <div>
                <Label for="email_responsible">E-Mail Verantwortlicher</Label>
                <Combobox
                  id="email_responsible"
                  v-model="formData.datasheet.email_responsible"
                  :options="emailResponsibleOptions"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label for="email_department">E-Mail Abteilung</Label>
                <Combobox
                  id="email_department"
                  v-model="formData.datasheet.email_department"
                  :options="emailDepartmentOptions"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label for="datasheet_manufacturer">Hersteller</Label>
                <Input
                  id="datasheet_manufacturer"
                  v-model="formData.datasheet.manufacturer"
                  placeholder="Name des Herstellers"
                />
              </div>
              <div>
                <Label for="datasheet_last_check">Letzte Prüfung</Label>
                <Input
                  id="datasheet_last_check"
                  type="date"
                  v-model="formData.datasheet.last_check"
                />
              </div>
              <template v-if="isHoeppner">
                <div>
                  <Label for="datasheet_last_check_hmc">Letzte Prüfung HMC</Label>
                  <Input
                    id="datasheet_last_check_hmc"
                    type="date"
                    v-model="formData.datasheet.last_check_hmc"
                  />
                </div>
                <div>
                  <Label for="datasheet_last_check_hmc_comment">Prüfung HMC Kommentar</Label>
                  <textarea
                    id="datasheet_last_check_hmc_comment"
                    v-model="formData.datasheet.last_check_hmc_comment"
                    rows="3"
                    class="w-full border rounded-lg p-2 text-sm focus:ring focus:ring-blue-200"
                    placeholder="Kommentar zur HMC Prüfung"
                  ></textarea>
                </div>
              </template>
              <div>
                <Label for="datasheet_file">Sicherheitsdatenblatt (Datei)</Label>
                <Input
                  id="datasheet_file"
                  type="file"
                  @change="onFileChange($event, 'datasheet')"
                  accept=".pdf,.doc,.docx"
                />
                <p v-if="formData.datasheet.existing_datasheet_url" class="text-sm text-gray-600 mt-2">
                  Aktuelles Dokument: 
                  <a :href="formData.datasheet.existing_datasheet_url" target="_blank" class="text-blue-600 underline">
                    Anzeigen
                  </a>
                </p>
                <p v-if="formData.datasheet.datasheet_file" class="text-sm text-green-600 mt-2">
                  Neue Datei ausgewählt: {{ formData.datasheet.datasheet_file.name }}
                </p>
              </div>
            </CardContent>
          </Card>
    
          <!-- GBU -->
          <Card>
            <CardHeader>
              <CardTitle>GBU (Gefahrstoffbeauftragter Unterweisung)</CardTitle>
            </CardHeader>
            <CardContent class="space-y-4">
              <div>
                <Label for="gbu_version_date">Versionsdatum</Label>
                <Input
                  id="gbu_version_date"
                  type="date"
                  v-model="formData.gbu.version_date"
                />
              </div>
              <div>
                <Label for="gbu_next_check">Nächste Prüfung</Label>
                <Input
                  id="gbu_next_check"
                  type="date"
                  v-model="formData.gbu.next_check"
                />
              </div>
              <div>
                <Label for="gbu_name_responsible">Name Verantwortlicher</Label>
                <Combobox
                  id="gbu_name_responsible"
                  v-model="formData.gbu.name_responsible"
                  :options="nameResponsibleOptions"
                  placeholder="Name des Verantwortlichen"
                />
              </div>
              <div>
                <Label for="gbu_email_responsible">E-Mail Verantwortlicher</Label>
                <Combobox
                  id="gbu_email_responsible"
                  v-model="formData.gbu.email_responsible"
                  :options="emailResponsibleOptions"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label for="gbu_email_department">E-Mail Abteilung</Label>
                <Combobox
                  id="gbu_email_department"
                  v-model="formData.gbu.email_department"
                  :options="emailDepartmentOptions"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label for="gbu_identifier">Kennung</Label>
                <Input
                  id="gbu_identifier"
                  v-model="formData.gbu.identifier"
                  placeholder="Kennung/Identifier"
                />
              </div>
              <div>
                <Label for="gbu_comment">Kommentar</Label>
                <textarea
                  id="gbu_comment"
                  v-model="formData.gbu.comment"
                  rows="3"
                  class="w-full border rounded-lg p-2 text-sm focus:ring focus:ring-blue-200"
                  placeholder="Kommentar"
                ></textarea>
              </div>
              <div>
                <Label for="gbu_file">GBU (Datei)</Label>
                <Input
                  id="gbu_file"
                  type="file"
                  @change="onFileChange($event, 'gbu')"
                  accept=".pdf,.doc,.docx"
                />
                <p v-if="formData.gbu.existing_gbu_url" class="text-sm text-gray-600 mt-2">
                  Aktuelles Dokument: 
                  <a :href="formData.gbu.existing_gbu_url" target="_blank" class="text-blue-600 underline">
                    Anzeigen
                  </a>
                </p>
                <p v-if="formData.gbu.gbu_file" class="text-sm text-green-600 mt-2">
                  Neue Datei ausgewählt: {{ formData.gbu.gbu_file.name }}
                </p>
              </div>
            </CardContent>
          </Card>
    
          <!-- Betriebsanweisung -->
          <Card>
            <CardHeader>
              <CardTitle>Betriebsanweisung</CardTitle>
            </CardHeader>
            <CardContent class="space-y-4">
              <div>
                <Label for="betriebsanweisung_version_date">Versionsdatum</Label>
                <Input
                  id="betriebsanweisung_version_date"
                  type="date"
                  v-model="formData.betriebsanweisung.version_date"
                />
              </div>
              <div>
                <Label for="betriebsanweisung_next_check">Nächste Prüfung</Label>
                <Input
                  id="betriebsanweisung_next_check"
                  type="date"
                  v-model="formData.betriebsanweisung.next_check"
                />
              </div>
              <div>
                <Label for="betriebsanweisung_name_responsible">Name Verantwortlicher</Label>
                <Combobox
                  id="betriebsanweisung_name_responsible"
                  v-model="formData.betriebsanweisung.name_responsible"
                  :options="nameResponsibleOptions"
                  placeholder="Name des Verantwortlichen"
                />
              </div>
              <div>
                <Label for="betriebsanweisung_email_responsible">E-Mail Verantwortlicher</Label>
                <Combobox
                  id="betriebsanweisung_email_responsible"
                  v-model="formData.betriebsanweisung.email_responsible"
                  :options="emailResponsibleOptions"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label for="betriebsanweisung_email_department">E-Mail Abteilung</Label>
                <Combobox
                  id="betriebsanweisung_email_department"
                  v-model="formData.betriebsanweisung.email_department"
                  :options="emailDepartmentOptions"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label for="betriebsanweisung_identifier">Kennung</Label>
                <Input
                  id="betriebsanweisung_identifier"
                  v-model="formData.betriebsanweisung.identifier"
                  placeholder="Kennung/Identifier"
                />
              </div>
              <div>
                <Label for="betriebsanweisung_comment">Kommentar</Label>
                <textarea
                  id="betriebsanweisung_comment"
                  v-model="formData.betriebsanweisung.comment"
                  rows="3"
                  class="w-full border rounded-lg p-2 text-sm focus:ring focus:ring-blue-200"
                  placeholder="Kommentar"
                ></textarea>
              </div>
              <div>
                <Label for="betriebsanweisung_file">Betriebsanweisung (Datei)</Label>
                <Input
                  id="betriebsanweisung_file"
                  type="file"
                  @change="onFileChange($event, 'betriebsanweisung')"
                  accept=".pdf,.doc,.docx"
                />
                <p v-if="formData.betriebsanweisung.existing_betriebsanweisung_url" class="text-sm text-gray-600 mt-2">
                  Aktuelles Dokument: 
                  <a :href="formData.betriebsanweisung.existing_betriebsanweisung_url" target="_blank" class="text-blue-600 underline">
                    Anzeigen
                  </a>
                </p>
                <p v-if="formData.betriebsanweisung.betriebsanweisung_file" class="text-sm text-green-600 mt-2">
                  Neue Datei ausgewählt: {{ formData.betriebsanweisung.betriebsanweisung_file.name }}
                </p>
              </div>
            </CardContent>
          </Card>
          
          <!-- Substitutiontest -->
          <Card>
            <CardHeader>
              <CardTitle>Substitutionsprüfung</CardTitle>
            </CardHeader>
            <CardContent class="space-y-4">
              <div>
                <Label for="substitutiontest_version_date">Versionsdatum</Label>
                <Input
                  id="substitutiontest_version_date"
                  type="date"
                  v-model="formData.substitutiontest.version_date"
                />
              </div>
              <div>
                <Label for="substitutiontest_next_check">Nächste Prüfung</Label>
                <Input
                  id="substitutiontest_next_check"
                  type="date"
                  v-model="formData.substitutiontest.next_check"
                />
              </div>
              <div>
                <Label for="substitutiontest_name_responsible">Name Verantwortlicher</Label>
                <Combobox
                  id="substitutiontest_name_responsible"
                  v-model="formData.substitutiontest.name_responsible"
                  :options="nameResponsibleOptions"
                  placeholder="Name des Verantwortlichen"
                />
              </div>
              <div>
                <Label for="substitutiontest_email_responsible">E-Mail Verantwortlicher</Label>
                <Combobox
                  id="substitutiontest_email_responsible"
                  v-model="formData.substitutiontest.email_responsible"
                  :options="emailResponsibleOptions"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label for="substitutiontest_email_department">E-Mail Abteilung</Label>
                <Combobox
                  id="substitutiontest_email_department"
                  v-model="formData.substitutiontest.email_department"
                  :options="emailDepartmentOptions"
                  placeholder="abteilung@example.com"
                />
              </div>
              <div>
                <Label for="substitutiontest_result">Ergebnis (Unterweisung)</Label>
                <textarea
                  id="substitutiontest_result"
                  v-model="formData.substitutiontest.result"
                  placeholder="Ergebnis der Substitutionsprüfung (Unterweisung)"
                  rows="4"
                  class="w-full border rounded-lg p-2 text-sm focus:ring focus:ring-blue-200"
                />
              </div>
            </CardContent>
          </Card>
    
          <!-- Actions -->
          <div class="flex justify-end gap-4">
            <Button type="button" variant="outline" @click="cancel" :disabled="isSaving">
              Abbrechen
            </Button>
            <Button type="submit" :disabled="isSaving || !formData.name">
              {{ isSaving ? 'Speichern...' : 'Speichern' }}
            </Button>
          </div>
        </form>
        
        <!-- SDB Upload Dialog -->
        <Dialog v-model:open="showSDBDialog">
          <DialogContent>
            <DialogHeader>
              <DialogTitle>KI Sicherheitsdatenblatt zu Eintrag</DialogTitle>
              <DialogDescription>
                Laden Sie ein Sicherheitsdatenblatt als PDF hoch, um das Formular automatisch vorauszufüllen.
              </DialogDescription>
            </DialogHeader>
            <div class="space-y-4">
              <div>
                <Label for="sdb_file">PDF-Datei</Label>
                <Input
                  id="sdb_file"
                  type="file"
                  accept=".pdf"
                  @change="onSDBFileChange"
                  :disabled="isUploadingSDB"
                />
                <p v-if="sdbFile" class="text-sm text-gray-600 mt-2">
                  Ausgewählte Datei: {{ sdbFile.name }}
                </p>
              </div>
              <div>
                <Label>Verhalten beim Vorausfüllen</Label>
                <RadioGroup v-model="sdbMergeMode" class="grid gap-2 pt-2">
                  <div class="flex items-center space-x-2">
                    <RadioGroupItem id="sdb_add_only" value="add_only" />
                    <Label for="sdb_add_only" class="font-normal cursor-pointer">Infos nur hinzufügen</Label>
                  </div>
                  <div class="flex items-center space-x-2">
                    <RadioGroupItem id="sdb_overwrite" value="overwrite" />
                    <Label for="sdb_overwrite" class="font-normal cursor-pointer">Infos überschreiben</Label>
                  </div>
                </RadioGroup>
                <p class="text-sm text-muted-foreground mt-1">
                  {{ sdbMergeMode === 'add_only' ? 'Bereits ausgefüllte Felder bleiben unverändert.' : 'Alle Felder werden mit den Daten aus dem Sicherheitsdatenblatt überschrieben.' }}
                </p>
              </div>
              <p v-if="sdbUploadError" class="text-sm text-destructive font-medium">
                {{ sdbUploadError }}
              </p>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                @click="showSDBDialog = false; sdbFile = null; sdbUploadError = ''"
                :disabled="isUploadingSDB"
              >
                Abbrechen
              </Button>
              <Button 
                type="button" 
                @click="uploadSDB"
                :disabled="!sdbFile || isUploadingSDB"
              >
                <Loader2 v-if="isUploadingSDB" class="mr-2 h-4 w-4 animate-spin" />
                {{ isUploadingSDB ? 'Verarbeitung...' : 'Hochladen' }}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <!-- Search & Prefill Dialog -->
        <Dialog v-model:open="showSearchDialog">
          <DialogContent class="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Suche nach Gefahrstoff</DialogTitle>
              <DialogDescription>
                Suchen Sie nach dem Namen oder der CAS-Nummer von Inhaltsstoffendes Gefahrstoffs. Wählen Sie einen Eintrag und klicken Sie auf „Vorausfüllen“, um das Formular zu übernehmen.
              </DialogDescription>
            </DialogHeader>
            <div class="space-y-4">
              <div>
                <Label for="search_gefahrstoff">Suchen</Label>
                <Input
                  id="search_gefahrstoff"
                  v-model="searchQuery"
                  type="text"
                  placeholder="Name oder CAS-Nummer eingeben..."
                  autofocus
                  @input="onSearchInput"
                />
              </div>
              <div v-if="isSearching" class="flex items-center justify-center py-6 text-gray-500">
                <Loader2 class="w-6 h-6 animate-spin mr-2" />
                Suche...
              </div>
              <div
                v-else-if="searchResults.length > 0"
                class="border rounded-md max-h-60 overflow-auto divide-y"
              >
                <button
                  v-for="item in searchResults"
                  :key="item.id"
                  type="button"
                  class="w-full text-left px-3 py-2 hover:bg-gray-100 transition-colors"
                  :class="{ 'bg-blue-50 ring-1 ring-blue-200': selectedSearchId === item.id }"
                  @click="selectedSearchId = item.id"
                >
                  <div class="font-medium text-gray-900">{{ item.name }}</div>
                  <div v-if="item.cas_numbers?.length" class="text-sm text-gray-600">
                    CAS: {{ item.cas_numbers.map((c: any) => c.cas_number).join(', ') }}
                  </div>
                </button>
              </div>
              <p v-else-if="searchQuery.trim() && !isSearching" class="text-sm text-gray-500">
                Keine Einträge gefunden. Geben Sie Name oder CAS-Nummer ein.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" @click="showSearchDialog = false">
                Abbrechen
              </Button>
              <Button
                type="button"
                :disabled="selectedSearchId == null"
                @click="applyPrefill"
              >
                Vorausfüllen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <!-- CSV Import Dialog -->
        <Dialog v-model:open="showImportCsvDialog">
          <DialogContent class="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>CSV-Import</DialogTitle>
              <DialogDescription>
                Laden Sie eine Semikolon-getrennte (semicolon-separated) CSV-Datei hoch. Die Einträge werden in Gefahrstoff, Sicherheitsdatenblatt, Inventar, Betriebsanweisung, GBU und Substitutionsprüfung übernommen.
              </DialogDescription>
            </DialogHeader>
            <div class="space-y-4">
              <div>
                <Label for="csv_file">CSV-Datei</Label>
                <Input
                  id="csv_file"
                  type="file"
                  accept=".csv,text/csv"
                  class="mt-1"
                  @change="onCsvFileChange"
                />
                <p v-if="csvImportFile" class="text-sm text-gray-500 mt-1">{{ csvImportFile.name }}</p>
                <p v-if="csvImportError" class="text-sm text-red-600 mt-1">{{ csvImportError }}</p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" @click="closeImportCsvDialog">
                Abbrechen
              </Button>
              <Button
                type="button"
                :disabled="!csvImportFile || isImportingCsv"
                @click="doImportCsv"
              >
                <Loader2 v-if="isImportingCsv" class="mr-2 h-4 w-4 animate-spin" />
                {{ isImportingCsv ? 'Import läuft...' : 'Importieren' }}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <!-- CSV Import Success Dialog -->
        <Dialog v-model:open="showImportSuccessDialog">
          <DialogContent class="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>CSV-Import abgeschlossen</DialogTitle>
            </DialogHeader>
            <div class="space-y-4" v-if="csvImportSuccess">
              <p class="text-sm text-gray-700">
                {{ csvImportSuccess.created }} erstellt, {{ csvImportSuccess.updated }} aktualisiert.
                <span v-if="csvImportSuccess.errors?.length" class="text-amber-600">{{ csvImportSuccess.errors.length }} Zeilen mit Fehlern.</span>
              </p>
              <div v-if="csvImportSuccess.errors?.length" class="rounded-md border border-amber-200 bg-amber-50/50 p-3">
                <p class="mb-2 text-xs font-medium text-amber-800">Fehlerdetails (Zeile und Grund):</p>
                <ul class="max-h-60 list-inside list-disc space-y-1 overflow-y-auto text-xs text-amber-900">
                  <li v-for="(err, i) in csvImportSuccess.errors" :key="i" class="break-words">{{ err }}</li>
                </ul>
              </div>
              <Button
                type="button"
                @click="goToGefahrstoffeListAfterImport"
              >
                Zu den Daten
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </template>
    
    